import {
    alignment,
    bold,
    bold1,
    bold2,
    checkSingolo,
    checkSingoloInterno,
    doubleFormat,
    empty,
    fontSize,
    fontSize1,
    fontSize2,
    format,
    fxTabella,
    fxTabellaEsternaBordo,
    Indent,
    intestazioneLogoConf,
    italics,
    italics1,
    italics2,
    lineaVuota,
    newPage,
    parametriNascosti,
    tabellaCheck,
} from './print.utils';
import { IRevisione } from '../core/models/interfaces/revisione.interface';
import { Content, TDocumentDefinitions, TFontDictionary } from 'pdfmake/interfaces';
import { format as formatDateFns } from 'date-fns';
import { Utilities } from '@core/helpers/utilities';
import { TipoDocumento } from '@core/helpers/constants';
import * as _ from 'lodash';

export class VerbaleRevisionePDFGenerator {
    private revisione_orig: IRevisione;
    private readonly revisione: any;
    private isBozza: boolean;
    private tipoDocumento: string;

    constructor(revisione: IRevisione, tipoDocumento: string, private isSezioneIValid: boolean = true) {
        this.revisione_orig = revisione;
        this.tipoDocumento = tipoDocumento;
        this.revisione = {};
        this.flattenObject(revisione);
    }

    public getFonts(): TFontDictionary {
        return {
            TimesNewRoman: {
                normal: 'times.ttf',
                bold: 'timesbd.ttf',
                italics: 'timesi.ttf',
                bolditalics: 'timesbi.ttf',
            },
        };
    }

    private flattenObject(obj): any {
        const flattened = {};

        Object.keys(obj).forEach((key) => {
            const value = obj[key];

            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                this.flattenObject(value);
            } else {
                if (this.revisione[key] === undefined) {
                    this.revisione[key] = value;
                }
            }
        });

        return flattened;
    }

    generate(): TDocumentDefinitions {
        const pdfDocDefinition: TDocumentDefinitions = {
            pageMargins: [45, 60, 45, 60],
            info: {
                title: 'Verbale Revisione',
                author: 'NODE SOC. COOP.',
                subject: '',
                keywords: '',
            },

            content: [this.generateBody()],

            defaultStyle: {
                fontSize: 10,
                font: 'Roboto',
                alignment: 'left',
                bold: false,
                italics: false,
            },
        };

        if (this.isBozza) {
            pdfDocDefinition['watermark'] = {
                text: 'bozza',
                color: '#ccc',
                opacity: 0.5,
                bold: true,
                italics: false,
            };
        }

        return pdfDocDefinition;
    }

    private getValueFromKey(key: string, root?: any): string {
        if (!root) root = this.revisione;

        // let result;
        // for (const k in root) {
        //     if (root.hasOwnProperty(key)) {
        //         return root[k];
        //     } else if (typeof root[k] == 'object') {
        //         result = this.getValueFromKey(key, root[k]);
        //     }
        // }

        return root[key];
    }

    private getArrayFromKey(key: string, root?: any): any[] {
        if (!root) root = this.revisione;

        // let result;
        // for (const key in root) {
        //     if (this.revisione.hasOwnProperty(key)) {
        //         return root[key];
        //     } else if (typeof root[key] == 'object' && !(root[key] instanceof Array)) {
        //         result = this.getValueFromKey(key, root[root]);
        //     } else if (typeof root[key] == 'object' && root[key] instanceof Array) {
        //         return root[key];
        //     }
        // }
        return root[key];
    }

    private getValueFromKeyWithWhitespace(key: string, root?: any): string {
        const value = this.getValueFromKey(key, root);
        return value ? value + ' ' : ' ';
    }

    private generateBody(): Content {
        if (this.tipoDocumento === TipoDocumento.RILEVAZIONE) {
            this.isBozza = this.calcolaBozzaSezioneI();
            return this.generateRilevazione();
        } else if (this.tipoDocumento === TipoDocumento.RILEVAZIONE_ESTRATTO) {
            return this.generateRilevazioneEstratto();
        } else if (this.tipoDocumento === TipoDocumento.ACCERTAMENTO) {
            return this.generateAccertamento();
        } else if (this.tipoDocumento === TipoDocumento.ACCERTAMENTO_ESTRATTO) {
            return this.generateAccertamentoEstratto();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_AGRICOLE) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_ALTRE) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_CONSUMO) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_DETTAGLIANTI) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_EDILIZIE) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_FIDI) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_PRODUZIONE_LAVORO) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_PESCA) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_SOCIALI) {
            return this.generateSchedaSettore();
        } else if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_TRASPORTO) {
            return this.generateSchedaSettore();
        }
    }

    /* Equivalente NCR di calcolaStampabileSezioneI (VBS 11752-11817): mostra il watermark "bozza"
    a meno che la Sezione I sia valida, my_StatoVerbale sia "1" e sia presente il consenso informativa.
    "ceFirma" (firma InfoCamere) non ha equivalente in NCR: l'unica eccezione mantenuta è la rilevazione
    già inviata al server, sempre trattata come definitiva anche se lo stato corrente presenta errori. */
    private calcolaBozzaSezioneI(): boolean {
        const rilevazioneSentToServer = this.getValueFromKey('my_Ck_RilevazioneSentToServer');
        if (rilevazioneSentToServer === '1') {
            return false;
        }

        const statoVerbale = this.getValueFromKey('my_StatoVerbale');
        const ckPossiedeInformativa = this.getValueFromKey('my_Ck_PossiedeInformativa');

        return !(this.isSezioneIValid && statoVerbale === '1' && ckPossiedeInformativa !== '0');
    }

    private formatDate(value): string {
        if (!value || !value.trim()) return value;
        const date = new Date(value.trim());
        console.log(date);
        try {
            return formatDateFns(date, 'dd/MM/yyyy');
        } catch (e) {
            return '';
        }
    }

    private generateAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        //rows.push([intestazionePagine()]);
        // SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II -
        // ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO ---
        // SEZIONE II - ACCERTAMENTO ---
        //rows.push([newPage()]);

        //DATI IDENTIFICATIVI
        rows.push([intestazioneLogoConf()]);
        ////rows.push([intestazioneTitoloMinisteriale()])

        rows.push([format('Confederazione Cooperative Italiane', true, 'center', 12)]);
        rows.push([lineaVuota()]);
        rows.push([format('Associazione nazionale di rappresentanza, assistenza, tutela e revisione del movimento cooperativo', true, 'center', 10)]);
        rows.push([format('riconosciuta giuridicamente con D.M. 12 Aprile 1948 ai sensi del D.L.C.P.S. 14 Dicembre 1947, n. 1577', true, 'center', 10)]);
        rows.push([format('SEDE CENTRALE: Via Torino, 146 - 00184 Roma', true, 'center', 10)]);
        rows.push([lineaVuota()]);
        rows.push([format('VIGILANZA SUGLI ENTI COOPERATIVI AI SENSI DEL D.Lgs. 2 AGOSTO 2002 N.220', true, 'center', 11)]);
        rows.push([lineaVuota()]);
        rows.push([format('VERBALE DI REVISIONE', true, 'center', 11)]);
        rows.push([format('(conforme al modello approvato con D.M. 12/06/2017 del Ministero delle Imprese e del Made in Italy)', false, 'center', fontSize, italics)]);
        rows.push([lineaVuota()]);
        rows.push([format('SEZIONE II - Accertamento', true, 'center', 10, true)]);
        rows.push([
            doubleFormat(
                'Biennio di revisione: ',
                true,
                false,
                10,
                this.getValueFromKeyWithWhitespace('my_Anno1II') + ' / ' + this.getValueFromKeyWithWhitespace('my_Anno2II'),
                false,
                false,
                10
            ),
        ]);
        rows.push([
            doubleFormat(
                'Anno di revisione ',
                true,
                false,
                10,
                '(se soggetta a revisione annuale) ' + this.getValueFromKeyWithWhitespace('my_AnnoRevisionePerAnnualiII'),
                false,
                false,
                10
            ),
        ]);
        rows.push([this.fxDatiIdentificativiAccertamento()]);
        rows.push([
            '64 - Il sottoscritto revisore ' +
                this.getValueFromKeyWithWhitespace('my_RevisoreII') +
                ' alla presenza del Sig. ' +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopNomeII') +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopCognomeII') +
                " rappresentante dell'ente cooperativo in qualità di: " +
                this.getValueFromKeyWithWhitespace('my_QualitaRappresII') +
                ', indentificato con documento ' +
                this.getValueFromKeyWithWhitespace('my_DocumRappresII') +
                ' ,(in caso di delega allegare la stessa e copia del documento del legale rappresentante), assistito da ' +
                this.getValueFromKeyWithWhitespace('my_AssistRappresII') +
                " presso (luogo dove si svolge l'accertamento, specificare se sede legale, ufficio del revisore od altro luogo concordato) " +
                this.getLuogoAccertamento() +
                ' effettua l’accertamento al fine di verificare se sono state sanate le seguenti irregolarità, già oggetto di diffida, emerse nel corso della revisione: \n',
        ]);
        rows.push([this.tabellaIrrSanabili()]);
        // rows.push([  this.getValueFromKeyWithWhitespace("my_DescIrregSanabili") ])
        rows.push([lineaVuota()]);
        rows.push(['65 - Risultanze degli accertamenti effettuati:']);
        rows.push([this.tabellaRisultanzeAccertamentiEff()]);
        rows.push([format('EVENTUALI ALTRE NOTIZIE:', true, 'center', 11)]);
        rows.push([
            tabellaCheck(
                '66 - Il revisore è venuto a conoscenza di nuovi fatti, indipendenti dall’oggetto della diffida, che possano comportare la richiesta di un provvedimento?',
                this.getValueFromKeyWithWhitespace('my_CkAltriFattiProvv')
            ),
        ]);
        rows.push(['Specificare: ' + this.getValueFromKeyWithWhitespace('my_EventAltreNotiz')]);
        rows.push([format('CONCLUSIONI', true, 'center', 11)]);
        rows.push([
            tabellaCheck(
                "67 - Il rappresentante dell'ente dichiara che è stato portato a conoscenza dei soci, con le modalità previste dall’art. 17 del D.Lgs. 220/02, l’estratto del verbale della più recente revisione o ispezione?",
                this.getValueFromKeyWithWhitespace('my_CkConoscSoci')
            ),
        ]);
        rows.push(['- in caso negativo specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecConoscSoci')]);
        rows.push(['68 - Giudizio Conclusivo del Revisore ed eventuali altre notizie: \n' + this.getValueFromKeyWithWhitespace('my_GiudizioConclusivoII')]);
        rows.push(['69 - Eventuali dichiarazioni del legale rappresentante: \n' + this.getValueFromKeyWithWhitespace('my_OssControded')]);
        rows.push([lineaVuota()]);
        rows.push(['Il revisore, considerate le risultanze di cui ai punti precedenti:']);
        rows.push([this.tabellaOsservazioniControdeduzioniAccertamento()]);
        rows.push([
            'La presente sezione del verbale debitamente controfirmata, è redatta in n. ' +
                this.getValueFromKeyWithWhitespace('my_NOriginaliII') +
                " originali, uno dei quali viene consegnato al rappresentante dell'ente revisionato o, in caso di rifiuto di sottoscrizione, notificato a mezzo posta certificata/raccomandata.",
        ]);
        rows.push([
            "Al rappresentante dell’ente viene ricordato l’obbligo di portare a conoscenza dei soci i contenuti del presente verbale con le modalità di cui all’art. 17 del Decreto legislativo n. 220/2002. Lo stesso viene altresì reso edotto della possibilità di presentare, entro 15 giorni dalla data odierna, eventuali ulteriori osservazioni o controdeduzioni all’Ufficio che ha disposto la revisione. Si precisa che la suddetta documentazione dovrà essere prodotta agli Uffici della Pubblica Amministrazione esclusivamente per via informatica a mezzo posta certificata. \n Ai sensi del combinato disposto del D.Lgs. n. 220/02 e della Legge n. 221/2012, si precisa che gli uffici amministrativi preposti alla vigilanza cooperativa assolvono i compiti loro affidati dalla legge esclusivamente nell'interesse pubblico e che la suddetta vigilanza esplica effetti ed è diretta nei soli confronti delle pubbliche amministrazioni ai fini della legittimazione a beneficiare delle agevolazioni fiscali, previdenziali e di altra natura, nonché per l’adozione dei provvedimenti previsti dall’articolo 12 del citato D.Lgs. n. 220/02.",
        ]);
        rows.push([lineaVuota()]);
        rows.push(['Luogo, ' + this.getValueFromKeyWithWhitespace('my_LuogoCompilazII') + '  data ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilazII'))]);
        rows.push([lineaVuota()]);
        rows.push([this.tabellaOsservazioniControdeduzioniFirmeAccertamento()]);

        this.stampaSchedeSettore(rows, cols);

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '2') {
            //SCHEDA GESTIONE COMMISSARIALE

            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI GESTIONE COMMISSARIALE', true, 'center', fontSize, italics)]);
            rows.push([
                tabellaCheck('1 - Ci sono reali e concrete prospettive per il perseguimento dello scopo sociale?', this.getValueFromKeyWithWhitespace('my_ckProspettScopoSocII')),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descProspettScopoSocII'))]);
            rows.push([
                tabellaCheck(
                    "2 - Su quali risorse materiali e finanziarie può contare l'ente per la prosecuzione dell'attività sociale?",
                    this.getValueFromKeyWithWhitespace('my_ckRisFinProsecuzioneAttSocII')
                ),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descRisFinProsecuzioneAttSocII'))]);
            rows.push(["3 - Quali iniziative in concreto il Commissario Governativo, una volta nominato, dovrebbe porre in essere per regolarizzare l'ente?"]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descInizCommGovernativoII'))]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '3') {
            //SCHEDA PROPOSTA DI SCIOGLIMENTO
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SCIOGLIMENTO', true, 'center', fontSize, italics)]);
            rows.push(["1 - Quali delle fattispecie di cui all'art. 2545 septiesdecies c.c. si ritengono ricorrenti per la cooperativa?"]);
            rows.push([this.tabellaCoopPropostaScioglimentoAccertamento()]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descArt2545RicII'))]);
            rows.push([
                "2 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEsII') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari?', this.getValueFromKeyWithWhitespace('my_ckBeniImmobiliII')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione?', this.getValueFromKeyWithWhitespace('my_ckBeniMobiliRegistrII')))]);
            rows.push([
                Indent(
                    4,
                    tabellaCheck("- Un valore dell'Attivo che renda necessaria la nomina di un liquidatore?", this.getValueFromKeyWithWhitespace('my_ckValAttivoNecessLiquidII'))
                ),
            ]);
            rows.push([Indent(4, 'Specificare: \n' + this.getValueFromKeyWithWhitespace('my_descRisultUltBilAppII'))]);
            rows.push([
                tabellaCheck(
                    "3 - Ricorrono entrambi i presupposti di cui all'art. 223 septiesdecies Disp. Att. c.c.?",
                    this.getValueFromKeyWithWhitespace('my_ckPresuppostiArt223II')
                ),
            ]);
            rows.push([Indent(4, 'Mancato deposito dei bilanci di esercizio da oltre cinque anniII')]);
            rows.push([Indent(4, 'Assenza di:II')]);
            rows.push([Indent(6, '- Beni immobiliariII')]);
            rows.push([Indent(6, '- Beni mobiliari soggetti a registrazioneII')]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '4' || this.getValueFromKey('my_CkProvvedimendoDiII') == '9') {
            //SCHEDA PROPOSTA SOSTITUZIONE LIQUIDATORE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SOSTITUZIONE DEL LIQUIDATORE O DI CANCELLAZIONE DAL REGISTRO DELLE IMPRESE', true, 'center', fontSize, italics)]);
            rows.push([
                '1 - Descrivere la natura del ritardo nello svolgimento della liquidazione e le eventuali motivazioni addotte dal liquidatore \n ' +
                    this.getValueFromKeyWithWhitespace('my_descNatRitardoLiquidII'),
            ]);
            rows.push([lineaVuota()]);
            rows.push(['2 - Descrivere in sintesi le eventuali ulteriori motivazioni della proposta \n' + this.getValueFromKeyWithWhitespace('my_descUlterMotivazII')]);
            rows.push([lineaVuota()]);
            rows.push([tabellaCheck('3 - Sono stati depositati i bilanci relativi agli ultimi cinque esercizi?', this.getValueFromKeyWithWhitespace('my_ckDepBil5EserciziII'))]);
            rows.push([
                "4 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEsII') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari', this.getValueFromKeyWithWhitespace('my_ckBeniImmobII')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione', this.getValueFromKeyWithWhitespace('my_ckBeniMobRegistrII')))]);
            rows.push([this.getValueFromKeyWithWhitespace('my_descRisultanzeBilII')]);
            rows.push(['5 - Il liquidatore da sostituire è stato a suo tempo nominato:']);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAssSociII')) + " dall'Assemblea dei soci")]);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAutGiudII')) + " dall'Autorità Giudiziaria")]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '5') {
            //SCHEDA LIQUIDAZIONE COATTA AMMINISTRATIVA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI LIQUIDAZIONE COATTA AMMINISTRATIVA', true, 'center', fontSize, italics)]);
            rows.push(['1 – Descrivere in sintesi le motivazioni della proposta \n ' + this.getValueFromKeyWithWhitespace('my_descMotivoPropostaII')]);
            rows.push([lineaVuota()]);
            rows.push([this.tabellaCoopLiquidazioneCoattaInterna()]);
            rows.push(['In caso affermativo specificare: \n' + this.getValueFromKeyWithWhitespace('my_descAzAvviateII')]);
            rows.push(['3 – Riepilogare le vertenze giudiziarie di cui al punto 51) \n' + this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudizII')]);
            rows.push([
                tabellaCheck(
                    "4 – E' stata allegata una situazione patrimoniale aggiornata, conforme allo schema di cui all’art. 2424 c.c., sottoscritta dal legale rappresentante e dall’Organo di Controllo se presente?",
                    this.getValueFromKeyWithWhitespace('my_ckSituazPatrAggiornII')
                ),
            ]);
            rows.push(['In caso di risposta negativa specificarne i motivi: \n' + this.getValueFromKeyWithWhitespace('my_descSituazPatrAggiornII')]);
            rows.push([
                tabellaCheck(
                    '5 - Risultano mancati pagamenti di mensilità stipendiali od omessi versamenti di contributi previdenziali? ',
                    this.getValueFromKeyWithWhitespace('my_ckOmessoStipContrII')
                ),
            ]);
            rows.push(['In caso di risposta positiva specificare: \n' + this.getValueFromKeyWithWhitespace('my_descOmessoStipContrII')]);
            rows.push(["6 – Dall'ultimo bilancio e dalla situazione patrimoniale aggiornata emergono i seguenti valori:"]);

            rows.push([this.tabellaCoopLiquidazioneCoattaBilancioPatrimoniale()]);

            //SCHEDA INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIORNATA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('INDICATORI DI BILANCIO DA CALCOLARE SULLA SITUAZIONE PATRIMONIALE AGGIORNATA', true, 'center', fontSize, italics)]);
            rows.push([this.tabellaCoopBilancioSituazionePatrimoniale()]);
            rows.push([
                format(
                    '¹) Al fine di una puntuale determinazione del corretto valore degli indicatori, in questo caso esclusivamente finalizzati a verificare il potenziale stato di insolvenza a breve, l’Attivo Circolante deve essere depurato dei crediti esigibili oltre l’esercizio. ',
                    bold,
                    alignment,
                    8,
                    italics
                ),
            ]);
            rows.push(['- Eventuali osservazioni: \n' + this.getValueFromKeyWithWhitespace('my_descEventOssII')]);
        }

        //PARAMETRI NASCOSTI
        rows.push([parametriNascosti(null, this.getValueFromKeyWithWhitespace('md5'), null, null)]);

        // return finale____________________
        return fxTabella(cols, rows);
    }

    private getLuogoAccertamento(): string {
        const luogoPrincipale: string = this.getValueFromKey('my_LuogoAccertamento').trim();
        if (luogoPrincipale !== '' && !_.isNil(luogoPrincipale) && !luogoPrincipale.includes('Altro luogo')) {
            return luogoPrincipale + ' ';
        }
        return this.getValueFromKeyWithWhitespace('my_SpecificaLuogoAccertamento');
    }

    private generateAccertamentoEstratto(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        //rows.push([intestazionePagine()]);
        // SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II -
        // ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO --- SEZIONE II - ACCERTAMENTO ---
        // SEZIONE II - ACCERTAMENTO ---
        //rows.push([newPage()]);

        //DATI IDENTIFICATIVI
        rows.push([intestazioneLogoConf()]);
        ////rows.push([intestazioneTitoloMinisteriale()])

        rows.push([format('Confederazione Cooperative Italiane', true, 'center', 12)]);
        rows.push([lineaVuota()]);
        rows.push([format('Associazione nazionale di rappresentanza, assistenza, tutela e revisione del movimento cooperativo', true, 'center', 10)]);
        rows.push([format('riconosciuta giuridicamente con D.M. 12 Aprile 1948 ai sensi del D.L.C.P.S. 14 Dicembre 1947, n. 1577', true, 'center', 10)]);
        rows.push([format('SEDE CENTRALE: Via Torino, 146 - 00184 Roma', true, 'center', 10)]);
        rows.push([lineaVuota()]);
        rows.push([format('VIGILANZA SUGLI ENTI COOPERATIVI AI SENSI DEL D.Lgs. 2 AGOSTO 2002 N.220', true, 'center', 11)]);
        rows.push([lineaVuota()]);
        rows.push([format('VERBALE DI REVISIONE', true, 'center', 11)]);
        rows.push([format('(conforme al modello approvato con D.M. 12/06/2017 del Ministero delle Imprese e del Made in Italy)', false, 'center', fontSize, italics)]);
        rows.push([lineaVuota()]);
        rows.push([format('SEZIONE II - Accertamento', true, 'center', 10, true)]);
        rows.push([
            doubleFormat(
                'Biennio di revisione: ',
                true,
                false,
                10,
                this.getValueFromKeyWithWhitespace('my_Anno1II') + ' / ' + this.getValueFromKeyWithWhitespace('my_Anno2II'),
                false,
                false,
                10
            ),
        ]);
        rows.push([
            doubleFormat(
                'Anno di revisione ',
                true,
                false,
                10,
                '(se soggetta a revisione annuale) ' + this.getValueFromKeyWithWhitespace('my_AnnoRevisionePerAnnualiII'),
                false,
                false,
                10
            ),
        ]);
        rows.push([this.fxDatiIdentificativiAccertamento()]);
        rows.push([
            '64 - Il sottoscritto revisore ' +
                this.getValueFromKeyWithWhitespace('my_RevisoreII') +
                ' alla presenza del Sig. ' +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopSceltaII') +
                " rappresentante dell'ente cooperativo in qualità di: " +
                this.getValueFromKeyWithWhitespace('my_QualitaRappresII') +
                ', indentificato con documento ' +
                this.getValueFromKeyWithWhitespace('my_DocumRappresII') +
                ' ,(in caso di delega allegare la stessa e copia del documento del legale rappresentante), assistito da ' +
                this.getValueFromKeyWithWhitespace('my_AssistRappresII') +
                " presso (luogo dove si svolge l'accertamento, specificare se sede legale, ufficio del revisore od altro luogo concordato) " +
                this.getValueFromKeyWithWhitespace('my_SpecificaLuogoAccertamento') +
                ' effettua l’accertamento al fine di verificare se sono state sanate le seguenti irregolarità, già oggetto di diffida, emerse nel corso della revisione: \n',
        ]);
        rows.push([this.tabellaIrrSanabili()]);
        // rows.push([  this.getValueFromKeyWithWhitespace("my_DescIrregSanabili") ])
        rows.push([lineaVuota()]);
        rows.push(['65 - Risultanze degli accertamenti effettuati:']);
        rows.push([this.tabellaRisultanzeAccertamentiEff()]);
        rows.push([format('EVENTUALI ALTRE NOTIZIE:', true, 'center', 11)]);
        rows.push([
            tabellaCheck(
                '66 - Il revisore è venuto a conoscenza di nuovi fatti, indipendenti dall’oggetto della diffida, che possano comportare la richiesta di un provvedimento?',
                this.getValueFromKeyWithWhitespace('my_CkAltriFattiProvv')
            ),
        ]);
        rows.push(['Specificare: ' + this.getValueFromKeyWithWhitespace('my_EventAltreNotiz')]);
        rows.push([format('CONCLUSIONI', true, 'center', 11)]);
        rows.push([
            tabellaCheck(
                "67 - Il rappresentante dell'ente dichiara che è stato portato a conoscenza dei soci, con le modalità previste dall’art. 17 del D.Lgs. 220/02, l’estratto del verbale della più recente revisione o ispezione?",
                this.getValueFromKeyWithWhitespace('my_CkConoscSoci')
            ),
        ]);
        rows.push(['- in caso negativo specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecConoscSoci')]);
        rows.push(['68 - Giudizio Conclusivo del Revisore ed eventuali altre notizie: \n' + this.getValueFromKeyWithWhitespace('my_GiudizioConclusivoII')]);
        rows.push(['69 - Eventuali dichiarazioni del legale rappresentante: \n' + this.getValueFromKeyWithWhitespace('my_OssControded')]);
        rows.push([lineaVuota()]);
        rows.push(['Il revisore, considerate le risultanze di cui ai punti precedenti:']);
        rows.push([this.tabellaOsservazioniControdeduzioniAccertamento()]);
        rows.push([
            'La presente sezione del verbale debitamente controfirmata, è redatta in n. ' +
                this.getValueFromKeyWithWhitespace('my_NOriginaliII') +
                " originali, uno dei quali viene consegnato al rappresentante dell'ente revisionato o, in caso di rifiuto di sottoscrizione, notificato a mezzo posta certificata/raccomandata.",
        ]);
        rows.push(['Le copie destinate all’Ufficio, sono completate con gli allegati elencati di seguito:']);
        rows.push([lineaVuota()]);
        rows.push([
            "Al rappresentante dell’ente viene ricordato l’obbligo di portare a conoscenza dei soci i contenuti del presente verbale con le modalità di cui all’art. 17 del Decreto legislativo n. 220/2002. Lo stesso viene altresì reso edotto della possibilità di presentare, entro 15 giorni dalla data odierna, eventuali ulteriori osservazioni o controdeduzioni all’Ufficio che ha disposto la revisione. Si precisa che la suddetta documentazione dovrà essere prodotta agli Uffici della Pubblica Amministrazione esclusivamente per via informatica a mezzo posta certificata. \n Ai sensi del combinato disposto del D.Lgs. n. 220/02 e della Legge n. 221/2012, si precisa che gli uffici amministrativi preposti alla vigilanza cooperativa assolvono i compiti loro affidati dalla legge esclusivamente nell'interesse pubblico e che la suddetta vigilanza esplica effetti ed è diretta nei soli confronti delle pubbliche amministrazioni ai fini della legittimazione a beneficiare delle agevolazioni fiscali, previdenziali e di altra natura, nonché per l’adozione dei provvedimenti previsti dall’articolo 12 del citato D.Lgs. n. 220/02.",
        ]);
        rows.push([lineaVuota()]);
        rows.push(['Luogo, ' + this.getValueFromKeyWithWhitespace('my_LuogoCompilazII') + '  data ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilazII'))]);
        rows.push([lineaVuota()]);
        rows.push([this.tabellaOsservazioniControdeduzioniFirmeAccertamento()]);

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '2') {
            //SCHEDA GESTIONE COMMISSARIALE

            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI GESTIONE COMMISSARIALE', true, 'center', fontSize, italics)]);
            rows.push([
                tabellaCheck('1 - Ci sono reali e concrete prospettive per il perseguimento dello scopo sociale?', this.getValueFromKeyWithWhitespace('my_ckProspettScopoSocII')),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descProspettScopoSocII'))]);
            rows.push([
                tabellaCheck(
                    "2 - Su quali risorse materiali e finanziarie può contare l'ente per la prosecuzione dell'attività sociale?",
                    this.getValueFromKeyWithWhitespace('my_ckRisFinProsecuzioneAttSocII')
                ),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descRisFinProsecuzioneAttSocII'))]);
            rows.push(["3 - Quali iniziative in concreto il Commissario Governativo, una volta nominato, dovrebbe porre in essere per regolarizzare l'ente?"]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descInizCommGovernativoII'))]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '3') {
            //SCHEDA PROPOSTA DI SCIOGLIMENTO
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SCIOGLIMENTO', true, 'center', fontSize, italics)]);
            rows.push(["1 - Quali delle fattispecie di cui all'art. 2545 septiesdecies c.c. si ritengono ricorrenti per la cooperativa?"]);
            rows.push([this.tabellaCoopPropostaScioglimentoAccertamento()]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descArt2545RicII'))]);
            rows.push([
                "2 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEsII') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari?', this.getValueFromKeyWithWhitespace('my_ckBeniImmobiliII')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione?', this.getValueFromKeyWithWhitespace('my_ckBeniMobiliRegistrII')))]);
            rows.push([
                Indent(
                    4,
                    tabellaCheck("- Un valore dell'Attivo che renda necessaria la nomina di un liquidatore?", this.getValueFromKeyWithWhitespace('my_ckValAttivoNecessLiquidII'))
                ),
            ]);
            rows.push([Indent(4, 'Specificare: \n' + this.getValueFromKeyWithWhitespace('my_descRisultUltBilAppII'))]);
            rows.push([
                tabellaCheck(
                    "3 - Ricorrono entrambi i presupposti di cui all'art. 223 septiesdecies Disp. Att. c.c.?",
                    this.getValueFromKeyWithWhitespace('my_ckPresuppostiArt223II')
                ),
            ]);
            rows.push([Indent(4, 'Mancato deposito dei bilanci di esercizio da oltre cinque anniII')]);
            rows.push([Indent(4, 'Assenza di:II')]);
            rows.push([Indent(6, '- Beni immobiliariII')]);
            rows.push([Indent(6, '- Beni mobiliari soggetti a registrazioneII')]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '4' || this.getValueFromKey('my_CkProvvedimendoDiII') == '9') {
            //SCHEDA PROPOSTA SOSTITUZIONE LIQUIDATORE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SOSTITUZIONE DEL LIQUIDATORE O DI CANCELLAZIONE DAL REGISTRO DELLE IMPRESE', true, 'center', fontSize, italics)]);
            rows.push([
                '1 - Descrivere la natura del ritardo nello svolgimento della liquidazione e le eventuali motivazioni addotte dal liquidatore \n ' +
                    this.getValueFromKeyWithWhitespace('my_descNatRitardoLiquidII'),
            ]);
            rows.push([lineaVuota()]);
            rows.push(['2 - Descrivere in sintesi le eventuali ulteriori motivazioni della proposta \n' + this.getValueFromKeyWithWhitespace('my_descUlterMotivazII')]);
            rows.push([lineaVuota()]);
            rows.push([tabellaCheck('3 - Sono stati depositati i bilanci relativi agli ultimi cinque esercizi?', this.getValueFromKeyWithWhitespace('my_ckDepBil5EserciziII'))]);
            rows.push([
                "4 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEsII') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari', this.getValueFromKeyWithWhitespace('my_ckBeniImmobII')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione', this.getValueFromKeyWithWhitespace('my_ckBeniMobRegistrII')))]);
            rows.push([this.getValueFromKeyWithWhitespace('my_descRisultanzeBilII')]);
            rows.push(['5 - Il liquidatore da sostituire è stato a suo tempo nominato:']);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAssSociII')) + " dall'Assemblea dei soci")]);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAutGiudII')) + " dall'Autorità Giudiziaria")]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDiII') == '5') {
            //SCHEDA LIQUIDAZIONE COATTA AMMINISTRATIVA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI LIQUIDAZIONE COATTA AMMINISTRATIVA', true, 'center', fontSize, italics)]);
            rows.push(['1 – Descrivere in sintesi le motivazioni della proposta \n ' + this.getValueFromKeyWithWhitespace('my_descMotivoPropostaII')]);
            rows.push([lineaVuota()]);
            rows.push([this.tabellaCoopLiquidazioneCoattaInterna()]);
            rows.push(['In caso affermativo specificare: \n' + this.getValueFromKeyWithWhitespace('my_descAzAvviateII')]);
            rows.push(['3 – Riepilogare le vertenze giudiziarie di cui al punto 51) \n' + this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudizII')]);
            rows.push([
                tabellaCheck(
                    "4 – E' stata allegata una situazione patrimoniale aggiornata, conforme allo schema di cui all’art. 2424 c.c., sottoscritta dal legale rappresentante e dall’Organo di Controllo se presente?",
                    this.getValueFromKeyWithWhitespace('my_ckSituazPatrAggiornII')
                ),
            ]);
            rows.push(['In caso di risposta negativa specificarne i motivi: \n' + this.getValueFromKeyWithWhitespace('my_descSituazPatrAggiornII')]);
            rows.push([
                tabellaCheck(
                    '5 - Risultano mancati pagamenti di mensilità stipendiali od omessi versamenti di contributi previdenziali? ',
                    this.getValueFromKeyWithWhitespace('my_ckOmessoStipContrII')
                ),
            ]);
            rows.push(['In caso di risposta positiva specificare: \n' + this.getValueFromKeyWithWhitespace('my_descOmessoStipContrII')]);
            rows.push(["6 – Dall'ultimo bilancio e dalla situazione patrimoniale aggiornata emergono i seguenti valori:"]);

            rows.push([this.tabellaCoopLiquidazioneCoattaBilancioPatrimoniale()]);

            //SCHEDA INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIORNATA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII'))]);
            rows.push([format('INDICATORI DI BILANCIO DA CALCOLARE SULLA SITUAZIONE PATRIMONIALE AGGIORNATA', true, 'center', fontSize, italics)]);
            rows.push([this.tabellaCoopBilancioSituazionePatrimoniale()]);
            rows.push([
                format(
                    '¹) Al fine di una puntuale determinazione del corretto valore degli indicatori, in questo caso esclusivamente finalizzati a verificare il potenziale stato di insolvenza a breve, l’Attivo Circolante deve essere depurato dei crediti esigibili oltre l’esercizio. ',
                    bold,
                    alignment,
                    8,
                    italics
                ),
            ]);
            rows.push(['- Eventuali osservazioni: \n' + this.getValueFromKeyWithWhitespace('my_descEventOssII')]);
        }

        //PARAMETRI NASCOSTI
        rows.push([parametriNascosti(null, this.getValueFromKeyWithWhitespace('md5'), null, null)]);

        // return finale____________________
        return fxTabella(cols, rows);
    }

    private generateRilevazione(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        // SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE ---
        // SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE ---

        //INTESTAZIONE
        rows.push([intestazioneLogoConf()]);
        //rows.push([intestazioneTitoloMinisteriale()])

        rows.push([format('Confederazione Cooperative Italiane', true, 'center', 12)]);
        rows.push([lineaVuota()]);
        rows.push([format('Associazione nazionale di rappresentanza, assistenza, tutela e revisione del movimento cooperativo', true, 'center', 10)]);
        rows.push([format('riconosciuta giuridicamente con D.M. 12 Aprile 1948 ai sensi del D.L.C.P.S. 14 Dicembre 1947, n. 1577', true, 'center', 10)]);
        rows.push([format('SEDE CENTRALE: Via Torino, 146 - 00184 Roma', true, 'center', 10)]);
        rows.push([lineaVuota()]);
        rows.push([format('VIGILANZA SUGLI ENTI COOPERATIVI AI SENSI DEL D.Lgs. 2 AGOSTO 2002 N.220', true, 'center', 11)]);
        rows.push([lineaVuota()]);
        rows.push([format('VERBALE DI REVISIONE', true, 'center', 11)]);
        rows.push([format('(conforme al modello approvato con D.M. 12/06/2017 del Ministero delle Imprese e del Made in Italy)', false, 'center', fontSize, italics)]);
        rows.push([lineaVuota()]);
        rows.push([format('SEZIONE I - Rilevazione', true, 'center', 10, true)]);

        //DATI IDENTIFICATIVI
        rows.push([format('DATI IDENTIFICATIVI', false, 'center')]);
        rows.push([
            doubleFormat(
                'Biennio di revisione: ',
                true,
                false,
                10,
                this.getValueFromKeyWithWhitespace('my_Anno1') + '/' + this.getValueFromKeyWithWhitespace('my_Anno2'),
                false,
                false,
                10
            ),
        ]);

        rows.push([
            doubleFormat(
                'Anno di revisione ',
                true,
                false,
                10,
                '(se soggetta a revisione annnuale) ' +
                    this.getValueFromKeyWithWhitespace('my_AnnoRevisionePerAnnuali') +
                    ' Revisione eseguita dal ' +
                    this.formatDate(this.getValueFromKeyWithWhitespace('my_DataRevisioneDal')) +
                    ' al ' +
                    this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilaz')),
                false,
                false,
                10
            ),
        ]);
        rows.push([
            format('Data delle eventuali dichiarazioni sostitutive prodotte (art.6, comma 6, D.Lgs. 220/02): ' + this.getValueFromKeyWithWhitespace('my_DataDichSost'), true),
        ]);
        rows.push([this.fxDatiIdentificativi()]);
        rows.push([
            "Rappresenta l'ente cooperativo, nel corso della revisione, il Sig. " +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopCognome') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopNome') +
                ', in qualità di*: ' +
                this.getValueFromKeyWithWhitespace('my_QualitaRappres') +
                ', identificato con documento ' +
                this.getValueFromKeyWithWhitespace('my_DocumRappres') +
                ', (in caso di delega allegare la stessa e copia del documento del legale rappresentante)',
        ]);

        rows.push(['assistito da: ' + this.getValueFromKeyWithWhitespace('my_AssistRappres')]);

        rows.push([
            format(
                '* Nel caso in cui il delegato del legale rappresentante non fornisca esaustivamente le notizie richieste il revisore deve esigere la presenza del legale rappresentante.',
                false,
                'left',
                8,
                false
            ),
        ]);

        rows.push([
            'Redige il presente verbale il Sig. ' +
                this.getValueFromKeyWithWhitespace('my_Revisore') +
                ' iscritto al n. ' +
                this.getValueFromKeyWithWhitespace('my_NIscrizRevis') +
                " dell'elenco tenuto presso il Ministero delle Imprese e del Made in Italy (art. 7, c. 6, D.L.gs. 220/02), in qualità di revisore incaricato da " +
                this.getValueFromKeyWithWhitespace('my_IncaricRevis') +
                ' presso (luogo dove si svolge la revisione (specificare se sede legale, ufficio del revisore od altro luogo concordato) : \n' +
                this.getValueFromKeyWithWhitespace('my_LuogoRilevazione'),
        ]);

        rows.push([lineaVuota()]);

        //NATURA MUTUALISTICA
        rows.push([format('NATURA MUTUALISTICA E ATTIVITÀ SVOLTA', true, 'center', 11)]);
        rows.push([format("REQUISITI INERENTI L'ATTO COSTITUTIVO E LO STATUTO", true, 'left', 10)]);
        rows.push(['Lo statuto prevede:']);
        rows.push([tabellaCheck('1 - lo scopo mutualistico (art. 2511 c.c.)', this.getValueFromKeyWithWhitespace('my_CkScopoMutual'))]);
        rows.push(['- in caso negativo specificare:']);
        rows.push([this.getValueFromKeyWithWhitespace('my_DescScopoMutual')]);
        rows.push([
            tabellaCheck(
                "2 - l'obbligo della destinazione degli utili netti annuali a riserva legale nella misura prevista dalla legge?",
                this.getValueFromKeyWithWhitespace('my_CkObblDestinazUtil')
            ),
        ]);
        rows.push([
            tabellaCheck(
                "3 - l'obbligo della devoluzione di una quota di utili netti annuali ai fondi mutualistici per la promozione e lo sviluppo della cooperazione (art. 2545 quater, comma 2) ?",
                this.getValueFromKeyWithWhitespace('my_CkObbDevolUtili')
            ),
        ]);
        rows.push([tabellaCheck("4 - i requisiti della mutualità prevalente di cui all'art. 2514 c.c. ?", this.getValueFromKeyWithWhitespace('my_CkReqMutualPrev'))]);
        rows.push(['- ovvero in caso di cooperative a mutualità non prevalente (ex art. 2545 quinquies c.c.)']);
        rows.push([
            tabellaCheck(
                Indent(2, '- le modalità e la percentuale massima di ripartizione dei dividendi tra i soci cooperatori?'),
                this.getValueFromKeyWithWhitespace('my_CkMutuaDivid')
            ),
        ]);
        rows.push([
            tabellaCheck(Indent(2, "- l'assegnazione ai soci, da parte dell'assemblea, delle riserve divisibili?"), this.getValueFromKeyWithWhitespace('my_CkMutuaRiserve')),
        ]);
        rows.push([
            tabellaCheck(
                "5 - la possibilità di ammettere il nuovo socio cooperatore in una categoria speciale in ragione dell'interesse alla sua formazione ovvero del suo inserimento nell'impresa, determinandone il termine massimo di permanenza, i diritti e gli obblighi?",
                this.getValueFromKeyWithWhitespace('my_CkNuovoSocioCatSpec')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '6 - la possibilità che la società svolga la propria attività anche con terzi ex art. 2512 c.c.?',
                this.getValueFromKeyWithWhitespace('my_CkAttivConTerzi')
            ),
        ]);
        rows.push([tabellaCheck('7 - la definizione dei criteri per la ripartizione dei ristorni?', this.getValueFromKeyWithWhitespace('my_CkDefCriteriRistorni'))]);
        rows.push([
            tabellaCheck(
                '8 - assemblee separate ex art.2540 c.c., per specifiche materie ovvero particolari categorie di soci? ',
                this.getValueFromKeyWithWhitespace('my_CkAssSeparate')
            ),
        ]);
        rows.push([tabellaCheck('9 - assemblee speciali dei possessori degli strumenti finanziari ex art. 2541 cc?', this.getValueFromKeyWithWhitespace('my_CkAssSpeciali'))]);
        rows.push([lineaVuota()]);
        rows.push([
            tabellaCheck(
                "Alla luce di quanto sopra e dall'esame dello stesso, lo Statuto è conforme alle previsioni normative?",
                this.getValueFromKeyWithWhitespace('my_CkStatutoConforme')
            ),
        ]);
        rows.push(['eventualmente specificare:' + this.getValueFromKeyWithWhitespace('my_StatConfNorm')]);
        rows.push([format("REQUISITI INERENTI L'ATTIVITÀ", true, 'left', 10)]);
        rows.push(["Eventuali osservazioni sull'effettivo rispetto delle previsioni Statutarie: \n" + this.getValueFromKeyWithWhitespace('my_OssRispettoPrevis')]);
        rows.push(["10 - Descrizione dell'oggetto sociale e dell'attività effettivamente svolta: \n"]);
        rows.push(['a) oggetto sociale come risultante dallo statuto: ' + this.getValueFromKeyWithWhitespace('my_OggSocStatuto')]);
        rows.push(['b) attività svolta in concreto: \n' + this.getValueFromKeyWithWhitespace('my_AttivSvoltaConcreta')]);
        rows.push([
            tabellaCheck(
                Indent(1, "- l'attività effettivamente svolta e l'oggetto sociale sono coerenti tra loro e con la categoria di iscrizione nell'Albo?"),
                this.getValueFromKeyWithWhitespace('my_CkAttivEffettSvolta')
            ),
        ]);
        rows.push([Indent(2, ' - in caso di risposta negativa, specificare: \n' + this.getValueFromKeyWithWhitespace('my_AttivEffettSvolta'))]);
        rows.push([
            tabellaCheck(
                "11  - Oltre all'eventuale Regolamento ex art. 6 della legge n. 142/01, esistono altri regolamenti interni che disciplinano aspetti della vita cooperativa e/o le modalità di attuazione dello scambio mutualistico? ",
                this.getValueFromKeyWithWhitespace('my_CkRegolamInterni')
            ),
        ]);
        rows.push([
            tabellaCheck(
                Indent(2, " - In caso di risposta affermativa, sono redatti ed approvati in conformità dell'ultimo comma dell'art. 2521 c.c. ?"),
                this.getValueFromKeyWithWhitespace('my_CkRegolamInterniConformi')
            ),
        ]);
        rows.push([Indent(2, ' - Descrivere gli aspetti oggetto di regolamentazione:' + this.getValueFromKeyWithWhitespace('my_AspettiOggRegolam'))]);
        rows.push([
            tabellaCheck(
                "12 - Nella costituzione e nell'esecuzione dei rapporti mutualistici è rispettato il principio di parità di trattamento tra i soci cooperatori?",
                this.getValueFromKeyWithWhitespace('my_CkParitaSociCoop')
            ),
        ]);
        rows.push([
            Indent(2, ' - Specificare le modalità e, in caso di risposta negativa, indicarne le motivazioni: ' + this.getValueFromKeyWithWhitespace('my_DescParitaSociCoop')),
        ]);
        rows.push([
            tabellaCheck(
                "13 - Il sistema amministrativo dell'ente consente di distinguere lo scambio mutualistico con i soci rispetto ai rapporti con terzi?",
                this.getValueFromKeyWithWhitespace('my_CkDistScambioMutual')
            ),
        ]);
        rows.push(['Specificare, indicandone le modalità e le eventuali carenze: ' + this.getValueFromKeyWithWhitespace('my_DescScambioMutual')]);
        rows.push([
            tabellaCheck(
                "14 - Nel Bilancio è documentata la condizione di prevalenza con l'evidenziazione dei parametri di cui all'art. 2513 c.c. ? ",
                this.getValueFromKeyWithWhitespace('my_CkCondizPrevNotaIntegr')
            ),
        ]);
        rows.push(['Se necessario specificare:' + this.getValueFromKeyWithWhitespace('my_DescCondizPrevNotaIntegr')]);

        //SCHEDA CONTROLLO VERIFICA PREVALENZA
        rows.push([fxTabellaEsternaBordo(this.fxVerificaPrevalenza())]);
        rows.push([
            tabellaCheck(
                "- L'attività dell'ente viene svolta in concreto prevalentemente in favore dei soci, ai sensi degli art. 2512 e 2513 c.c.?",
                this.getValueFromKeyWithWhitespace('my_CkAttPrevalSoci')
            ),
        ]);
        rows.push([
            tabellaCheck(
                Indent(2, ' - In caso di risposta negativa, la cooperativa è prevalente di diritto o rientra in uno dei regimi derogatori?'),
                this.getValueFromKeyWithWhitespace('my_CkRientraRegDerogatori')
            ),
        ]);
        rows.push([Indent(4, 'Specificare:' + this.getValueFromKeyWithWhitespace('my_DescRegimeDerogatorioProvalenza'))]);
        rows.push([doubleFormat('15 - ', false, false, 10, 'In caso di cooperativa a mutualità non prevalente:', true, false, 10)]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di dividendi tra i soci?', this.getValueFromKeyWithWhitespace('my_CkEsisteRipDividSociMutNP'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva sono state rispettate le previsioni di cui all'art. 2545 quinquies?",
                this.getValueFromKeyWithWhitespace('my_CkRipDividSociMutNP')
            ),
        ]);
        rows.push([tabellaCheck('- sono state costituite riserve divisibili?', this.getValueFromKeyWithWhitespace('my_CkCostituiteRiserveDivisibili'))]);
        rows.push([tabellaCheck('- in caso di risposta positiva sono gestite separatamente?', this.getValueFromKeyWithWhitespace('my_CkRiserveIndivis'))]);
        rows.push([tabellaCheck('- la gestione separata è riportata in Nota Integrativa?', this.getValueFromKeyWithWhitespace('my_CkGestSeparata'))]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di riserve?', this.getValueFromKeyWithWhitespace('my_CkRiserveDivisibiliRipartite'))]);
        rows.push([
            tabellaCheck("- sono state rispettate le previsioni di cui all'art. 2545 quinquies?", this.getValueFromKeyWithWhitespace('my_CkRiserveDivisibiliRispetPrevision')),
        ]);
        rows.push([doubleFormat('16 - ', false, false, 10, 'In caso di cooperativa a mutualità prevalente', true, false, 10)]);
        rows.push([
            tabellaCheck(
                '- i criteri di prevalenza ex art. 2513 sono stati rispettati in almeno uno degli ultimi due esercizi?',
                this.getValueFromKeyWithWhitespace('my_CkRispCritPrev')
            ),
        ]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di dividendi tra i soci? ', this.getValueFromKeyWithWhitespace('my_CkSonoStatiRipDividSociMutP'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva sono state rispettate le previsioni di cui all'art. 2514 c.c. lettere a)?",
                this.getValueFromKeyWithWhitespace('my_CkRipDividSociMutP')
            ),
        ]);
        rows.push([tabellaCheck('- sono stati emessi strumenti finanziari?', this.getValueFromKeyWithWhitespace('my_CkEmessoStrumFinanziari'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva, sono rispettate le previsioni di cui all'art. 2514 c.c. lettera b)?",
                this.getValueFromKeyWithWhitespace('my_CkRispettoGestStrumFinanziari')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '17 - In base alle risposte fornite ai punti precedenti, nonché dalle verifiche di cui alla scheda di controllo sulla prevalenza, la cooperativa può essere considerata a mutualità prevalente?',
                this.getValueFromKeyWithWhitespace('my_CkConsidMutualPrev')
            ),
        ]);

        if (Utilities.convertStringToNumber(this.getValueFromKey('my_CkCambiatoSezione')) === 1) {
            rows.push([
                tabellaCheck(
                    '18 - In caso di perdita o di riacquisto della qualifica di prevalenza la cooperativa ha correttamente ottemperato alle disposizioni di legge? *',
                    this.getValueFromKeyWithWhitespace('my_CkAdempiutoProcedureCambioSez')
                ),
            ]);
        }

        rows.push([
            format(
                "(*) Si ricorda che, in base all'art. 2545 octies c.c., sia in caso di perdita che di riacquisto della qualifica di prevalenza, la cooperativa è tenuta a segnalare tali condizioni all'Albo e, in caso di modifica delle previsioni statutarie di cui all'articolo 2514 c.c. o di emissione di strumenti finanziari, a provvedere anche alla redazione dell'apposito bilancio.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push(['- Note ed osservazioni:' + this.getValueFromKeyWithWhitespace('my_NatMutNote')]);

        //CATEGORIE SOCI E BASE SOCIALE
        rows.push([format('CATEGORIE DEI SOCI ED EFFETTIVITÀ DELLA BASE SOCIALE', true, 'left', 10, italics)]);
        rows.push(['19 - Categorie di soci presenti:']);
        rows.push([this.tabellaSociPresenti()]);
        rows.push([
            tabellaCheck(
                "20 - La procedura seguita per l'ammissione, il recesso e l'esclusione dei soci, è conforme alle previsioni di Legge e di Statuto?",
                this.getValueFromKeyWithWhitespace('my_CkProcedConforme')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecProcedConforme')]);
        rows.push([
            tabellaCheck(
                "- ai sensi dell'art. 2527 c.c., i requisiti dei soci sono coerenti con l'attività svolta dalla cooperativa?",
                this.getValueFromKeyWithWhitespace('my_CkReqSociCoerenti')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecReqSociCoerenti')]);
        rows.push([
            tabellaCheck(
                "21 - In presenza di soci ammessi alla categoria speciale, è rispettato il limite di cui all'art. 2527 c.c.? ",
                this.getValueFromKeyWithWhitespace('my_CkSociSpecLimite')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '- al termine di un periodo comunque non superiore a cinque anni i soci appartenenti alla categoria speciale sono ammessi a godere dei diritti che spettano agli altri soci cooperatori. ',
                this.getValueFromKeyWithWhitespace('my_CkSociSpecDiritti')
            ),
        ]);
        rows.push(['- in caso di risposta negativa, specificare: ' + this.getValueFromKeyWithWhitespace('my_SocSpecNota')]);
        rows.push([this.tabellaTreUnitaSociale()]);
        rows.push([lineaVuota()]);

        //BILANCIO
        rows.push([format('BILANCIO', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                '23 - Lo schema del bilancio approvato (stato patrimoniale, rendiconto finanziario, conto economico, nota integrativa) è conforme a quanto previsto dal Codice Civile?',
                this.getValueFromKeyWithWhitespace('my_CkConformSchemaBil')
            ),
        ]);
        rows.push([tabellaCheck('24 - Sono stati rispettati i termini di approvazione del bilancio?', this.getValueFromKeyWithWhitespace('my_CkRispTermAppBil'))]);
        rows.push([
            tabellaCheck("25 - L'ultimo bilancio approvato risulta regolarmente depositato al Registro delle Imprese?", this.getValueFromKeyWithWhitespace('my_CkBilRegDeposURI')),
        ]);
        rows.push([tabellaCheck("26 - L'ente cooperativo è tenuto alla certificazione di bilancio? ", this.getValueFromKeyWithWhitespace('my_CkEnteTenutoCertBil'))]);
        rows.push([
            tabellaCheck(
                Indent(1, '- in caso di risposta affermativa provvede regolarmente ex comma 2, art. 15 della Legge 59/92?'),
                this.getValueFromKeyWithWhitespace('my_CkProvvedeCertBil')
            ),
        ]);
        rows.push(['Ragione sociale della Società di Revisione incaricata:' + this.getValueFromKeyWithWhitespace('my_RagSocRevIncaricataDesc')]);
        rows.push(["27 - Qual è l'importo del patrimonio netto risultante dal bilancio negli ultimi 2 esercizi?"]);
        rows.push([this.miniTabDoppiaBilancio()]);
        rows.push(['28 - Capitale Sociale ']);
        rows.push([this.miniTabQuattroBilancio()]);
        rows.push([this.miniTabDoppiaCapSociale()]);
        rows.push([fxTabellaEsternaBordo(this.tabellaControlloRistorni())]);
        rows.push([
            format(
                "1) Considerata la soppressione della sezione “E” del conto economico, nel calcolo dell'avanzo della gestione mutualistica non si dovrà tener conto degli elementi di entità o incidenza eccezionali che devono essere indicati in nota integrativa, a condizione che non siano attinenti allo scambio mutualistico tra soci e società, quali, a titolo esemplificativo, quelli relativi alle plusvalenze e minusvalenze di natura straordinaria derivanti da trasferimenti d'azienda o di rami d'azienda, alienazione di partecipazioni, immobili e beni non strumentali, nonché al plusvalore derivante dall'acquisizione delle immobilizzazioni materiali a titolo gratuito.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push([tabellaCheck('30 - Le eventuali ristorni risultano correttamente determinati e ripartiti?', this.getValueFromKeyWithWhitespace('my_CkRistorni'))]);
        rows.push([tabellaCheck("31 - L'ente cooperative raccoglie il prestito dai soci?", this.getValueFromKeyWithWhitespace('my_CkPrestitoSoci'))]);
        rows.push([fxTabellaEsternaBordo(this.tabellaVerificaContabilePrestito())]);
        rows.push([Indent(2, '- in caso di risposta affermativa:')]);
        rows.push([tabellaCheck(Indent(2, '- è rispettato il limite individuale?'), this.getValueFromKeyWithWhitespace('my_CkRispettatoLimiteIndivid'))]);
        rows.push([
            tabellaCheck(Indent(2, '- in caso di cooperative con più di 50 soci, è rispettato il rapporto CICR?'), this.getValueFromKeyWithWhitespace('my_CkRipettatoCICR')),
        ]);
        rows.push([
            tabellaCheck(
                '32 - In base alle risposte fornite ai punti precedenti, nonché dalle verifiche di cui alla scheda di controllo sul prestito da soci la raccolta del prestito è svolta correttamente?',
                this.getValueFromKeyWithWhitespace('my_CkPrestitoSociCorretto')
            ),
        ]);
        rows.push([
            tabellaCheck("33 - La società è stata destinataria di contributi pubblici dalla data dell'ultima revisione?", this.getValueFromKeyWithWhitespace('my_CkContribPubb')),
        ]);
        rows.push([' - Descrizione:' + this.getValueFromKeyWithWhitespace('my_DescContribPubb')]);
        rows.push([this.tabellaEntiErogatori()]);
        rows.push([tabellaCheck('34 - La cooperativa redige il Bilancio Sociale?', this.getValueFromKeyWithWhitespace('my_CkRedazBilSociale'))]);
        rows.push([Indent(2, '- Eventualmente specificare:' + this.getValueFromKeyWithWhitespace('my_NoteBilSociale'))]);
        rows.push([tabellaCheck("35 - L'ente è tenuto alla redazione del bilancio consolidato?", this.getValueFromKeyWithWhitespace('my_CkRedazBilConsol'))]);
        rows.push([Indent(2, 'In caso di risposta affermativa:')]);
        rows.push([Indent(2, tabellaCheck('- viene regolarmente redatto?', this.getValueFromKeyWithWhitespace('my_CkRegolRedBilConsol')))]);
        rows.push([Indent(2, tabellaCheck('- viene regolarmente depositato presso il Registro delle Imprese?', this.getValueFromKeyWithWhitespace('my_CkRegolDepBilConsol')))]);
        rows.push(['36 - Società partecipate']);
        rows.push([this.tabellaSocietaPartecipate()]);
        rows.push([
            tabellaCheck(
                "37 - Dall'ultima revisione l'ente ha presentato le dichiarazioni fiscali (mod. Redditi, Mod. 770, Dichiarazione IVA, IRAP)?",
                this.getValueFromKeyWithWhitespace('my_CK37DichFiscPresDaUltRev')
            ),
        ]);
        rows.push(['In caso di risposta negativa specificare:' + this.getValueFromKeyWithWhitespace('my_DescDichFiscPresDaUltRev')]);
        rows.push(['38 - Eventuali note ed osservazioni inerenti i punti da 23 a 37:' + this.getValueFromKeyWithWhitespace('my_NoteEdOsserv')]);

        //RAGGIUNGIMENTO DELLO SCOPO SOCIALE
        rows.push([format('RAGGIUNGIMENTO DELLO SCOPO SOCIALE', true, 'center', 10, italics)]);
        rows.push(["39 - Capacità dell'ente cooperativo di raggiungere gli scopi per cui è stato costituito:" + this.getValueFromKeyWithWhitespace('my_CapacitaRaggScopi')]);
        rows.push([
            '40 - Eventi non attinenti alla gestione caratteristica che hanno provocato sensibili conseguenze sulla situazione economica, finanziaria e patrimoniale della società:' +
                this.getValueFromKeyWithWhitespace('my_EventiNonAttinenti'),
        ]);
        rows.push([doubleFormat('41 - ', false, false, 10, 'INDICATORI DI BILANCIO', true, false, 10, 'center')]);
        rows.push([this.tabellaIndicatoriBilancio()]);
        rows.push([
            '42 - Eventuali note e osservazioni sulla situazione aziendale con particolare riferimento alle risultanze di cui ai punti 39, 40 e 41:' +
                this.getValueFromKeyWithWhitespace('my_NoteSituazAziendale'),
        ]);

        rows.push([newPage()]);

        //SISTEMA AMMINISTRAZIONE E CONTROLLO
        rows.push([format('SISTEMA DI AMMINISTRAZIONE E DI CONTROLLO (*)', bold, 'center', 10, italics)]);
        rows.push([this.tabellaSistemaAmministrazioneControllo()]);
        rows.push([format('Il revisore verificherà l’effettività degli organi sociali ed il loro regolare funzionamento', bold, alignment, 9, true)]);
        rows.push([format('(*) N.B. Allegare solo la scheda relativa al sistema utilizzato', bold, alignment, 9, true)]);

        //SCHEDE AMMINISTRAZIONE E CONTROLLO

        let schContr = Number(this.getValueFromKey('my_CkNorme')); //NaN

        if (!isNaN(schContr)) {
            if (schContr > 0) {
                rows.push([this.creaSchedaAmmControllo(schContr)]);
            }
        }
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(2)])
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(3)])
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(4)])

        rows.push([tabellaCheck('43 - Gli organi societari sono correttamente costituiti?', this.getValueFromKeyWithWhitespace('my_CkOrgSocietCorrCostit'))]);
        rows.push([tabellaCheck('44 - Funzionano correttamente?', this.getValueFromKeyWithWhitespace('my_CkOrgSocietCorrFunz'))]);
        rows.push(['45 - Note ed osservazioni \n' + this.getValueFromKeyWithWhitespace('my_NoteEdOsservazioni')]);
        rows.push([
            '46 - Eventuali note ed osservazioni sulla natura mutualistica dell’ente cooperativo sotto il profilo formale e sostanziale, sull’effettività e la composizione della base sociale e sulla reale partecipazione dei soci alla vita sociale:' +
                this.getValueFromKeyWithWhitespace('my_NoteNatMutEnte'),
        ]);
        rows.push([lineaVuota()]);

        //CONTRIBUTO BIENNALE
        rows.push([format('CONTRIBUTO BIENNALE', true, 'center', 10, italics)]);
        rows.push([lineaVuota()]);
        rows.push(['47 -  Calcolo del contributo relativo al Biennio ' + this.getValueFromKeyWithWhitespace('my_Anno1') + ' / ' + this.getValueFromKeyWithWhitespace('my_Anno2')]);

        rows.push([this.tabellaRiferimentoContributoBiennale()]);
        rows.push([this.tabellaRiferimentoContributoBiennaleImporti()]);
        rows.push([' - Maggiorazione del 10 per cento (per gli enti cooperativi edilizi)³ dovuta € ' + this.getValueFromKeyWithWhitespace('my_MaggEntiEdilDovuta')]);
        rows.push([' - Maggiorazione del 10 per cento (per gli enti cooperativi edilizi)* versata € ' + this.getValueFromKeyWithWhitespace('my_MaggEntiEdilVersata')]);
        rows.push([' - Data scadenza da cui far decorrere il calcolo delle penalità: ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataScadCalcPenal'))]);
        rows.push([
            format(
                "¹) Se l'ente si è posto in liquidazione prima della scadenza di pagamento del contributo assolverà lo stesso per l'importo minimo, altrimenti l'importo dovuto sarà quello relativo alla fascia contributiva normale. \n ²) Nelle cooperative edilizie il fatturato è determinato prendendo come riferimento il maggior valore tra l’incremento di valore dell’immobile rilevato nelle voci B-2 e C-1 dello Stato patrimoniale (art. 2424 cc.) e il valore della produzione di cui alla lettera A) dell’art.2425 cc.” \n ³) Maggiorazione comunque dovuta alla P.A. anche da parte delle cooperative edilizie aderenti ad Associazioni Nazionali di Rappresentanza e dalle stesse vigilate (il relativo versamento deve effettuarsi a mezzo mod. F24). \n *) Al fine della determinazione del contributo per le cooperative edilizie, iscritte al relativo Albo con programma avviato, si precisa che con delibera del 29/02/2016 il Comitato per l’Albo ha individuato nell’”Inizio Lavori” l’avvio effettivo degli stessi, a seguito dell’ottenimento del permesso di costruire e la comunicazione all’Ufficio Tecnico del Comune che ne attesta l’inizio.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push([tabellaCheck('48 - Risultano irregolarità contributive pregresse dall’ultima revisione?', this.getValueFromKeyWithWhitespace('my_CkContrBiennIrrUltRev'))]);
        rows.push(['49 - Note ed osservazioni sul contributo biennale: \n ' + this.getValueFromKeyWithWhitespace('my_NoteContrBienn')]);
        rows.push([format('CONTRIBUTO AI SENSI DELL’ART. 11 DELLA LEGGE 59/92', true, 'center', 10, italics)]);
        rows.push([fxTabellaEsternaBordo(this.tabellaDevoluzioneContributi())]);
        rows.push([
            tabellaCheck(
                '50 - In ordine alla devoluzione ai fondi mutualistici ex art. 11 della legge n. 59/92, l’ente cooperativo ha correttamente effettuato i versamenti eventualmente dovuti?',
                this.getValueFromKeyWithWhitespace('my_CkVersamEffett')
            ),
        ]);
        rows.push([
            Indent(4, tabellaCheck('- Risultano irregolarità contributive pregresse dalla data dell’ultima revisione?', this.getValueFromKeyWithWhitespace('my_CkIrregPregL59'))),
        ]);
        rows.push(['- Eventualmente specificare: \n ' + this.getValueFromKeyWithWhitespace('my_DescVersamEffett')]);
        rows.push([format('ALTRE NOTIZIE', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                '51 - Il legale rappresentante dichiara la sussistenza di vertenze giudiziarie particolarmente rilevanti in corso?',
                this.getValueFromKeyWithWhitespace('my_CkVertenzeGiudiz')
            ),
        ]);
        rows.push([
            'Quali? (indicare sinteticamente parti in causa, oggetto e stato degli atti e gli eventuali rischi sulla situazione patrimoniale ed economica): \n' +
                this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudiz'),
        ]);
        rows.push(['Specificare il complessivo presumibile importo, se quantificabile: € ' + this.getValueFromKeyWithWhitespace('my_VertenzeGiudizImpPres')]);

        //LIBRI SOCIALI
        rows.push([lineaVuota()]);
        rows.push([format('LIBRI SOCIALI', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                doubleFormat('52 ', true, italics1, fontSize1, '- I Libri sociali obbligatori sono stati regolarmente istituiti? ', bold2, italics2, fontSize2, alignment),
                this.getValueFromKeyWithWhitespace('my_CkLibriSociali')
            ),
        ]);
        rows.push(['In caso di risposta affermativa: ']);
        rows.push([tabellaCheck('- risultano correttamente bollati e vidimati inizialmente?', this.getValueFromKeyWithWhitespace('my_CkLibriSocBolloVid'))]);
        rows.push([
            tabellaCheck('- risultano versate correttamente l’imposta di bollo e la tassa di concessione governativa?', this.getValueFromKeyWithWhitespace('my_CkLibriSocTassa')),
        ]);
        rows.push([lineaVuota()]);
        rows.push([format('53 - LIBRO DEI SOCI [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([
            tabellaCheck(
                '- In ciascuna sezione è correttamente riportata: la numerazione progressiva, l’anagrafica completa del socio, la data di ammissione nella compagine societaria, la qualifica del socio, la quota sociale sottoscritta, quella versata, la data di recesso o esclusione, la quota restituita?',
                this.getValueFromKeyWithWhitespace('my_CkLibroSociDati')
            ),
        ]);
        rows.push(['- se necessario specificare: \n ' + this.getValueFromKeyWithWhitespace('my_NoteLibroSoci')]);
        rows.push([lineaVuota()]);
        rows.push([format('54 - LIBRO DELLE ASSEMBLEE / DECISIONI DEI SOCI [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([
            tabellaCheck(
                '- E’ correttamente trascritto il verbale dell’Assemblea eventualmente andata deserta in prima convocazione o, comunque, nei verbali d’Assemblea viene evidenziato se trattasi di convocazioni successive?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssVerb')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '- I verbali sono redatti con la necessaria chiarezza, tale da rendere possibile determinare i quorum costitutivi e deliberativi, i presenti in proprio o per delega, i voti dagli stessi espressi?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssRedaz')
            ),
        ]);
        rows.push([tabellaCheck('- I verbali sono sottoscritti dal Presidente e dal Segretario dell’Assemblea?', this.getValueFromKeyWithWhitespace('my_CkLibroAssVerbSott'))]);
        rows.push([
            tabellaCheck(
                '- Nei verbali in cui si approva il bilancio, ovvero regolamenti o modifiche statutarie tali documenti vengono correttamente trascritti?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssTrascritti')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroAssemb')]);
        rows.push([
            tabellaCheck(
                'In caso di convocazione di Assemblee separate o speciali, sono stati istituiti i libri relativi',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssSpeciali')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroAssSpeciali')]);
        rows.push([lineaVuota()]);
        rows.push([
            format("55 - LIBRO DELL’ORGANO AMMINISTRATIVO (Collegiale, Plurale, Monocratico)\novvero - DELL'ORGANO LIQUIDATORIO [OBBLIGATORIO]", true, alignment, 11, italics),
        ]);
        rows.push([
            tabellaCheck(
                '- Risultano trascritte le delibere/determinazioni relative ai principali fatti gestionali nonché quelle relative all’ammissione, al recesso ed all’esclusione dei soci?',
                this.getValueFromKeyWithWhitespace('my_CkLibroOrgAmm')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroOrgAmm')]);
        rows.push([lineaVuota()]);
        rows.push([format('56 - LIBRO DELL’ORGANO DI CONTROLLO (Collegiale, Monocratico) [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([tabellaCheck('- Risultano trascritti i verbali delle riunioni/verifiche da tenersi ogni 90 giorni?', this.getValueFromKeyWithWhitespace('my_CkLibroOrgContr'))]);
        rows.push([tabellaCheck('- E’ correttamente indicata la natura dei controlli effettuati?', this.getValueFromKeyWithWhitespace('my_CkLibroOrgContrNat'))]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroOrgContr')]);
        rows.push([format('57 - LIBRO DELL’ORGANO DI CONTROLLO CONTABILE [FACOLTATIVO]', true, alignment, 11, italics)]);
        rows.push([Indent(2, format('(Revisore - Società di Revisione) ', true, alignment, 10, italics))]);
        rows.push([tabellaCheck('- E’ stato istituito il libro dell’Organo di Controllo Contabile?', this.getValueFromKeyWithWhitespace('my_CkLibroContrCont'))]);
        rows.push(['- specificare la frequenza e la natura dei controlli: \n' + this.getValueFromKeyWithWhitespace('my_NoteLibroContrCont')]);
        rows.push([tabellaCheck('58 - Il Libro Giornale ed il Libro Inventari sono stati regolarmente istituiti?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInv'))]);
        rows.push(['In caso di risposta affermativa:']);
        rows.push([tabellaCheck('- risultano numerati ed aggiornati?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvAgg'))]);
        rows.push([tabellaCheck('- Per tali ultimi libri risulta versata correttamente l’imposta di bollo?¹', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvBollo'))]);
        rows.push([tabellaCheck('- L’inventario è annualmente sottoscritto dal legale rappresentante dell’ente?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvLR'))]);
        rows.push([
            tabellaCheck(
                '- Sono stati correttamente istituiti ed aggiornati gli altri Libri e Registri Contabili, pur non obbligatori per la disciplina civilistica? IVA Acquisti, IVA vendite, Registro beni ammortizzabili, ecc.)',
                this.getValueFromKeyWithWhitespace('my_CkAltriLibri')
            ),
        ]);
        rows.push([
            format(
                '¹)Le cooperative sociali sono esenti dalla tassa di concessione governativa e dall’imposta di bollo, dovranno indicare nell’ultima pagina del libro rispettivamente “Esente da tassa CC.GG. ai sensi art.18 del D.Lgs. 4/12/1997 n. 460” ed “Esente da imposta di bollo ai sensi art. 17 D.Lgs. n. 460 del 4/12/1997”. \n - Le cooperative edilizie a mutualità prevalente versano ¼ della tassa di concessione governativa (art. 147, lett. f del R.D. 28/04/38 n. 1165), sono inoltre esenti dal pagamento dell’imposta di bollo e dovranno indicare nell’ultima pagina del libro gli estremi di esenzione: “Esente da imposta di bollo ai sensi art. 66 D.L. 331/1993 conv. in L. n. 427 del 29/10/1993” ',
                bold,
                alignment,
                9,
                true
            ),
        ]);
        rows.push(['Eventuali note ed osservazioni sui punti da 52 a 58: \n' + this.getValueFromKeyWithWhitespace('my_NoteOsserLibri')]);
        rows.push([
            tabellaCheck(
                "59 - Il rappresentante dell'ente dichiara che è stato portato a conoscenza dei soci, con le modalità previste dall’art. 17 del D.Lgs. 220/02, l’estratto del verbale della più recente revisione o ispezione? ",
                this.getValueFromKeyWithWhitespace('my_CkVerbConoscSoci')
            ),
        ]);
        rows.push(['- in caso di risposta negativa specificare:' + this.getValueFromKeyWithWhitespace('my_DescVerbConoscSoci')]);
        rows.push(['60 - Altro:' + this.getValueFromKeyWithWhitespace('my_Altro')]);
        rows.push([format('CONCLUSIONI', true, 'center', 10, italics)]);
        rows.push([
            '61 - Giudizio Conclusivo del Revisore ed eventuali altre notizie, anche in ordine a segnalazioni ed esposti in relazione ai quali lo stesso debba in particolare riferire: \n' +
                this.getValueFromKeyWithWhitespace('my_NotaProcedRevisione'),
        ]);
        rows.push(['\n' + this.getValueFromKeyWithWhitespace('my_GiudizioConclusivo')]);
        rows.push([
            '62 - Eventuali suggerimenti e consigli per migliorare la gestione, il livello di democrazia interna, al fine di promuovere la reale partecipazione dei soci alla vita sociale:' +
                this.getValueFromKeyWithWhitespace('my_SuggConsigli'),
        ]);
        rows.push(['63 - Irregolarità sanabili per le quali deve essere irrogata la diffida:']);
        rows.push([this.tabellaIrrSanabili()]);
        rows.push([lineaVuota()]);

        //OSSERVAZIONI E CONTRODEDUZIONI
        rows.push([format('EVENTUALI OSSERVAZIONI E CONTRODEDUZIONI DEL LEGALE RAPPRESENTANTE', true, 'center', 10, italics)]);
        rows.push([
            'Il rappresentante dell’ente è reso edotto della possibilità di presentare, entro 15 giorni dalla dat documentazione dovrà essere prodotta agli Uffici della Pubblica Amministrazione esclusivamente per via informatica a mezzo posta certificata. \n Il rappresentante dichiara:' +
                this.getValueFromKeyWithWhitespace('my_OsservControdeduz'),
        ]);
        rows.push([
            'Il revisore, considerate le risultanze fin qui emerse, ed in particolare quanto indicato ai punti 61, 62 e 63, preso atto delle osservazioni e controdeduzioni del legale rappresentante:',
        ]);
        rows.push([Indent(2, this.tabellaOsservazioniControdeduzioni())]);
        rows.push([
            'La presente sezione del verbale debitamente controfirmata, è redatta in n. ' +
                this.getValueFromKeyWithWhitespace('my_NOriginali') +
                " originali, uno dei quali viene consegnato al rappresentante dell'ente revisionato o, in caso di rifiuto di sottoscrizione, notificato a mezzo posta certificata/raccomandata.",
        ]);
        rows.push([
            "Al rappresentante dell’ente viene ricordato l’obbligo di portare a conoscenza dei soci i contenuti del presente verbale con le modalità di cui all’art. 17 del Decreto legislativo n. 220/2002. \n Ai sensi del combinato disposto del D.Lgs. n. 220/02 e della Legge n. 221/2012, si precisa che gli uffici amministrativi preposti alla vigilanza cooperativa assolvono i compiti loro affidati dalla legge esclusivamente nell'interesse pubblico e che la suddetta vigilanza esplica effetti ed è diretta nei soli confronti delle pubbliche amministrazioni ai fini della legittimazione a beneficiare delle agevolazioni fiscali, previdenziali e di altra natura, nonché per l’adozione dei provvedimenti previsti dall’articolo 12 del citato D.Lgs. n. 220/02.",
        ]);
        rows.push([lineaVuota()]);
        rows.push(['Luogo, ' + this.getValueFromKeyWithWhitespace('my_LuogoCompilaz') + '  data ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilaz'))]);
        rows.push([lineaVuota()]);
        rows.push([this.tabellaOsservazioniControdeduzioniFirme()]);

        this.stampaSchedeSettore(rows, cols);

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '2') {
            //SCHEDA GESTIONE COMMISSARIALE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI GESTIONE COMMISSARIALE', true, 'center', fontSize, italics)]);
            rows.push([
                tabellaCheck('1 - Ci sono reali e concrete prospettive per il perseguimento dello scopo sociale?', this.getValueFromKeyWithWhitespace('my_ckProspettScopoSoc')),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descProspettScopoSoc'))]);
            rows.push([
                tabellaCheck(
                    "2 - Su quali risorse materiali e finanziarie può contare l'ente per la prosecuzione dell'attività sociale?",
                    this.getValueFromKeyWithWhitespace('my_ckRisFinProsecuzioneAttSoc')
                ),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descRisFinProsecuzioneAttSoc'))]);
            rows.push(["3 - Quali iniziative in concreto il Commissario Governativo, una volta nominato, dovrebbe porre in essere per regolarizzare l'ente?"]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descInizCommGovernativo'))]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '3') {
            //SCHEDA PROPOSTA DI SCIOGLIMENTO
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SCIOGLIMENTO', true, 'center', fontSize, italics)]);
            rows.push(["1 - Quali delle fattispecie di cui all'art. 2545 septiesdecies c.c. si ritengono ricorrenti per la cooperativa?"]);
            rows.push([this.tabellaCoopPropostaScioglimento()]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descArt2545Ric'))]);
            rows.push([
                "2 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari?', this.getValueFromKeyWithWhitespace('my_ckBeniImmobili')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione?', this.getValueFromKeyWithWhitespace('my_ckBeniMobiliRegistr')))]);
            rows.push([
                Indent(
                    4,
                    tabellaCheck("- Un valore dell'Attivo che renda necessaria la nomina di un liquidatore?", this.getValueFromKeyWithWhitespace('my_ckValAttivoNecessLiquid'))
                ),
            ]);
            rows.push([Indent(4, 'Specificare: \n' + this.getValueFromKeyWithWhitespace('my_descRisultUltBilApp'))]);
            rows.push([
                tabellaCheck(
                    "3 - Ricorrono entrambi i presupposti di cui all'art. 223 septiesdecies Disp. Att. c.c.?",
                    this.getValueFromKeyWithWhitespace('my_ckPresuppostiArt223')
                ),
            ]);
            rows.push([Indent(4, 'Mancato deposito dei bilanci di esercizio da oltre cinque anni')]);
            rows.push([Indent(4, 'Assenza di:')]);
            rows.push([Indent(6, '- Beni immobiliari')]);
            rows.push([Indent(6, '- Beni mobiliari soggetti a registrazione')]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '4' || this.getValueFromKey('my_CkProvvedimendoDi') == '9') {
            //SCHEDA PROPOSTA SOSTITUZIONE LIQUIDATORE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SOSTITUZIONE DEL LIQUIDATORE O DI CANCELLAZIONE DAL REGISTRO DELLE IMPRESE', true, 'center', fontSize, italics)]);
            rows.push([
                '1 - Descrivere la natura del ritardo nello svolgimento della liquidazione e le eventuali motivazioni addotte dal liquidatore \n ' +
                    this.getValueFromKeyWithWhitespace('my_descNatRitardoLiquid'),
            ]);
            rows.push([lineaVuota()]);
            rows.push(['2 - Descrivere in sintesi le eventuali ulteriori motivazioni della proposta \n' + this.getValueFromKeyWithWhitespace('my_descUlterMotivaz')]);
            rows.push([lineaVuota()]);
            rows.push([tabellaCheck('3 - Sono stati depositati i bilanci relativi agli ultimi cinque esercizi?', this.getValueFromKeyWithWhitespace('my_ckDepBil5Esercizi'))]);
            rows.push([
                "4 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari', this.getValueFromKeyWithWhitespace('my_ckBeniImmob')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione', this.getValueFromKeyWithWhitespace('my_ckBeniMobRegistr')))]);
            rows.push([this.getValueFromKeyWithWhitespace('my_descRisultanzeBil')]);
            rows.push(['5 - Il liquidatore da sostituire è stato a suo tempo nominato:']);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAssSoci')) + " dall'Assemblea dei soci ")]);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAutGiud')) + " dall'Autorità Giudiziaria ")]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '5') {
            //SCHEDA LIQUIDAZIONE COATTA AMMINISTRATIVA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI LIQUIDAZIONE COATTA AMMINISTRATIVA', true, 'center', fontSize, italics)]);
            rows.push(['1 – Descrivere in sintesi le motivazioni della proposta \n ' + this.getValueFromKeyWithWhitespace('my_descMotivoProposta')]);
            rows.push([lineaVuota()]);
            rows.push([this.tabellaCoopLiquidazioneCoattaInterna()]);
            rows.push(['In caso affermativo specificare: \n' + this.getValueFromKeyWithWhitespace('my_descAzAvviate')]);
            rows.push(['3 – Riepilogare le vertenze giudiziarie di cui al punto 51) \n' + this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudiz')]);
            rows.push([
                tabellaCheck(
                    "4 – E' stata allegata una situazione patrimoniale aggiornata, conforme allo schema di cui all’art. 2424 c.c., sottoscritta dal legale rappresentante e dall’Organo di Controllo se presente?",
                    this.getValueFromKeyWithWhitespace('my_ckSituazPatrAggiorn')
                ),
            ]);
            rows.push(['In caso di risposta negativa specificarne i motivi: \n' + this.getValueFromKeyWithWhitespace('my_descSituazPatrAggiorn')]);
            rows.push([
                tabellaCheck(
                    '5 - Risultano mancati pagamenti di mensilità stipendiali od omessi versamenti di contributi previdenziali? ',
                    this.getValueFromKeyWithWhitespace('my_ckOmessoStipContr')
                ),
            ]);
            rows.push(['In caso di risposta positiva specificare: \n' + this.getValueFromKeyWithWhitespace('my_descOmessoStipContr')]);
            rows.push(["6 – Dall'ultimo bilancio e dalla situazione patrimoniale aggiornata emergono i seguenti valori:"]);
            rows.push([this.tabellaCoopLiquidazioneCoattaBilancioPatrimoniale()]);

            //SCHEDA INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIORNATA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('INDICATORI DI BILANCIO DA CALCOLARE SULLA SITUAZIONE PATRIMONIALE AGGIORNATA', true, 'center', fontSize, italics)]);
            rows.push([this.tabellaCoopBilancioSituazionePatrimoniale()]);
            rows.push([
                format(
                    '¹) Al fine di una puntuale determinazione del corretto valore degli indicatori, in questo caso esclusivamente finalizzati a verificare il potenziale stato di insolvenza a breve, l’Attivo Circolante deve essere depurato dei crediti esigibili oltre l’esercizio. ',
                    bold,
                    alignment,
                    8,
                    italics
                ),
            ]);
            rows.push(['- Eventuali osservazioni: \n' + this.getValueFromKeyWithWhitespace('my_descEventOss')]);
        }

        //PARAMETRI NASCOSTI
        rows.push([parametriNascosti(null, this.getValueFromKeyWithWhitespace('md5'), null, null)]);

        // return finale____________________
        return fxTabella(cols, rows);
    }

    private stampaSchedeSettore(rows, cols): void {
        // SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI
        // SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE ---
        // SCHEDE DI SETTORE ---

        console.log('my_ScAgricoleObbl', this.getValueFromKey('my_ScAgricoleObbl'));
        console.log('my_ScAltreCoopObbl', this.getValueFromKey('my_ScAltreCoopObbl'));
        console.log('my_ScConsumoObbl', this.getValueFromKey('my_ScConsumoObbl'));
        console.log('my_ScCoopDettagliObbl', this.getValueFromKey('my_ScCoopDettagliObbl'));
        console.log('my_ScEdilObbl', this.getValueFromKey('my_ScEdilObbl'));
        console.log('my_ScCoopFidiObbl', this.getValueFromKey('my_ScCoopFidiObbl'));
        console.log('my_ScPDLObbl', this.getValueFromKey('my_ScPDLObbl'));
        console.log('my_ScPescaObbl', this.getValueFromKey('my_ScPescaObbl'));
        console.log('my_ScSocObbl', this.getValueFromKey('my_ScSocObbl'));
        console.log('my_ScTrasportoObbl', this.getValueFromKey('my_ScTrasportoObbl'));

        //COOPERATIVE SOCIALI
        if (this.getValueFromKey('my_ScSocObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeSociali(rows);
        }
        //COOPERATIVE PRODUZIONE E LAVORO
        if (this.getValueFromKey('my_ScPDLObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeProduzioneLavoro(rows);
        }
        //COOPERATIVE AGRICOLE / LAVORO AGRICOLO
        if (this.getValueFromKey('my_TipoSezEnte') === '6') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeAgricole1(rows);
        }
        //COOPERATIVE AGRICOLE / ALLEVAMENTO
        if (this.getValueFromKey('my_TipoSezEnte') === '14') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeProdAgricoliAllevamento(rows);
        }
        //SCHEDA SETTORE CONSORZI AGRARI
        if (this.getValueFromKey('my_TipoSezEnte') === '7') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreConsorziAgrari(rows);
        }
        //COOPERATIVE TRASPORTO
        if (this.getValueFromKey('my_ScTrasportoObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeTrasporto(rows);
        }
        //COOPERATIVE PESCA
        if (this.getValueFromKey('my_ScPescaObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativePesca(rows);
        }
        //COOPERATIVE ALTRE COOPERATIVE
        if (this.getValueFromKey('my_ScAltreCoopObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreAltreCooperative(rows);
        }
        //COOPERATIVE DI CONSUMO
        if (this.getValueFromKey('my_ScConsumoObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeConsumo(rows);
        }
        //COOPERATIVE DI ABITAZIONE E LORO CONSORZI
        if (this.getValueFromKey('my_SezEnte') === '5') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeAbitazione(rows);
        }
        //SCHEDA SETTORE COOPERATIVE O CONSORZI GARANZIA FIDI
        if (this.getValueFromKey('my_ScCoopFidiObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeGaranziaFidi(rows);
        }
        //SCHEDA SETTORE COOPERATIVE DI DETTAGLIANTI
        if (this.getValueFromKey('my_ScCoopDettagliObbl') === '1') {
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeDettaglianti(rows);
        }
    }

    private generateRilevazioneEstratto(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        // SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE ---
        // SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE --- SEZIONE I - RILEVAZIONE ---

        //INTESTAZIONE
        rows.push([intestazioneLogoConf()]);
        //rows.push([intestazioneTitoloMinisteriale()])

        rows.push([format('Confederazione Cooperative Italiane', true, 'center', 12)]);
        rows.push([lineaVuota()]);
        rows.push([format('Associazione nazionale di rappresentanza, assistenza, tutela e revisione del movimento cooperativo', true, 'center', 10)]);
        rows.push([format('riconosciuta giuridicamente con D.M. 12 Aprile 1948 ai sensi del D.L.C.P.S. 14 Dicembre 1947, n. 1577', true, 'center', 10)]);
        rows.push([format('SEDE CENTRALE: Via Torino, 146 - 00184 Roma', true, 'center', 10)]);
        rows.push([lineaVuota()]);
        rows.push([format('VIGILANZA SUGLI ENTI COOPERATIVI AI SENSI DEL D.Lgs. 2 AGOSTO 2002 N.220', true, 'center', 11)]);
        rows.push([lineaVuota()]);
        rows.push([format('VERBALE DI REVISIONE', true, 'center', 11)]);
        rows.push([format('(conforme al modello approvato con D.M. 12/06/2017 del Ministero delle Imprese e del Made in Italy)', false, 'center', fontSize, italics)]);
        rows.push([lineaVuota()]);
        rows.push([format('SEZIONE I - Rilevazione', true, 'center', 10, true)]);

        //DATI IDENTIFICATIVI
        rows.push([format('DATI IDENTIFICATIVI', false, 'center')]);
        rows.push([
            doubleFormat(
                'Biennio di revisione: ',
                true,
                false,
                10,
                this.getValueFromKeyWithWhitespace('my_Anno1') + '/' + this.getValueFromKeyWithWhitespace('my_Anno2'),
                false,
                false,
                10
            ),
        ]);

        rows.push([
            doubleFormat(
                'Anno di revisione ',
                true,
                false,
                10,
                '(se soggetta a revisione annnuale) ' +
                    this.getValueFromKeyWithWhitespace('my_AnnoRevisionePerAnnuali') +
                    ' Revisione eseguita dal ' +
                    this.formatDate(this.getValueFromKeyWithWhitespace('my_DataRevisioneDal')) +
                    ' al ' +
                    this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilaz')),
                false,
                false,
                10
            ),
        ]);
        rows.push([
            format('Data delle eventuali dichiarazioni sostitutive prodotte (art.6, comma 6, D.Lgs. 220/02): ' + this.getValueFromKeyWithWhitespace('my_DataDichSost'), true),
        ]);
        rows.push([this.fxDatiIdentificativi()]);
        rows.push([
            "Rappresenta l'ente cooperativo, nel corso della revisione, il Sig. " +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopCognome') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_RappresentanteCoopNome') +
                ', in qualità di*: ' +
                this.getValueFromKeyWithWhitespace('my_QualitaRappres') +
                ', identificato con documento ' +
                this.getValueFromKeyWithWhitespace('my_DocumRappres') +
                ', (in caso di delega allegare la stessa e copia del documento del legale rappresentante)',
        ]);

        rows.push(['assistito da: ' + this.getValueFromKeyWithWhitespace('my_AssistRappres')]);

        rows.push([
            format(
                '* Nel caso in cui il delegato del legale rappresentante non fornisca esaustivamente le notizie richieste il revisore deve esigere la presenza del legale rappresentante.',
                false,
                'left',
                8,
                false
            ),
        ]);

        rows.push([
            'Redige il presente verbale il Sig. ' +
                this.getValueFromKeyWithWhitespace('my_Revisore') +
                ' iscritto al n. ' +
                this.getValueFromKeyWithWhitespace('my_NIscrizRevis') +
                " dell'elenco tenuto presso il Ministero delle Imprese e del Made in Italy (art. 7, c. 6, D.L.gs. 220/02), in qualità di revisore incaricato da " +
                this.getValueFromKeyWithWhitespace('my_IncaricRevis') +
                ' presso (luogo dove si svolge la revisione (specificare se sede legale, ufficio del revisore od altro luogo concordato) : \n' +
                this.getValueFromKeyWithWhitespace('my_LuogoRilevazione'),
        ]);

        rows.push([lineaVuota()]);

        //NATURA MUTUALISTICA
        rows.push([format('NATURA MUTUALISTICA E ATTIVITÀ SVOLTA', true, 'center', 11)]);
        rows.push([format("REQUISITI INERENTI L'ATTO COSTITUTIVO E LO STATUTO", true, 'left', 10)]);
        rows.push(['Lo statuto prevede:']);
        rows.push([tabellaCheck('1 - lo scopo mutualistico (art. 2511 c.c.)', this.getValueFromKeyWithWhitespace('my_CkScopoMutual'))]);
        rows.push(['- in caso negativo specificare:']);
        rows.push([this.getValueFromKeyWithWhitespace('my_DescScopoMutual')]);
        rows.push([
            tabellaCheck(
                "2 - l'obbligo della destinazione degli utili netti annuali a riserva legale nella misura prevista dalla legge?",
                this.getValueFromKeyWithWhitespace('my_CkObblDestinazUtil')
            ),
        ]);
        rows.push([
            tabellaCheck(
                "3 - l'obbligo della devoluzione di una quota di utili netti annuali ai fondi mutualistici per la promozione e lo sviluppo della cooperazione (art. 2545 quater, comma 2) ?",
                this.getValueFromKeyWithWhitespace('my_CkObbDevolUtili')
            ),
        ]);
        rows.push([tabellaCheck("4 - i requisiti della mutualità prevalente di cui all'art. 2514 c.c. ?", this.getValueFromKeyWithWhitespace('my_CkReqMutualPrev'))]);
        rows.push(['- ovvero in caso di cooperative a mutualità non prevalente (ex art. 2545 quinquies c.c.)']);
        rows.push([
            tabellaCheck(
                Indent(2, '- le modalità e la percentuale massima di ripartizione dei dividendi tra i soci cooperatori?'),
                this.getValueFromKeyWithWhitespace('my_CkMutuaDivid')
            ),
        ]);
        rows.push([
            tabellaCheck(Indent(2, "- l'assegnazione ai soci, da parte dell'assemblea, delle riserve divisibili?"), this.getValueFromKeyWithWhitespace('my_CkMutuaRiserve')),
        ]);
        rows.push([
            tabellaCheck(
                "5 - la possibilità di ammettere il nuovo socio cooperatore in una categoria speciale in ragione dell'interesse alla sua formazione ovvero del suo inserimento nell'impresa, determinandone il termine massimo di permanenza, i diritti e gli obblighi?",
                this.getValueFromKeyWithWhitespace('my_CkNuovoSocioCatSpec')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '6 - la possibilità che la società svolga la propria attività anche con terzi ex art. 2512 c.c.?',
                this.getValueFromKeyWithWhitespace('my_CkAttivConTerzi')
            ),
        ]);
        rows.push([tabellaCheck('7 - la definizione dei criteri per la ripartizione dei ristorni?', this.getValueFromKeyWithWhitespace('my_CkDefCriteriRistorni'))]);
        rows.push([
            tabellaCheck(
                '8 - assemblee separate ex art.2540 c.c., per specifiche materie ovvero particolari categorie di soci? ',
                this.getValueFromKeyWithWhitespace('my_CkAssSeparate')
            ),
        ]);
        rows.push([tabellaCheck('9 - assemblee speciali dei possessori degli strumenti finanziari ex art. 2541 cc?', this.getValueFromKeyWithWhitespace('my_CkAssSpeciali'))]);
        rows.push([lineaVuota()]);
        rows.push([
            tabellaCheck(
                "Alla luce di quanto sopra e dall'esame dello stesso, lo Statuto è conforme alle previsioni normative?",
                this.getValueFromKeyWithWhitespace('my_CkStatutoConforme')
            ),
        ]);
        rows.push(['eventualmente specificare:' + this.getValueFromKeyWithWhitespace('my_StatConfNorm')]);
        rows.push([format("REQUISITI INERENTI L'ATTIVITÀ", true, 'left', 10)]);
        rows.push(["Eventuali osservazioni sull'effettivo rispetto delle previsioni Statutarie: \n" + this.getValueFromKeyWithWhitespace('my_OssRispettoPrevis')]);
        rows.push(["10 - Descrizione dell'oggetto sociale e dell'attività effettivamente svolta: \n"]);
        rows.push(['a) oggetto sociale come risultante dallo statuto: ' + this.getValueFromKeyWithWhitespace('my_OggSocStatuto')]);
        rows.push(['b) attività svolta in concreto: \n' + this.getValueFromKeyWithWhitespace('my_AttivSvoltaConcreta')]);
        rows.push([
            tabellaCheck(
                Indent(1, "- l'attività effettivamente svolta e l'oggetto sociale sono coerenti tra loro e con la categoria di iscrizione nell'Albo?"),
                this.getValueFromKeyWithWhitespace('my_CkAttivEffettSvolta')
            ),
        ]);
        rows.push([Indent(2, ' - in caso di risposta negativa, specificare: \n' + this.getValueFromKeyWithWhitespace('my_AttivEffettSvolta'))]);
        rows.push([
            tabellaCheck(
                "11  - Oltre all'eventuale Regolamento ex art. 6 della legge n. 142/01, esistono altri regolamenti interni che disciplinano aspetti della vita cooperativa e/o le modalità di attuazione dello scambio mutualistico? ",
                this.getValueFromKeyWithWhitespace('my_CkRegolamInterni')
            ),
        ]);
        rows.push([
            tabellaCheck(
                Indent(2, " - In caso di risposta affermativa, sono redatti ed approvati in conformità dell'ultimo comma dell'art. 2521 c.c. ?"),
                this.getValueFromKeyWithWhitespace('my_CkRegolamInterniConformi')
            ),
        ]);
        rows.push([Indent(2, ' - Descrivere gli aspetti oggetto di regolamentazione:' + this.getValueFromKeyWithWhitespace('my_AspettiOggRegolam'))]);
        rows.push([
            tabellaCheck(
                "12 - Nella costituzione e nell'esecuzione dei rapporti mutualistici è rispettato il principio di parità di trattamento tra i soci cooperatori?",
                this.getValueFromKeyWithWhitespace('my_CkParitaSociCoop')
            ),
        ]);
        rows.push([
            Indent(2, ' - Specificare le modalità e, in caso di risposta negativa, indicarne le motivazioni: ' + this.getValueFromKeyWithWhitespace('my_DescParitaSociCoop')),
        ]);
        rows.push([
            tabellaCheck(
                "13 - Il sistema amministrativo dell'ente consente di distinguere lo scambio mutualistico con i soci rispetto ai rapporti con terzi?",
                this.getValueFromKeyWithWhitespace('my_CkDistScambioMutual')
            ),
        ]);
        rows.push(['Specificare, indicandone le modalità e le eventuali carenze: ' + this.getValueFromKeyWithWhitespace('my_DescScambioMutual')]);
        rows.push([
            tabellaCheck(
                "14 - Nel Bilancio è documentata la condizione di prevalenza con l'evidenziazione dei parametri di cui all'art. 2513 c.c. ? ",
                this.getValueFromKeyWithWhitespace('my_CkCondizPrevNotaIntegr')
            ),
        ]);
        rows.push(['Se necessario specificare:' + this.getValueFromKeyWithWhitespace('my_DescCondizPrevNotaIntegr')]);

        //SCHEDA CONTROLLO VERIFICA PREVALENZA
        rows.push([fxTabellaEsternaBordo(this.fxVerificaPrevalenza())]);
        rows.push([
            tabellaCheck(
                "- L'attività dell'ente viene svolta in concreto prevalentemente in favore dei soci, ai sensi degli art. 2512 e 2513 c.c.?",
                this.getValueFromKeyWithWhitespace('my_CkAttPrevalSoci')
            ),
        ]);
        rows.push([
            tabellaCheck(
                Indent(2, ' - In caso di risposta negativa, la cooperativa è prevalente di diritto o rientra in uno dei regimi derogatori?'),
                this.getValueFromKeyWithWhitespace('my_CkRientraRegDerogatori')
            ),
        ]);
        rows.push([Indent(4, 'Specificare:' + this.getValueFromKeyWithWhitespace('my_DescRegimeDerogatorioProvalenza'))]);
        rows.push([doubleFormat('15 - ', false, false, 10, 'In caso di cooperativa a mutualità non prevalente:', true, false, 10)]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di dividendi tra i soci?', this.getValueFromKeyWithWhitespace('my_CkEsisteRipDividSociMutNP'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva sono state rispettate le previsioni di cui all'art. 2545 quinquies?",
                this.getValueFromKeyWithWhitespace('my_CkRipDividSociMutNP')
            ),
        ]);
        rows.push([tabellaCheck('- sono state costituite riserve divisibili?', this.getValueFromKeyWithWhitespace('my_CkCostituiteRiserveDivisibili'))]);
        rows.push([tabellaCheck('- in caso di risposta positiva sono gestite separatamente?', this.getValueFromKeyWithWhitespace('my_CkRiserveIndivis'))]);
        rows.push([tabellaCheck('- la gestione separata è riportata in Nota Integrativa?', this.getValueFromKeyWithWhitespace('my_CkGestSeparata'))]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di riserve?', this.getValueFromKeyWithWhitespace('my_CkRiserveDivisibiliRipartite'))]);
        rows.push([
            tabellaCheck("- sono state rispettate le previsioni di cui all'art. 2545 quinquies?", this.getValueFromKeyWithWhitespace('my_CkRiserveDivisibiliRispetPrevision')),
        ]);
        rows.push([doubleFormat('16 - ', false, false, 10, 'In caso di cooperativa a mutualità prevalente', true, false, 10)]);
        rows.push([
            tabellaCheck(
                '- i criteri di prevalenza ex art. 2513 sono stati rispettati in almeno uno degli ultimi due esercizi?',
                this.getValueFromKeyWithWhitespace('my_CkRispCritPrev')
            ),
        ]);
        rows.push([tabellaCheck('- sono state effettuate ripartizioni di dividendi tra i soci? ', this.getValueFromKeyWithWhitespace('my_CkSonoStatiRipDividSociMutP'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva sono state rispettate le previsioni di cui all'art. 2514 c.c. lettere a)?",
                this.getValueFromKeyWithWhitespace('my_CkRipDividSociMutP')
            ),
        ]);
        rows.push([tabellaCheck('- sono stati emessi strumenti finanziari?', this.getValueFromKeyWithWhitespace('my_CkEmessoStrumFinanziari'))]);
        rows.push([
            tabellaCheck(
                "- in caso di risposta positiva, sono rispettate le previsioni di cui all'art. 2514 c.c. lettera b)?",
                this.getValueFromKeyWithWhitespace('my_CkRispettoGestStrumFinanziari')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '17 - In base alle risposte fornite ai punti precedenti, nonché dalle verifiche di cui alla scheda di controllo sulla prevalenza, la cooperativa può essere considerata a mutualità prevalente?',
                this.getValueFromKeyWithWhitespace('my_CkConsidMutualPrev')
            ),
        ]);

        if (Utilities.convertStringToNumber(this.getValueFromKey('my_CkCambiatoSezione')) === 1) {
            rows.push([
                tabellaCheck(
                    '18 - In caso di perdita o di riacquisto della qualifica di prevalenza la cooperativa ha correttamente ottemperato alle disposizioni di legge? *',
                    this.getValueFromKeyWithWhitespace('my_CkAdempiutoProcedureCambioSez')
                ),
            ]);
        }

        rows.push([
            format(
                "(*) Si ricorda che, in base all'art. 2545 octies c.c., sia in caso di perdita che di riacquisto della qualifica di prevalenza, la cooperativa è tenuta a segnalare tali condizioni all'Albo e, in caso di modifica delle previsioni statutarie di cui all'articolo 2514 c.c. o di emissione di strumenti finanziari, a provvedere anche alla redazione dell'apposito bilancio.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push(['- Note ed osservazioni:' + this.getValueFromKeyWithWhitespace('my_NatMutNote')]);

        //CATEGORIE SOCI E BASE SOCIALE
        rows.push([format('CATEGORIE DEI SOCI ED EFFETTIVITÀ DELLA BASE SOCIALE', true, 'left', 10, italics)]);
        rows.push(['19 - Categorie di soci presenti:']);
        rows.push([this.tabellaSociPresenti()]);
        rows.push([
            tabellaCheck(
                "20 - La procedura seguita per l'ammissione, il recesso e l'esclusione dei soci, è conforme alle previsioni di Legge e di Statuto?",
                this.getValueFromKeyWithWhitespace('my_CkProcedConforme')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecProcedConforme')]);
        rows.push([
            tabellaCheck(
                "- ai sensi dell'art. 2527 c.c., i requisiti dei soci sono coerenti con l'attività svolta dalla cooperativa?",
                this.getValueFromKeyWithWhitespace('my_CkReqSociCoerenti')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecReqSociCoerenti')]);
        rows.push([
            tabellaCheck(
                "21 - In presenza di soci ammessi alla categoria speciale, è rispettato il limite di cui all'art. 2527 c.c.? ",
                this.getValueFromKeyWithWhitespace('my_CkSociSpecLimite')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '- al termine di un periodo comunque non superiore a cinque anni i soci appartenenti alla categoria speciale sono ammessi a godere dei diritti che spettano agli altri soci cooperatori. ',
                this.getValueFromKeyWithWhitespace('my_CkSociSpecDiritti')
            ),
        ]);
        rows.push(['- in caso di risposta negativa, specificare: ' + this.getValueFromKeyWithWhitespace('my_SocSpecNota')]);
        rows.push([this.tabellaTreUnitaSociale()]);
        rows.push([lineaVuota()]);

        //BILANCIO
        rows.push([format('BILANCIO', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                '23 - Lo schema del bilancio approvato (stato patrimoniale, rendiconto finanziario, conto economico, nota integrativa) è conforme a quanto previsto dal Codice Civile?',
                this.getValueFromKeyWithWhitespace('my_CkConformSchemaBil')
            ),
        ]);
        rows.push([tabellaCheck('24 - Sono stati rispettati i termini di approvazione del bilancio?', this.getValueFromKeyWithWhitespace('my_CkRispTermAppBil'))]);
        rows.push([
            tabellaCheck("25 - L'ultimo bilancio approvato risulta regolarmente depositato al Registro delle Imprese?", this.getValueFromKeyWithWhitespace('my_CkBilRegDeposURI')),
        ]);
        rows.push([tabellaCheck("26 - L'ente cooperativo è tenuto alla certificazione di bilancio? ", this.getValueFromKeyWithWhitespace('my_CkEnteTenutoCertBil'))]);
        rows.push([
            tabellaCheck(
                Indent(1, '- in caso di risposta affermativa provvede regolarmente ex comma 2, art. 15 della Legge 59/92?'),
                this.getValueFromKeyWithWhitespace('my_CkProvvedeCertBil')
            ),
        ]);
        rows.push(['Ragione sociale della Società di Revisione incaricata:' + this.getValueFromKeyWithWhitespace('my_RagSocRevIncaricataDesc')]);
        rows.push(["27 - Qual è l'importo del patrimonio netto risultante dal bilancio negli ultimi 2 esercizi?"]);
        rows.push([this.miniTabDoppiaBilancio()]);
        rows.push(['28 - Capitale Sociale ']);
        rows.push([this.miniTabQuattroBilancio()]);
        rows.push([this.miniTabDoppiaCapSociale()]);
        rows.push([fxTabellaEsternaBordo(this.tabellaControlloRistorni())]);
        rows.push([
            format(
                "1) Considerata la soppressione della sezione “E” del conto economico, nel calcolo dell'avanzo della gestione mutualistica non si dovrà tener conto degli elementi di entità o incidenza eccezionali che devono essere indicati in nota integrativa, a condizione che non siano attinenti allo scambio mutualistico tra soci e società, quali, a titolo esemplificativo, quelli relativi alle plusvalenze e minusvalenze di natura straordinaria derivanti da trasferimenti d'azienda o di rami d'azienda, alienazione di partecipazioni, immobili e beni non strumentali, nonché al plusvalore derivante dall'acquisizione delle immobilizzazioni materiali a titolo gratuito.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push([tabellaCheck('30 - Le eventuali ristorni risultano correttamente determinati e ripartiti?', this.getValueFromKeyWithWhitespace('my_CkRistorni'))]);
        rows.push([tabellaCheck("31 - L'ente cooperative raccoglie il prestito dai soci?", this.getValueFromKeyWithWhitespace('my_CkPrestitoSoci'))]);
        rows.push([fxTabellaEsternaBordo(this.tabellaVerificaContabilePrestito())]);
        rows.push([Indent(2, '- in caso di risposta affermativa:')]);
        rows.push([tabellaCheck(Indent(2, '- è rispettato il limite individuale?'), this.getValueFromKeyWithWhitespace('my_CkRispettatoLimiteIndivid'))]);
        rows.push([
            tabellaCheck(Indent(2, '- in caso di cooperative con più di 50 soci, è rispettato il rapporto CICR?'), this.getValueFromKeyWithWhitespace('my_CkRipettatoCICR')),
        ]);
        rows.push([
            tabellaCheck(
                '32 - In base alle risposte fornite ai punti precedenti, nonché dalle verifiche di cui alla scheda di controllo sul prestito da soci la raccolta del prestito è svolta correttamente?',
                this.getValueFromKeyWithWhitespace('my_CkPrestitoSociCorretto')
            ),
        ]);
        rows.push([
            tabellaCheck("33 - La società è stata destinataria di contributi pubblici dalla data dell'ultima revisione?", this.getValueFromKeyWithWhitespace('my_CkContribPubb')),
        ]);
        rows.push([' - Descrizione:' + this.getValueFromKeyWithWhitespace('my_DescContribPubb')]);
        rows.push([this.tabellaEntiErogatori()]);
        rows.push([tabellaCheck('34 - La cooperativa redige il Bilancio Sociale?', this.getValueFromKeyWithWhitespace('my_CkRedazBilSociale'))]);
        rows.push([Indent(2, '- Eventualmente specificare:' + this.getValueFromKeyWithWhitespace('my_NoteBilSociale'))]);
        rows.push([tabellaCheck("35 - L'ente è tenuto alla redazione del bilancio consolidato?", this.getValueFromKeyWithWhitespace('my_CkRedazBilConsol'))]);
        rows.push([Indent(2, 'In caso di risposta affermativa:')]);
        rows.push([Indent(2, tabellaCheck('- viene regolarmente redatto?', this.getValueFromKeyWithWhitespace('my_CkRegolRedBilConsol')))]);
        rows.push([Indent(2, tabellaCheck('- viene regolarmente depositato presso il Registro delle Imprese?', this.getValueFromKeyWithWhitespace('my_CkRegolDepBilConsol')))]);
        rows.push(['36 - Società partecipate']);
        rows.push([this.tabellaSocietaPartecipate()]);
        rows.push([
            tabellaCheck(
                "37 - Dall'ultima revisione l'ente ha presentato le dichiarazioni fiscali (mod. Redditi, Mod. 770, Dichiarazione IVA, IRAP)?",
                this.getValueFromKeyWithWhitespace('my_CK37DichFiscPresDaUltRev')
            ),
        ]);
        rows.push(['In caso di risposta negativa specificare:' + this.getValueFromKeyWithWhitespace('my_DescDichFiscPresDaUltRev')]);
        rows.push(['38 - Eventuali note ed osservazioni inerenti i punti da 23 a 37:' + this.getValueFromKeyWithWhitespace('my_NoteEdOsserv')]);

        //RAGGIUNGIMENTO DELLO SCOPO SOCIALE
        rows.push([format('RAGGIUNGIMENTO DELLO SCOPO SOCIALE', true, 'center', 10, italics)]);
        rows.push(["39 - Capacità dell'ente cooperativo di raggiungere gli scopi per cui è stato costituito:" + this.getValueFromKeyWithWhitespace('my_CapacitaRaggScopi')]);
        rows.push([
            '40 - Eventi non attinenti alla gestione caratteristica che hanno provocato sensibili conseguenze sulla situazione economica, finanziaria e patrimoniale della società:' +
                this.getValueFromKeyWithWhitespace('my_EventiNonAttinenti'),
        ]);
        rows.push([doubleFormat('41 - ', false, false, 10, 'INDICATORI DI BILANCIO', true, false, 10, 'center')]);
        rows.push([this.tabellaIndicatoriBilancio()]);
        rows.push([
            '42 - Eventuali note e osservazioni sulla situazione aziendale con particolare riferimento alle risultanze di cui ai punti 39, 40 e 41:' +
                this.getValueFromKeyWithWhitespace('my_NoteSituazAziendale'),
        ]);

        rows.push([newPage()]);

        //SISTEMA AMMINISTRAZIONE E CONTROLLO
        rows.push([format('SISTEMA DI AMMINISTRAZIONE E DI CONTROLLO (*)', bold, 'center', 10, italics)]);
        rows.push([this.tabellaSistemaAmministrazioneControllo()]);
        rows.push([format('Il revisore verificherà l’effettività degli organi sociali ed il loro regolare funzionamento', bold, alignment, 9, true)]);
        rows.push([format('(*) N.B. Allegare solo la scheda relativa al sistema utilizzato', bold, alignment, 9, true)]);

        //SCHEDE AMMINISTRAZIONE E CONTROLLO

        let schContr = Number(this.getValueFromKey('my_CkNorme')); //NaN

        if (!isNaN(schContr)) {
            if (schContr > 0) {
                rows.push([this.creaSchedaAmmControllo(schContr)]);
            }
        }
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(2)])
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(3)])
        //rows.push([newPage()])
        //rows.push([creaSchedaAmmControllo(4)])

        rows.push([tabellaCheck('43 - Gli organi societari sono correttamente costituiti?', this.getValueFromKeyWithWhitespace('my_CkOrgSocietCorrCostit'))]);
        rows.push([tabellaCheck('44 - Funzionano correttamente?', this.getValueFromKeyWithWhitespace('my_CkOrgSocietCorrFunz'))]);
        rows.push(['45 - Note ed osservazioni \n' + this.getValueFromKeyWithWhitespace('my_NoteEdOsservazioni')]);
        rows.push([
            '46 - Eventuali note ed osservazioni sulla natura mutualistica dell’ente cooperativo sotto il profilo formale e sostanziale, sull’effettività e la composizione della base sociale e sulla reale partecipazione dei soci alla vita sociale:' +
                this.getValueFromKeyWithWhitespace('my_NoteNatMutEnte'),
        ]);
        rows.push([lineaVuota()]);

        //CONTRIBUTO BIENNALE
        rows.push([format('CONTRIBUTO BIENNALE', true, 'center', 10, italics)]);
        rows.push([lineaVuota()]);
        rows.push(['47 -  Calcolo del contributo relativo al Biennio ' + this.getValueFromKeyWithWhitespace('my_Anno1') + ' / ' + this.getValueFromKeyWithWhitespace('my_Anno2')]);

        rows.push([this.tabellaRiferimentoContributoBiennale()]);
        rows.push([this.tabellaRiferimentoContributoBiennaleImporti()]);
        rows.push([' - Maggiorazione del 10 per cento (per gli enti cooperativi edilizi)³ dovuta € ' + this.getValueFromKeyWithWhitespace('my_MaggEntiEdilDovuta')]);
        rows.push([' - Maggiorazione del 10 per cento (per gli enti cooperativi edilizi)* versata € ' + this.getValueFromKeyWithWhitespace('my_MaggEntiEdilVersata')]);
        rows.push([' - Data scadenza da cui far decorrere il calcolo delle penalità: ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataScadCalcPenal'))]);
        rows.push([
            format(
                "¹) Se l'ente si è posto in liquidazione prima della scadenza di pagamento del contributo assolverà lo stesso per l'importo minimo, altrimenti l'importo dovuto sarà quello relativo alla fascia contributiva normale. \n ²) Nelle cooperative edilizie il fatturato è determinato prendendo come riferimento il maggior valore tra l’incremento di valore dell’immobile rilevato nelle voci B-2 e C-1 dello Stato patrimoniale (art. 2424 cc.) e il valore della produzione di cui alla lettera A) dell’art.2425 cc.” \n ³) Maggiorazione comunque dovuta alla P.A. anche da parte delle cooperative edilizie aderenti ad Associazioni Nazionali di Rappresentanza e dalle stesse vigilate (il relativo versamento deve effettuarsi a mezzo mod. F24). \n *) Al fine della determinazione del contributo per le cooperative edilizie, iscritte al relativo Albo con programma avviato, si precisa che con delibera del 29/02/2016 il Comitato per l’Albo ha individuato nell’”Inizio Lavori” l’avvio effettivo degli stessi, a seguito dell’ottenimento del permesso di costruire e la comunicazione all’Ufficio Tecnico del Comune che ne attesta l’inizio.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);
        rows.push([tabellaCheck('48 - Risultano irregolarità contributive pregresse dall’ultima revisione?', this.getValueFromKeyWithWhitespace('my_CkContrBiennIrrUltRev'))]);
        rows.push(['49 - Note ed osservazioni sul contributo biennale: \n ' + this.getValueFromKeyWithWhitespace('my_NoteContrBienn')]);
        rows.push([format('CONTRIBUTO AI SENSI DELL’ART. 11 DELLA LEGGE 59/92', true, 'center', 10, italics)]);
        rows.push([fxTabellaEsternaBordo(this.tabellaDevoluzioneContributi())]);
        rows.push([
            tabellaCheck(
                '50 - In ordine alla devoluzione ai fondi mutualistici ex art. 11 della legge n. 59/92, l’ente cooperativo ha correttamente effettuato i versamenti eventualmente dovuti?',
                this.getValueFromKeyWithWhitespace('my_CkVersamEffett')
            ),
        ]);
        rows.push([
            Indent(4, tabellaCheck('- Risultano irregolarità contributive pregresse dalla data dell’ultima revisione?', this.getValueFromKeyWithWhitespace('my_CkIrregPregL59'))),
        ]);
        rows.push(['- Eventualmente specificare: \n ' + this.getValueFromKeyWithWhitespace('my_DescVersamEffett')]);
        rows.push([format('ALTRE NOTIZIE', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                '51 - Il legale rappresentante dichiara la sussistenza di vertenze giudiziarie particolarmente rilevanti in corso?',
                this.getValueFromKeyWithWhitespace('my_CkVertenzeGiudiz')
            ),
        ]);
        rows.push([
            'Quali? (indicare sinteticamente parti in causa, oggetto e stato degli atti e gli eventuali rischi sulla situazione patrimoniale ed economica): \n' +
                this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudiz'),
        ]);
        rows.push(['Specificare il complessivo presumibile importo, se quantificabile: € ' + this.getValueFromKeyWithWhitespace('my_VertenzeGiudizImpPres')]);

        //LIBRI SOCIALI
        rows.push([lineaVuota()]);
        rows.push([format('LIBRI SOCIALI', true, 'center', 10, italics)]);
        rows.push([
            tabellaCheck(
                doubleFormat('52 ', true, italics1, fontSize1, '- I Libri sociali obbligatori sono stati regolarmente istituiti? ', bold2, italics2, fontSize2, alignment),
                this.getValueFromKeyWithWhitespace('my_CkLibriSociali')
            ),
        ]);
        rows.push(['In caso di risposta affermativa: ']);
        rows.push([tabellaCheck('- risultano correttamente bollati e vidimati inizialmente?', this.getValueFromKeyWithWhitespace('my_CkLibriSocBolloVid'))]);
        rows.push([
            tabellaCheck('- risultano versate correttamente l’imposta di bollo e la tassa di concessione governativa?', this.getValueFromKeyWithWhitespace('my_CkLibriSocTassa')),
        ]);
        rows.push([lineaVuota()]);
        rows.push([format('53 - LIBRO DEI SOCI [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([
            tabellaCheck(
                '- In ciascuna sezione è correttamente riportata: la numerazione progressiva, l’anagrafica completa del socio, la data di ammissione nella compagine societaria, la qualifica del socio, la quota sociale sottoscritta, quella versata, la data di recesso o esclusione, la quota restituita?',
                this.getValueFromKeyWithWhitespace('my_CkLibroSociDati')
            ),
        ]);
        rows.push(['- se necessario specificare: \n ' + this.getValueFromKeyWithWhitespace('my_NoteLibroSoci')]);
        rows.push([lineaVuota()]);
        rows.push([format('54 - LIBRO DELLE ASSEMBLEE / DECISIONI DEI SOCI [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([
            tabellaCheck(
                '- E’ correttamente trascritto il verbale dell’Assemblea eventualmente andata deserta in prima convocazione o, comunque, nei verbali d’Assemblea viene evidenziato se trattasi di convocazioni successive?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssVerb')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '- I verbali sono redatti con la necessaria chiarezza, tale da rendere possibile determinare i quorum costitutivi e deliberativi, i presenti in proprio o per delega, i voti dagli stessi espressi?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssRedaz')
            ),
        ]);
        rows.push([tabellaCheck('- I verbali sono sottoscritti dal Presidente e dal Segretario dell’Assemblea?', this.getValueFromKeyWithWhitespace('my_CkLibroAssVerbSott'))]);
        rows.push([
            tabellaCheck(
                '- Nei verbali in cui si approva il bilancio, ovvero regolamenti o modifiche statutarie tali documenti vengono correttamente trascritti?',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssTrascritti')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroAssemb')]);
        rows.push([
            tabellaCheck(
                'In caso di convocazione di Assemblee separate o speciali, sono stati istituiti i libri relativi',
                this.getValueFromKeyWithWhitespace('my_CkLibroAssSpeciali')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroAssSpeciali')]);
        rows.push([lineaVuota()]);
        rows.push([
            format("55 - LIBRO DELL’ORGANO AMMINISTRATIVO (Collegiale, Plurale, Monocratico)\novvero - DELL'ORGANO LIQUIDATORIO [OBBLIGATORIO]", true, alignment, 11, italics),
        ]);
        rows.push([
            tabellaCheck(
                '- Risultano trascritte le delibere/determinazioni relative ai principali fatti gestionali nonché quelle relative all’ammissione, al recesso ed all’esclusione dei soci?',
                this.getValueFromKeyWithWhitespace('my_CkLibroOrgAmm')
            ),
        ]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroOrgAmm')]);
        rows.push([lineaVuota()]);
        rows.push([format('56 - LIBRO DELL’ORGANO DI CONTROLLO (Collegiale, Monocratico) [OBBLIGATORIO]', true, alignment, 11, italics)]);
        rows.push([tabellaCheck('- Risultano trascritti i verbali delle riunioni/verifiche da tenersi ogni 90 giorni?', this.getValueFromKeyWithWhitespace('my_CkLibroOrgContr'))]);
        rows.push([tabellaCheck('- E’ correttamente indicata la natura dei controlli effettuati?', this.getValueFromKeyWithWhitespace('my_CkLibroOrgContrNat'))]);
        rows.push(['- se necessario specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteLibroOrgContr')]);
        rows.push([format('57 - LIBRO DELL’ORGANO DI CONTROLLO CONTABILE [FACOLTATIVO]', true, alignment, 11, italics)]);
        rows.push([Indent(2, format('(Revisore - Società di Revisione) ', true, alignment, 10, italics))]);
        rows.push([tabellaCheck('- E’ stato istituito il libro dell’Organo di Controllo Contabile?', this.getValueFromKeyWithWhitespace('my_CkLibroContrCont'))]);
        rows.push(['- specificare la frequenza e la natura dei controlli: \n' + this.getValueFromKeyWithWhitespace('my_NoteLibroContrCont')]);
        rows.push([tabellaCheck('58 - Il Libro Giornale ed il Libro Inventari sono stati regolarmente istituiti?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInv'))]);
        rows.push(['In caso di risposta affermativa:']);
        rows.push([tabellaCheck('- risultano numerati ed aggiornati?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvAgg'))]);
        rows.push([tabellaCheck('- Per tali ultimi libri risulta versata correttamente l’imposta di bollo?¹', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvBollo'))]);
        rows.push([tabellaCheck('- L’inventario è annualmente sottoscritto dal legale rappresentante dell’ente?', this.getValueFromKeyWithWhitespace('my_CkLibroGioInvLR'))]);
        rows.push([
            tabellaCheck(
                '- Sono stati correttamente istituiti ed aggiornati gli altri Libri e Registri Contabili, pur non obbligatori per la disciplina civilistica? IVA Acquisti, IVA vendite, Registro beni ammortizzabili, ecc.)',
                this.getValueFromKeyWithWhitespace('my_CkAltriLibri')
            ),
        ]);
        rows.push([
            format(
                '¹)Le cooperative sociali sono esenti dalla tassa di concessione governativa e dall’imposta di bollo, dovranno indicare nell’ultima pagina del libro rispettivamente “Esente da tassa CC.GG. ai sensi art.18 del D.Lgs. 4/12/1997 n. 460” ed “Esente da imposta di bollo ai sensi art. 17 D.Lgs. n. 460 del 4/12/1997”. \n - Le cooperative edilizie a mutualità prevalente versano ¼ della tassa di concessione governativa (art. 147, lett. f del R.D. 28/04/38 n. 1165), sono inoltre esenti dal pagamento dell’imposta di bollo e dovranno indicare nell’ultima pagina del libro gli estremi di esenzione: “Esente da imposta di bollo ai sensi art. 66 D.L. 331/1993 conv. in L. n. 427 del 29/10/1993” ',
                bold,
                alignment,
                9,
                true
            ),
        ]);
        rows.push(['Eventuali note ed osservazioni sui punti da 52 a 58: \n' + this.getValueFromKeyWithWhitespace('my_NoteOsserLibri')]);
        rows.push([
            tabellaCheck(
                "59 - Il rappresentante dell'ente dichiara che è stato portato a conoscenza dei soci, con le modalità previste dall’art. 17 del D.Lgs. 220/02, l’estratto del verbale della più recente revisione o ispezione? ",
                this.getValueFromKeyWithWhitespace('my_CkVerbConoscSoci')
            ),
        ]);
        rows.push(['- in caso di risposta negativa specificare:' + this.getValueFromKeyWithWhitespace('my_DescVerbConoscSoci')]);
        rows.push(['60 - Altro:' + this.getValueFromKeyWithWhitespace('my_Altro')]);
        rows.push([format('CONCLUSIONI', true, 'center', 10, italics)]);
        rows.push([
            '61 - Giudizio Conclusivo del Revisore ed eventuali altre notizie, anche in ordine a segnalazioni ed esposti in relazione ai quali lo stesso debba in particolare riferire: \n' +
                this.getValueFromKeyWithWhitespace('my_NotaProcedRevisione'),
        ]);
        rows.push(['\n' + this.getValueFromKeyWithWhitespace('my_GiudizioConclusivo')]);
        rows.push([
            '62 - Eventuali suggerimenti e consigli per migliorare la gestione, il livello di democrazia interna, al fine di promuovere la reale partecipazione dei soci alla vita sociale:' +
                this.getValueFromKeyWithWhitespace('my_SuggConsigli'),
        ]);
        rows.push(['63 - Irregolarità sanabili per le quali deve essere irrogata la diffida:']);
        rows.push([this.tabellaIrrSanabili()]);
        rows.push([lineaVuota()]);

        //OSSERVAZIONI E CONTRODEDUZIONI
        rows.push([format('EVENTUALI OSSERVAZIONI E CONTRODEDUZIONI DEL LEGALE RAPPRESENTANTE', true, 'center', 10, italics)]);
        rows.push([
            'Il rappresentante dell’ente è reso edotto della possibilità di presentare, entro 15 giorni dalla dat documentazione dovrà essere prodotta agli Uffici della Pubblica Amministrazione esclusivamente per via informatica a mezzo posta certificata. \n Il rappresentante dichiara:' +
                this.getValueFromKeyWithWhitespace('my_OsservControdeduz'),
        ]);
        rows.push([
            'Il revisore, considerate le risultanze fin qui emerse, ed in particolare quanto indicato ai punti 61, 62 e 63, preso atto delle osservazioni e controdeduzioni del legale rappresentante:',
        ]);
        rows.push([Indent(2, this.tabellaOsservazioniControdeduzioni())]);
        rows.push([
            'La presente sezione del verbale debitamente controfirmata, è redatta in n. ' +
                this.getValueFromKeyWithWhitespace('my_NOriginali') +
                " originali, uno dei quali viene consegnato al rappresentante dell'ente revisionato o, in caso di rifiuto di sottoscrizione, notificato a mezzo posta certificata/raccomandata.",
        ]);
        rows.push([
            "Al rappresentante dell’ente viene ricordato l’obbligo di portare a conoscenza dei soci i contenuti del presente verbale con le modalità di cui all’art. 17 del Decreto legislativo n. 220/2002. \n Ai sensi del combinato disposto del D.Lgs. n. 220/02 e della Legge n. 221/2012, si precisa che gli uffici amministrativi preposti alla vigilanza cooperativa assolvono i compiti loro affidati dalla legge esclusivamente nell'interesse pubblico e che la suddetta vigilanza esplica effetti ed è diretta nei soli confronti delle pubbliche amministrazioni ai fini della legittimazione a beneficiare delle agevolazioni fiscali, previdenziali e di altra natura, nonché per l’adozione dei provvedimenti previsti dall’articolo 12 del citato D.Lgs. n. 220/02.",
        ]);
        rows.push([lineaVuota()]);
        rows.push(['Luogo, ' + this.getValueFromKeyWithWhitespace('my_LuogoCompilaz') + '  data ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCompilaz'))]);
        rows.push([lineaVuota()]);
        rows.push([this.tabellaOsservazioniControdeduzioniFirme()]);

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '2') {
            //SCHEDA GESTIONE COMMISSARIALE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI GESTIONE COMMISSARIALE', true, 'center', fontSize, italics)]);
            rows.push([
                tabellaCheck('1 - Ci sono reali e concrete prospettive per il perseguimento dello scopo sociale?', this.getValueFromKeyWithWhitespace('my_ckProspettScopoSoc')),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descProspettScopoSoc'))]);
            rows.push([
                tabellaCheck(
                    "2 - Su quali risorse materiali e finanziarie può contare l'ente per la prosecuzione dell'attività sociale?",
                    this.getValueFromKeyWithWhitespace('my_ckRisFinProsecuzioneAttSoc')
                ),
            ]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descRisFinProsecuzioneAttSoc'))]);
            rows.push(["3 - Quali iniziative in concreto il Commissario Governativo, una volta nominato, dovrebbe porre in essere per regolarizzare l'ente?"]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descInizCommGovernativo'))]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '3') {
            //SCHEDA PROPOSTA DI SCIOGLIMENTO
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SCIOGLIMENTO', true, 'center', fontSize, italics)]);
            rows.push(["1 - Quali delle fattispecie di cui all'art. 2545 septiesdecies c.c. si ritengono ricorrenti per la cooperativa?"]);
            rows.push([this.tabellaCoopPropostaScioglimento()]);
            rows.push([Indent(4, 'Specificare \n' + this.getValueFromKeyWithWhitespace('my_descArt2545Ric'))]);
            rows.push([
                "2 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari?', this.getValueFromKeyWithWhitespace('my_ckBeniImmobili')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione?', this.getValueFromKeyWithWhitespace('my_ckBeniMobiliRegistr')))]);
            rows.push([
                Indent(
                    4,
                    tabellaCheck("- Un valore dell'Attivo che renda necessaria la nomina di un liquidatore?", this.getValueFromKeyWithWhitespace('my_ckValAttivoNecessLiquid'))
                ),
            ]);
            rows.push([Indent(4, 'Specificare: \n' + this.getValueFromKeyWithWhitespace('my_descRisultUltBilApp'))]);
            rows.push([
                tabellaCheck(
                    "3 - Ricorrono entrambi i presupposti di cui all'art. 223 septiesdecies Disp. Att. c.c.?",
                    this.getValueFromKeyWithWhitespace('my_ckPresuppostiArt223')
                ),
            ]);
            rows.push([Indent(4, 'Mancato deposito dei bilanci di esercizio da oltre cinque anni')]);
            rows.push([Indent(4, 'Assenza di:')]);
            rows.push([Indent(6, '- Beni immobiliari')]);
            rows.push([Indent(6, '- Beni mobiliari soggetti a registrazione')]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '4' || this.getValueFromKey('my_CkProvvedimendoDi') == '9') {
            //SCHEDA PROPOSTA SOSTITUZIONE LIQUIDATORE
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI SOSTITUZIONE DEL LIQUIDATORE O DI CANCELLAZIONE DAL REGISTRO DELLE IMPRESE', true, 'center', fontSize, italics)]);
            rows.push([
                '1 - Descrivere la natura del ritardo nello svolgimento della liquidazione e le eventuali motivazioni addotte dal liquidatore \n ' +
                    this.getValueFromKeyWithWhitespace('my_descNatRitardoLiquid'),
            ]);
            rows.push([lineaVuota()]);
            rows.push(['2 - Descrivere in sintesi le eventuali ulteriori motivazioni della proposta \n' + this.getValueFromKeyWithWhitespace('my_descUlterMotivaz')]);
            rows.push([lineaVuota()]);
            rows.push([tabellaCheck('3 - Sono stati depositati i bilanci relativi agli ultimi cinque esercizi?', this.getValueFromKeyWithWhitespace('my_ckDepBil5Esercizi'))]);
            rows.push([
                "4 - Dalle risultanze dell'ultimo bilancio approvato, relativo all'esercizio " +
                    this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs') +
                    ' \n si evince la presenza di:',
            ]);
            rows.push([Indent(4, tabellaCheck('- Beni immobiliari', this.getValueFromKeyWithWhitespace('my_ckBeniImmob')))]);
            rows.push([Indent(4, tabellaCheck('- Beni mobiliari soggetti a registrazione', this.getValueFromKeyWithWhitespace('my_ckBeniMobRegistr')))]);
            rows.push([this.getValueFromKeyWithWhitespace('my_descRisultanzeBil')]);
            rows.push(['5 - Il liquidatore da sostituire è stato a suo tempo nominato:']);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAssSoci')) + " dall'Assemblea dei soci ")]);
            rows.push([Indent(4, checkSingoloInterno(this.getValueFromKey('my_ckNominaLiqAutGiud')) + " dall'Autorità Giudiziaria ")]);
        }

        if (this.getValueFromKey('my_CkProvvedimendoDi') == '5') {
            //SCHEDA LIQUIDAZIONE COATTA AMMINISTRATIVA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('SCHEDA RELATIVA ALLA PROPOSTA DI LIQUIDAZIONE COATTA AMMINISTRATIVA', true, 'center', fontSize, italics)]);
            rows.push(['1 – Descrivere in sintesi le motivazioni della proposta \n ' + this.getValueFromKeyWithWhitespace('my_descMotivoProposta')]);
            rows.push([lineaVuota()]);
            rows.push([this.tabellaCoopLiquidazioneCoattaInterna()]);
            rows.push(['In caso affermativo specificare: \n' + this.getValueFromKeyWithWhitespace('my_descAzAvviate')]);
            rows.push(['3 – Riepilogare le vertenze giudiziarie di cui al punto 51) \n' + this.getValueFromKeyWithWhitespace('my_DescVertenzeGiudiz')]);
            rows.push([
                tabellaCheck(
                    "4 – E' stata allegata una situazione patrimoniale aggiornata, conforme allo schema di cui all’art. 2424 c.c., sottoscritta dal legale rappresentante e dall’Organo di Controllo se presente?",
                    this.getValueFromKeyWithWhitespace('my_ckSituazPatrAggiorn')
                ),
            ]);
            rows.push(['In caso di risposta negativa specificarne i motivi: \n' + this.getValueFromKeyWithWhitespace('my_descSituazPatrAggiorn')]);
            rows.push([
                tabellaCheck(
                    '5 - Risultano mancati pagamenti di mensilità stipendiali od omessi versamenti di contributi previdenziali? ',
                    this.getValueFromKeyWithWhitespace('my_ckOmessoStipContr')
                ),
            ]);
            rows.push(['In caso di risposta positiva specificare: \n' + this.getValueFromKeyWithWhitespace('my_descOmessoStipContr')]);
            rows.push(["6 – Dall'ultimo bilancio e dalla situazione patrimoniale aggiornata emergono i seguenti valori:"]);
            rows.push([this.tabellaCoopLiquidazioneCoattaBilancioPatrimoniale()]);

            //SCHEDA INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIORNATA
            rows.push([newPage()]);
            rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
            rows.push([format('INDICATORI DI BILANCIO DA CALCOLARE SULLA SITUAZIONE PATRIMONIALE AGGIORNATA', true, 'center', fontSize, italics)]);
            rows.push([this.tabellaCoopBilancioSituazionePatrimoniale()]);
            rows.push([
                format(
                    '¹) Al fine di una puntuale determinazione del corretto valore degli indicatori, in questo caso esclusivamente finalizzati a verificare il potenziale stato di insolvenza a breve, l’Attivo Circolante deve essere depurato dei crediti esigibili oltre l’esercizio. ',
                    bold,
                    alignment,
                    8,
                    italics
                ),
            ]);
            rows.push(['- Eventuali osservazioni: \n' + this.getValueFromKeyWithWhitespace('my_descEventOss')]);
        }

        //PARAMETRI NASCOSTI
        rows.push([parametriNascosti(null, this.getValueFromKeyWithWhitespace('md5'), null, null)]);

        // return finale____________________
        return fxTabella(cols, rows);
    }

    private generateSchedaSettore(): Content {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '100%' });
        //COOPERATIVE SOCIALI
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_SOCIALI) {
            this.stampaSchedaSettoreCooperativeSociali(rows);
        }
        //COOPERATIVE PRODUZIONE E LAVORO
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_PRODUZIONE_LAVORO) {
            this.stampaSchedaSettoreCooperativeProduzioneLavoro(rows);
        }
        //COOPERATIVE AGRICOLE / LAVORO AGRICOLO
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_AGRICOLE) {
            this.stampaSchedaSettoreCooperativeAgricole1(rows);
            rows.push([newPage()]);
            this.stampaSchedaSettoreCooperativeProdAgricoliAllevamento(rows);
            rows.push([newPage()]);
            this.stampaSchedaSettoreConsorziAgrari(rows);
        }
        //COOPERATIVE TRASPORTO
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_TRASPORTO) {
            this.stampaSchedaSettoreCooperativeTrasporto(rows);
        }
        //COOPERATIVE PESCA
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_PESCA) {
            this.stampaSchedaSettoreCooperativePesca(rows);
        }
        //COOPERATIVE ALTRE COOPERATIVE
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_ALTRE) {
            this.stampaSchedaSettoreAltreCooperative(rows);
        }
        //COOPERATIVE DI CONSUMO
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_CONSUMO) {
            this.stampaSchedaSettoreCooperativeConsumo(rows);
        }
        //COOPERATIVE DI ABITAZIONE E LORO CONSORZI
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_EDILIZIE) {
            this.stampaSchedaSettoreCooperativeAbitazione(rows);
        }
        //SCHEDA SETTORE COOPERATIVE O CONSORZI GARANZIA FIDI
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_FIDI) {
            this.stampaSchedaSettoreCooperativeGaranziaFidi(rows);
        }
        //SCHEDA SETTORE COOPERATIVE DI DETTAGLIANTI
        if (this.tipoDocumento === TipoDocumento.SCHEDA_SETTORE_DETTAGLIANTI) {
            this.stampaSchedaSettoreCooperativeDettaglianti(rows);
        }
        // return finale____________________
        return fxTabella(cols, rows);
    }

    private stampaSchedaSettoreConsorziAgrari(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE CONSORZI AGRARI', true, 'center', fontSize, italics)]);
        rows.push(['Oltre a quanto riferito nel resto del verbale il revisore segnala ulteriori aspetti rilevanti in ordine a:']);
        rows.push([' - Scambio Mutualistico statutariamente previsto con i soci, agricoltori, conferenti prodotti agricoli, fornitori, altri']);
        rows.push([this.getValueFromKeyWithWhitespace('my_CommScambioMutPrevisto')]);
        rows.push([' - Struttura organizzativa ( sede centrale, sedi consortili, agenzie, altro)']);
        rows.push([this.getValueFromKeyWithWhitespace('my_CommStruttOrgSediAg')]);
        rows.push([' - Rapporti instaurati con gli agenti']);
        rows.push([this.getValueFromKeyWithWhitespace('my_CommRappAgenti')]);
        rows.push([' - Attività (vendita o noleggio di prodotti di supporto all’agricoltura, altro)']);
        rows.push([this.getValueFromKeyWithWhitespace('my_CommVenNolProdAgr')]);
        rows.push([' - Gestioni collettive: ammasso, stoccaggio, negoziazione della cambiale agraria, servizi assicurativi, altro']);
        rows.push([this.getValueFromKeyWithWhitespace('my_CommGestCollett')]);
    }

    private stampaSchedaSettoreCooperativeDettaglianti(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE DI DETTAGLIANTI', true, 'center', fontSize, italics)]);
        rows.push(['Descrivere la tipologia dei servizi che la cooperativa fornisce ai propri soci:']);
        rows.push([' - Acquisti collettivi in forma di commissionaria']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettAcqColl')]);
        rows.push([' - Servizi Amministrativi']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettServAmm')]);
        rows.push([' - Servizi Finanziari']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettServFin')]);
        rows.push([' - Gestione Mercati rionali']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettGestMercRion')]);
        rows.push([' - Logistica']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettLogistica')]);
        rows.push([' - Altro']);
        rows.push([this.getValueFromKeyWithWhitespace('my_SchDettAltro')]);
        rows.push(['Eventuali note ed osservazioni: \n' + this.getValueFromKeyWithWhitespace('my_SchDettNoteOsserv')]);
    }

    private stampaSchedaSettoreCooperativeGaranziaFidi(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE O CONSORZI GARANZIA FIDI', true, 'center', fontSize, italics)]);
        rows.push([
            tabellaCheck(
                "1 - Lo statuto è conforme alle previsioni di cui all'art. 13 del Decreto Legge n. 269/03, convertito con modificazioni dalla legge n. 326/03? ",
                this.getValueFromKeyWithWhitespace('my_CkCoopFidiConforme')
            ),
        ]);
        rows.push(['- In particolare: ']);
        rows.push([tabellaCheck('- Il Capitale Sociale è uguale o superiore a € 100.000? ', this.getValueFromKeyWithWhitespace('my_CkCapSocPerFidi'))]);
        rows.push([tabellaCheck('- Il Patrimonio netto è uguale o superiore a € 250.000? ', this.getValueFromKeyWithWhitespace('my_CkPatrNettoPerFidi'))]);
        rows.push(['La quota sociale è ']);
        rows.push([Indent(6, tabellaCheck('- di almeno € 250 ', this.getValueFromKeyWithWhitespace('my_CkQuotaSocFidi')))]);
        rows.push([Indent(6, tabellaCheck('- non superiore al 20% del Capitale Sociale  ', this.getValueFromKeyWithWhitespace('my_CkQuotaSocPercFidi')))]);
        rows.push([tabellaCheck('- Viene versato lo 0,5 x mille ai fondi di garanzia interconsortili? ', this.getValueFromKeyWithWhitespace('my_CkVersFondiGarFidi'))]);
        rows.push([lineaVuota()]);
        rows.push(['A seguito della riforma operata dal D.lgs. n. 141/2010:']);
        rows.push([
            doubleFormat(
                '2 - ',
                bold1,
                italics1,
                fontSize1,
                " Per le cooperative vigilate dall'Organismo di Vigilanza, cd. Confidi Minori (iscritte all’Elenco ex 112 TUB):",
                bold2,
                true,
                fontSize2,
                alignment
            ),
        ]);
        rows.push([
            tabellaCheck(
                '- Lo schema di bilancio è conforme, per quanto di competenza, al D.Lgs. 18/08/2015, n. 136? ',
                this.getValueFromKeyWithWhitespace('my_CkFidiSchemaBilVig')
            ),
        ]);
        rows.push([tabellaCheck("- L'ente svolge esclusivamente l'attività di garanzia collettiva dei fidi?", this.getValueFromKeyWithWhitespace('my_CkSoloGaranziaFidi'))]);
        rows.push([lineaVuota()]);
        rows.push([
            doubleFormat(
                '3 - ',
                bold1,
                italics1,
                fontSize1,
                " Per le cooperative vigilate dalla Banca d'Italia, cd. “Confidi Maggiori (iscritte all’Albo ex 106 TUB):",
                bold2,
                true,
                fontSize2,
                alignment
            ),
        ]);
        rows.push([tabellaCheck("Il volume dell'attività finanziaria è almeno di 150 milioni di euro ", this.getValueFromKeyWithWhitespace('my_CkFidiVolAttBanca'))]);
        rows.push([tabellaCheck('In caso di risposta negativa, rientra nella disciplina transitoria: ', this.getValueFromKeyWithWhitespace('my_CkFidiVolAttBancaDiscip'))]);
        rows.push(['Specificare: ' + this.getValueFromKeyWithWhitespace('my_NoteVolumeAttBanca')]);
        rows.push(["- Tipologia dei servizi offerti ai soci, oltre l'attività di garanzia dei fidi " + this.getValueFromKeyWithWhitespace('my_ServiziAiSociFidi')]);
        rows.push([
            tabellaCheck(
                '- Lo schema di bilancio è conforme, per quanto di competenza, al D.Lgs. 18/08/2015, n. 136? ',
                this.getValueFromKeyWithWhitespace('my_CkFidiSchemaBilancioConforme')
            ),
        ]);
        rows.push([
            "4 - Riferire in ordine alla struttura ed alla attività del sodalizio in riferimento alle previsioni di cui all'art. 13 della legge n. 326/03, del regolamento di cui al Decreto 17 febbraio 2009, n. 29 ed eventuali successive modificazioni ed integrazioni: ",
        ]);
        rows.push([this.tabellaCoopConsorzioFidi()]);
        rows.push(['Eventuali note ed osservazioni: \n ' + this.getValueFromKeyWithWhitespace('my_CoopFidiNote')]);
    }

    private stampaSchedaSettoreCooperativeAbitazione(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE DI ABITAZIONE E LORO CONSORZI', true, 'center', fontSize, italics)]);
        rows.push(["1 - Albo di cui all'art. 13 della legge n. 59/92. La cooperativa risulta al momento: "]);
        rows.push([this.tabellaCoopAbitazioneConsorziInterna()]);
        rows.push(['Se iscritta, indicare il numero di posizione ' + this.getValueFromKeyWithWhitespace('my_NumPosiz')]);
        rows.push([
            tabellaCheck('2 - Dalla data di iscrizione al predetto Albo sono state deliberate modifiche statutarie? ', this.getValueFromKeyWithWhitespace('my_CkDelibModifStat')),
        ]);
        rows.push([
            tabellaCheck(
                '- In caso affermativo, una copia del nuovo statuto depositato presso la C.C.I.A.A. è stata trasmessa all’Ufficio per l’Amministrazione del Comitato per l’Albo ?',
                this.getValueFromKeyWithWhitespace('my_CkCopiaStatDep')
            ),
        ]);
        rows.push([tabellaCheck('3 - L’ente aderisce ad un consorzio? ', this.getValueFromKeyWithWhitespace('my_CkEnteConsorzio'))]);
        rows.push(['In caso di risposta affermativa, specificare: ' + this.getValueFromKeyWithWhitespace('my_SpecConsorzio')]);
        rows.push([
            '4 - La cooperativa è a proprietà  ' +
                checkSingoloInterno(this.getValueFromKey('my_CkPropDivisa'), '1') +
                ' divisa ' +
                checkSingoloInterno(this.getValueFromKey('my_CkPropDivisa'), '2') +
                ' indivisa ' +
                checkSingoloInterno(this.getValueFromKey('my_CkPropDivisa'), '3') +
                ' con entrambe le tipologie ' +
                checkSingoloInterno(this.getValueFromKey('my_CkPropDivisa'), '4') +
                ' - Altro \n',
        ]);
        rows.push([
            format(
                'ACCERTAMENTO DEI REQUISITI PER IL MANTENIMENTO DELL’ISCRIZIONE ALL’ALBO NAZIONALE DELLE SOCIETÀ COOPERATIVE EDILIZIE DI ABITAZIONE E LORO CONSORZI',
                bold,
                'center',
                fontSize,
                italics
            ),
        ]);
        rows.push([
            tabellaCheck(
                'a) - La cooperativa ha mantenuto l’iscrizione nell’apposita sezione (Edilizia Abitativa) dell’Albo Società Cooperative?',
                this.getValueFromKeyWithWhitespace('my_CkMantenutaIscrSez')
            ),
        ]);
        rows.push([
            tabellaCheck(
                'b) - La cooperativa ha presentato ogni anno la relazione sull’attività svolta con le modalità e nei termini di cui al comma 10, lettera c) dell’art. 13 della L. 59/92 ? ',
                this.getValueFromKeyWithWhitespace('my_CkPresentRelaz')
            ),
        ]);
        rows.push([
            'c) - Data di presentazione dell’ultima relazione annuale ' +
                this.formatDate(this.getValueFromKeyWithWhitespace('my_DataRelazAnnuale')) +
                ' Anno di riferimento ' +
                this.getValueFromKeyWithWhitespace('my_DataRelazAnnualeAnnoRif'),
        ]);
        rows.push([
            tabellaCheck(
                'd) - In caso di iscrizione richiesta ai sensi dell’art. 13, comma 7, lettera a) della L. 59/92, tutti i soci hanno sottoscritto e versato la prescritta quota di Euro 258,23? ',
                this.getValueFromKeyWithWhitespace('my_CkPrescrittaQuota')
            ),
        ]);
        rows.push([
            tabellaCheck(
                'e) - Dalla data di presentazione dell’ultima relazione, il numero dei soci è mai sceso al di sotto di 18? ',
                this.getValueFromKeyWithWhitespace('my_CkSociSotto18')
            ),
        ]);
        rows.push([
            tabellaCheck(
                'f) - Nel caso si tratti di cooperativa, dalla data dell’iscrizione sono subentrati soci persone giuridiche? ',
                this.getValueFromKeyWithWhitespace('my_CkSociSubentratiGiu')
            ),
        ]);
        rows.push([
            Indent(
                2,
                tabellaCheck(
                    '- In caso di risposta positiva, sono cooperative edilizie iscritte all’Albo Nazionale delle Società Cooperative edilizie di abitazione e dei loro consorzi? ',
                    this.getValueFromKeyWithWhitespace('my_CkSociSubentratiCoop')
                )
            ),
        ]);
        rows.push(['- In caso di risposta negativa specificare la natura dell’ente subentrato: \n' + this.getValueFromKeyWithWhitespace('my_NaturaEnteCoop')]);
        rows.push([
            tabellaCheck(
                'g) - Nel caso si tratti di consorzio, dalla data dell’iscrizione sono subentrati soci persone fisiche?',
                this.getValueFromKeyWithWhitespace('my_CkSociSubentratiFis')
            ),
        ]);
        rows.push([
            Indent(
                2,
                tabellaCheck(
                    '- Le eventuali persone giuridiche subentrate sono cooperative edilizie iscritte all’ Albo Nazionale delle Società Cooperative edilizie di abitazione e dei loro consorzi ? ',
                    this.getValueFromKeyWithWhitespace('my_CkGiuSubentratiCoop')
                )
            ),
        ]);
        rows.push(['In caso di risposta negativa specificare la natura dell’ente subentrato: \n' + this.getValueFromKeyWithWhitespace('my_NaturaEnteSubCons')]);
        rows.push([format('Aree e interventi:', true, alignment, fontSize, italics)]);
        rows.push(['5 - Quanti programmi edilizi ha realizzato? ' + this.getValueFromKeyWithWhitespace('my_numProgEdRealizz')]);
        rows.push([tabellaCheck('6 - Ha programmi edilizi in corso di realizzazione? ', this.getValueFromKeyWithWhitespace('my_CkInCorsoProgrammaRealizzazione'))]);
        rows.push([Indent(2, 'In caso di risposta affermativa, per quanti alloggi? ' + this.getValueFromKeyWithWhitespace('my_numAlloggiInCorsoRealiz'))]);
        rows.push(['7 - Numero prenotatari:' + this.getValueFromKeyWithWhitespace('my_numPrenotat')]);
        rows.push([
            tabellaCheck('8 - Eventuali osservazioni sul numero dei soci prenotatari e gli alloggi in programma?', this.getValueFromKeyWithWhitespace('my_CkOssPrenotAlloggi')),
        ]);
        rows.push([this.getValueFromKeyWithWhitespace('my_descOssPrenotAlloggi')]);
        rows.push([tabellaCheck('9 - Esistono attualmente soci non prenotatari? ', this.getValueFromKeyWithWhitespace('my_CkSociNonPrenot'))]);
        rows.push(['In caso di risposta affermativa, qual è il loro ruolo nella compagine sociale? ' + this.getValueFromKeyWithWhitespace('my_ruoloSociNonPrenot')]);
        rows.push([tabellaCheck('10 - Il consiglio di amministrazione è composto da soci prenotatari o soci già assegnatari? ', this.tabellaCoopAbitazioneSociPrenotatari())]);
        rows.push(['Specificare: ' + this.getValueFromKeyWithWhitespace('my_DescCdASociPrenOAss')]);
        rows.push([lineaVuota()]);
        rows.push([format('Costruzioni:', true, alignment, fontSize, italics)]);
        rows.push(['11 - Fonti di Finanziamento : ']);
        rows.push([this.tabellaCoopAreeInterventiFontiFinanziamento()]);
        rows.push([this.getValueFromKeyWithWhitespace('my_FonFinAltroDesc')]);
        rows.push([
            tabellaCheck('12 - Sono stati richiesti finanziamenti pubblici in relazione ai programmi in corso? ', this.getValueFromKeyWithWhitespace('my_SchEdFinanzPubb')),
        ]);
        rows.push(['13 - Altre notizie o osservazioni: \n ' + this.getValueFromKeyWithWhitespace('my_SchEdAltreNotOsserv')]);
    }

    private stampaSchedaSettoreCooperativeConsumo(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE DI CONSUMO', true, 'center', fontSize, italics)]);
        rows.push([tabellaCheck('1 -Vende anche ai non soci?', this.getValueFromKeyWithWhitespace('my_CkVendeAiNonSoci'))]);
        rows.push([
            tabellaCheck(
                '2 - Quanti punti vendita gestisce? ' + this.getValueFromKeyWithWhitespace('my_PuntiVendita') + ' Se richieste, è dotata delle relative licenze? ',
                this.getValueFromKeyWithWhitespace('my_CkPossiedeLicenze')
            ),
        ]);
        rows.push([this.tabellaCoopConsumoInterna()]);
        rows.push(['Specificare \n' + this.getValueFromKeyWithWhitespace('my_GeneriVenduti')]);
        rows.push([tabellaCheck('4 - La cooperativa ha settori di attività dati in gestioni a terzi?', this.getValueFromKeyWithWhitespace('my_CkSettoriATerzi'))]);
        rows.push(['In caso di risposta affermativa, specificare: \n' + this.getValueFromKeyWithWhitespace('my_SettoriInGestioneTerzi')]);
        rows.push([tabellaCheck('5 - La cooperativa offre ai soci altri servizi: ', this.getValueFromKeyWithWhitespace('my_CkOffreAltriServizi'))]);
        rows.push(['Specificare: \n' + this.getValueFromKeyWithWhitespace('my_AltriServiziOfferti')]);
        rows.push([
            '6 - Se la cooperativa gestisce una sezione di prestito sociale, come si collega tale attività con il perseguimento dello scopo sociale? \n' +
                this.getValueFromKeyWithWhitespace('my_CommentoGestionePrestitoSociale'),
        ]);
    }

    private stampaSchedaSettoreAltreCooperative(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE ALTRE COOPERATIVE', true, 'center', fontSize, italics)]);
        rows.push([format('Con scambio mutualistico particolare o plurimo, o che fornisce o riceve servizi dai soci', true, 'center', 9, italics)]);
        rows.push([lineaVuota()]);
        rows.push(['1- Nel caso di pluralità di scambi mutualistici compilare anche le schede corrispondenti. ']);
        rows.push([this.tabellaAltreCoopCategoriaScambiMutualistici()]);
        rows.push(['2- Descrivere le tipologie di scambio mutualistico: n' + this.getValueFromKeyWithWhitespace('my_AltreCoopTipoScambioMutua')]);
        rows.push([lineaVuota()]);
        rows.push(['3 - Note ed osservazioni sul particolare inquadramento della cooperativa: \n' + this.getValueFromKeyWithWhitespace('my_AltreCoopNote')]);
    }

    private stampaSchedaSettoreCooperativePesca(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE DELLA PESCA', true, 'center', fontSize, italics)]);
        rows.push([this.tabellaCoopPescaInterna()]);
        rows.push([
            tabellaCheck(
                '2 - La cooperativa ha natanti di sua proprietà o dei soci? Specificare \n' + this.getValueFromKeyWithWhitespace('my_DescrNatanti'),
                this.getValueFromKeyWithWhitespace('my_CkPossiedeNatanti')
            ),
        ]);
        rows.push([
            '3 - Quali attività ( produzione ghiaccio, rifornimenti materiali, attrezzi, carburanti, tintoria reti, salagione trasporto, ecc.) vengono svolte dalla cooperativa a favore dei soci? \n' +
                this.getValueFromKeyWithWhitespace('my_AltreAttivitaPerSociPesca'),
        ]);
        rows.push([tabellaCheck('4 - Gestisce centri di raccolta del pescato?', this.getValueFromKeyWithWhitespace('my_CkGestisceCentriRaccoltaPescato'))]);
        rows.push([tabellaCheck('5 - Gestisce mercati ittici?', this.getValueFromKeyWithWhitespace('my_CkGestisceMercatiIttici'))]);
        rows.push([tabellaCheck('6 - Gestisce centri di acquacoltura?', this.getValueFromKeyWithWhitespace('my_CkGesctisceCentriAcquaColtura'))]);
        rows.push(['7 - Altro: specificare \n ' + this.getValueFromKeyWithWhitespace('my_SchPescaGestisceAltroDesc')]);
        rows.push(['Per le cooperative di lavoro compilare la scheda seguente:']);
        rows.push([fxTabellaEsternaBordo(this.tabellaExArt1())]);
    }

    private stampaSchedaSettoreCooperativeTrasporto(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE DI TRASPORTO', true, 'center', fontSize, italics)]);
        rows.push([this.tabellaCoopTrasportoInterna()]);
        rows.push(['2 - Per le cooperative di servizio: ']);
        rows.push([Indent(2, tabellaCheck('a) la cooperativa possiede officine o rimesse?', this.getValueFromKeyWithWhitespace('my_CkPossideOfficine')))]);
        rows.push([Indent(2, tabellaCheck('b) la cooperativa gestisce distributori di carburante?', this.getValueFromKeyWithWhitespace('my_CkGestisceDistributoriCarburante')))]);
        rows.push([Indent(2, tabellaCheck('c) la cooperativa gestisce il servizio di radiotaxi?', this.getValueFromKeyWithWhitespace('my_CkGestisceRadioTaxi')))]);
        rows.push([Indent(2, 'd) quali altri servizi la cooperativa offre ai soci? \n' + this.getValueFromKeyWithWhitespace('my_ServiziOffertiAiSoci'))]);
        rows.push(['3 - Per le cooperative di lavoro:']);
        rows.push([
            Indent(2, tabellaCheck('- la cooperativa ha la titolarità/disponibilità dei mezzi e delle licenze?', this.getValueFromKeyWithWhitespace('my_TitolareMezziLicenze'))),
        ]);
        rows.push([
            Indent(
                2,
                tabellaCheck('- in caso di risposta affermativa gli stessi sono stati conferiti dai soci?', this.getValueFromKeyWithWhitespace('my_MezziLicenzeConferitiSoci'))
            ),
        ]);
        rows.push(['Altre osservazioni: ' + this.getValueFromKeyWithWhitespace('my_ScTraspAltreOsserv')]);
        rows.push([fxTabellaEsternaBordo(this.tabellaExArt1(false, false, true))]);
    }

    private stampaSchedaSettoreCooperativeProdAgricoliAllevamento(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE AGRICOLE DI CONFERIMENTO PRODOTTI AGRICOLI E DI ALLEVAMENTO', true, 'center', fontSize, italics)]);
        rows.push([tabellaCheck('1 - La cooperativa dispone di un proprio stabilimento?', this.getValueFromKeyWithWhitespace('my_CkDispStabilimento'))]);
        rows.push([this.tabellaCoopAgricAllevamentoProprietà()]);
        rows.push(['- Natura delle lavorazioni svolte: \n ' + this.getValueFromKeyWithWhitespace('my_LavorazioniSvolte')]);
        rows.push(['2 - Quali prodotti agricoli conferiscono i soci? ' + this.getValueFromKeyWithWhitespace('my_ProdottiConferitiDaiSoci')]);
        rows.push(['3 - Quali prodotti agricoli vengono acquistati da terzi? ' + this.getValueFromKeyWithWhitespace('my_ProdottiAcquistatiDaTerzi')]);
        rows.push([
            tabellaCheck(
                '4 - La cooperativa fornisce servizi ai propri soci beni e/o servizi diretti alla cura ed allo sviluppo del ciclo biologico?',
                this.getValueFromKeyWithWhitespace('my_CkCicloBio')
            ),
        ]);
        rows.push(['In caso di risposta affermativa, specificare: \n ' + this.getValueFromKeyWithWhitespace('my_NoteCicloBio')]);
    }

    private stampaSchedaSettoreCooperativeAgricole1(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE AGRICOLE E DI LAVORO AGRICOLO', true, 'center', fontSize, italics)]);
        rows.push(["1 - Disponibilità dei Terreni lavorati nell'anno in corso: "]);
        rows.push([Indent(7, this.tabellaTerreniLavorati())]);
        rows.push(['2 - Colture praticate: \n ' + this.getValueFromKeyWithWhitespace('my_ColturePraticate')]);
        rows.push([tabellaCheck('3 - Fruisce di provvidenze a seguito di calamità naturali?', this.getValueFromKeyWithWhitespace('my_CkFruisceProvvidenze'))]);
        rows.push(['In caso di risposta affermativa, specificare: ' + this.getValueFromKeyWithWhitespace('my_ProvvidenzePerCalamita')]);
        rows.push([
            tabellaCheck(
                '4 - La cooperativa fornisce servizi a i propri soci? (motoaratura, trebbiatura, acquisti e vendita di prodotti agricoli, immagazzinamento ortofrutticolo, agrumarie, ecc.).',
                this.getValueFromKeyWithWhitespace('my_CkFornisceServiziSoci')
            ),
        ]);
        rows.push(['In caso di risposta affermativa, specificare: ' + this.getValueFromKeyWithWhitespace('my_ElencoServiziSoci')]);
        rows.push([fxTabellaEsternaBordo(this.tabellaExArt1())]);
    }

    private stampaSchedaSettoreCooperativeProduzioneLavoro(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE PRODUZIONE E LAVORO', true, 'center', fontSize, italics)]);
        rows.push([fxTabellaEsternaBordo(this.tabellaExArt1())]);
    }

    private stampaSchedaSettoreCooperativeSociali(rows: any[]) {
        rows.push([this.intestazioneSchedaSettore(this.getValueFromKeyWithWhitespace('my_DenominazEnte'), this.getValueFromKeyWithWhitespace('my_PosizAlboEnti'))]);
        rows.push([format('SCHEDA DI SETTORE COOPERATIVE SOCIALI', true, 'center', fontSize, italics)]);
        rows.push([fxTabellaEsternaBordo(this.tabellaExArt1(true, true))]);
        rows.push([lineaVuota()]);
        rows.push([tabellaCheck("1 - La denominazione sociale contiene l'indicazione “cooperativa sociale”?", this.getValueFromKeyWithWhitespace('my_CkDenom'))]);
        rows.push([
            tabellaCheck(
                '2 - La cooperativa sociale è Onlus di diritto ex D. Lgs. n. 460/97, ha ulteriori qualificazioni (Impresa Sociale, Impresa del Terzo Settore, ecc.) ?',
                this.getValueFromKeyWithWhitespace('my_CkOnlus')
            ),
        ]);
        rows.push(['Eventualmente specificare' + this.getValueFromKeyWithWhitespace('my_NoteOnlus')]);
        rows.push(["3 - La cooperativa ha lo scopo di perseguire l'interesse generale della comunità alla promozione umana e all'integrazione sociale dei cittadini attraverso:"]);
        rows.push([Indent(2, tabellaCheck('a) la gestione di servizi sociosanitari ed educativi?', this.getValueFromKeyWithWhitespace('my_CkServSocioSanit')))]);
        rows.push([
            Indent(
                2,
                tabellaCheck(
                    "b) lo svolgimento di attività diverse - agricole, industriali, commerciali o di servizi - finalizzate all'inserimento lavorativo di persone svantaggiate in campo:",
                    empty()
                )
            ),
        ]);
        rows.push([Indent(4, tabellaCheck('- Agricolo', this.getValueFromKeyWithWhitespace('my_CkAgricolo')))]);
        rows.push([Indent(4, tabellaCheck('- Industriale', this.getValueFromKeyWithWhitespace('my_CkIndustr')))]);
        rows.push([Indent(4, tabellaCheck('- Commerciale', this.getValueFromKeyWithWhitespace('my_CkComm')))]);
        rows.push([Indent(4, tabellaCheck('- di Servizio', this.getValueFromKeyWithWhitespace('my_CkServiz')))]);
        rows.push([Indent(2, tabellaCheck('c) oggetto plurimo comprensivo di entrambe le tipologie', this.getValueFromKeyWithWhitespace('my_CkPlurimo')))]);
        rows.push([lineaVuota()]);
        rows.push([
            tabellaCheck(
                '4 - Lo statuto prevede la presenza di soci volontari che prestino la loro attività gratuitamente?',
                this.getValueFromKeyWithWhitespace('my_CkSociVolontari')
            ),
        ]);
        rows.push([
            Indent(
                2,
                '- Numero dei soci volontari: ' +
                    this.getValueFromKeyWithWhitespace('my_SociVolontari') +
                    ', loro percentuale rispetto al numero complessivo dei soci? ' +
                    (parseFloat(this.getValueFromKeyWithWhitespace('my_PercentSociVolont')) * 100).toFixed(3) +
                    '%'
            ),
        ]);
        rows.push([tabellaCheck("5 - I soci volontari sono iscritti in un'apposita sezione del libro soci?", this.getValueFromKeyWithWhitespace('my_CkSezSociVolont'))]);
        rows.push([
            tabellaCheck(
                '6 - I soci volontari sono coperti da assicurazione contro gli infortuni sul lavoro e le malattie professionali?',
                this.getValueFromKeyWithWhitespace('my_CkSociAssicurati')
            ),
        ]);
        rows.push([
            tabellaCheck(
                '7 - Ai soci volontari viene corrisposto soltanto il rimborso delle spese effettivamente sostenute e documentate sulla base di parametri stabiliti dalla cooperativa sociale per la totalità dei soci?',
                this.getValueFromKeyWithWhitespace('my_CkVolontRimborso')
            ),
        ]);
        rows.push(['8 - In caso di risposta negativa evidenziarne i motivi: \n' + this.getValueFromKeyWithWhitespace('my_MotiviRimborso')]);
        rows.push([
            tabellaCheck(
                '9 - Le categorie dei soggetti svantaggiati rientrano tra quelle previste dall’art. 4 della legge 381/91?',
                this.getValueFromKeyWithWhitespace('my_CkSociSvantagg')
            ),
        ]);
        rows.push([Indent(2, '- In caso di risposta negativa specificare: ' + this.getValueFromKeyWithWhitespace('my_MotiviSociSvantagg'))]);
        rows.push([
            '10 - Percentuale delle persone svantaggiate rispetto al numero complessivo dei lavoratori¹: ' +
                (parseFloat(this.getValueFromKeyWithWhitespace('my_PercentPersSvantagg')) * 100).toFixed(3) +
                '%',
        ]);
        rows.push([Indent(2, '- Numero delle persone svantaggiate ' + this.getValueFromKeyWithWhitespace('my_PersSvantagg'))]);
        rows.push(['11 - La cooperativa ha stipulato le seguenti convenzioni con enti pubblici e / o privati: ' + this.getValueFromKeyWithWhitespace('my_NumConvEnti')]);
        rows.push([this.listaConvenzioniStipulateCoopSocialiHeader()]);
        rows.push([this.listaConvenzioniStipulateCoopSocialiDati()]);
        rows.push(['- Eventuale ulteriore descrizione: ' + this.getValueFromKeyWithWhitespace('my_DescEntiConvenzionati')]);
        rows.push([tabellaCheck("12 - La cooperativa è iscritta all'Albo Regionale delle Cooperative Sociali? ", this.getValueFromKeyWithWhitespace('my_CkIscrAlboCoopSoc'))]);
        rows.push([
            Indent(
                2,
                '- In caso di risposta positiva, riportare gli estremi del provvedimento di iscrizione, ovvero evidenziarne i motivi in caso di risposta negativa: \n' +
                    this.getValueFromKeyWithWhitespace('my_MotiviIscriz')
            ),
        ]);
        rows.push([tabellaCheck('13 - La cooperativa è correttamente inquadrata nell’Albo delle Società Cooperative?', this.getValueFromKeyWithWhitespace('my_CkCoopInquadrata'))]);
        rows.push([
            tabellaCheck(
                '14 - Sono ammesse come soci persone giuridiche pubbliche o private nei cui statuti sia previsto il finanziamento e lo sviluppo delle attività di tali cooperative? ',
                this.getValueFromKeyWithWhitespace('my_CkAmmessiSociFinanz')
            ),
        ]);
        rows.push([
            '15 - Elenco di tali persone giuridiche, e loro funzione all’interno della compagine sociale: \n' + this.getValueFromKeyWithWhitespace('my_ElencoPersGiuridiche'),
        ]);
        rows.push(['16 - Nel caso di consorzio di cooperative sociali:']);
        rows.push([Indent(2, '- Quante cooperative aderiscono al consorzio?' + this.getValueFromKeyWithWhitespace('my_NCoopConsorzio'))]);
        rows.push([
            Indent(
                2,
                tabellaCheck(
                    "- Il consorzio ha una base sociale conforme a quanto previsto dall'art. 8 della legge n. 381/91?",
                    this.getValueFromKeyWithWhitespace('my_CkConsorzConforme')
                )
            ),
        ]);
        rows.push([format('¹) Si ricorda che la C.C.C. con delibera del 13/01/2017 ha fissato alcuni criteri per il calcolo: \n', bold, alignment, 8, italics)]);
        rows.push([
            Indent(
                0.5,
                format(
                    '- La percentuale va calcolata sul numero dei lavoratori in forza e non su quello dei soci, pertanto solo quando la cooperativa comincerà una propria attività produttiva sarà necessario che rispetti la percentuale minima del 30%, dal conteggio dovranno essere esclusi i soci non impegnati nell’attività, i volontari ed i sovventori o finanziatori e, naturalmente gli svantaggiati stessi; \n - qualora lo Statuto della cooperativa sociale integri contestualmente le attività e gli scopi previsti dalle lettere a) e b) della legge 381/1991 la percentuale di lavoratori svantaggiati va calcolata in rapporto al solo personale impiegato nell’attività di “tipo b”, escludendo invece il personale impiegato nell’attività di “tipo a”. \n - l’assenza dalla costituzione degli svantaggiati, in una cooperativa sociale di tipo B, che si protrae per più di un anno nonostante l’esercizio dell’attività, configura la spurietà dell’ente, intesa come irregolarità insanabile; nel caso, invece, in cui tale percentuale sia venuta meno e non più ripristinata oltre il termine previsto, l’ente deve essere sciolto, previa diffida.',
                    bold,
                    alignment,
                    8,
                    italics
                )
            ),
        ]);
    }

    // *******************************************************************************************************************************
    // *******************************************************************************************************************************
    // SEZIONE I RILEVAZIONE --- SEZIONE I RILEVAZIONE --- SEZIONE I RILEVAZIONE --- SEZIONE I RILEVAZIONE --- SEZIONE I RILEVAZIONE -
    // *******************************************************************************************************************************
    // *******************************************************************************************************************************

    // ***************************************************************************************************************************************************************************
    // DATI IDENTIFICATIVI --- DATI IDENTIFICATIVI --- DATI IDENTIFICATIVI ---DATI IDENTIFICATIVI --- DATI IDENTIFICATIVI --- DATI IDENTIFICATIVI --- DATI IDENTIFICATIVI ---
    // ***************************************************************************************************************************************************************************

    private fxDatiIdentificativi(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '40%' });
        colsTmp.push({ header: '', width: '60%' });

        rowsTmp.push(["Denominazione dell'ente", this.getValueFromKeyWithWhitespace('my_DenominazEnte')]);

        //rowsTmp.push(["Matricola",  this.getValueFromKeyWithWhitespace("my_Matricola")])
        rowsTmp.push([empty(), this.tabellaDenominazioneEnte_Value()]);
        rowsTmp.push([
            doubleFormat(
                'Sede legale (indirizzo, telefono, e-mail, sito WEB) \n',
                false,
                false,
                10,
                '* Si raccomanda al revisore di verificare che la pec sia univoca ed attiva, in caso negativo, diffidare la cooperativa alla regolarizzazione della stessa.',
                false,
                false,
                8
            ),
            this.tabellaDatiUtente_Value(),
        ]);
        rowsTmp.push(['Eventuale sede amministrativa', this.tabellaDatiSedeAmministrativa_Value()]);
        rowsTmp.push(['Data costituzione', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCostituz'))]);
        rowsTmp.push(['Termine della società', this.tabellaTermineSoc()]);

        rowsTmp.push(['Codice Fiscale', this.getValueFromKeyWithWhitespace('my_CodFisc')]);
        rowsTmp.push(['Data ultima revisione', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataUltRev'))]);
        rowsTmp.push(['Soggetto che ha disposto la revisione', this.getValueFromKeyWithWhitespace('my_DescSoggDispostoRev')]);
        rowsTmp.push(['Data eventuale ultima certificazione bilancio', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataUltCert'))]);

        rowsTmp.push(['Codice attività', this.getValueFromKeyWithWhitespace('my_CodAttiv')]);
        rowsTmp.push(['Posizione Albo Società Cooperative', this.tabellaAlboCoop()]);

        rowsTmp.push(["Categoria a cui appartiene l'ente cooperativo", this.getValueFromKeyWithWhitespace('my_DescSezEnte')]);
        rowsTmp.push(['Data ultima modifica statutaria', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataUltModStat'))]);
        rowsTmp.push([this.tabellaAderenzaSX(), this.tabellaAderenzaDX()]);

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private tabellaDenominazioneEnte_Value(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: 20 });
        cols.push({ header: '', width: 100 });
        cols.push({ header: '', width: '*' });

        rows.push([empty(), 'In liquidazione dal', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataAssembleaLiquidazVolontaria'))]);

        return fxTabella(cols, rows);
    }

    private tabellaDatiUtente_Value(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([
            'Indirizzo: ' +
                this.getValueFromKeyWithWhitespace('my_IndirizzoSedeLegale') +
                ',  ' +
                this.getValueFromKeyWithWhitespace('my_DescComuneSedeLegale') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_DescProvinciaSedeLegale') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_CapSedeLegale'),
        ]);
        rows.push(['Tel. ' + this.getValueFromKeyWithWhitespace('my_SpecificaTelefonoSedeLegale') + '  Fax ' + this.getValueFromKeyWithWhitespace('my_SpecificaFaxSedeLegale')]);
        rows.push(['e-mail: ' + this.getValueFromKeyWithWhitespace('my_SpecificaMailSedeLegale')]);
        rows.push(['e-mail certificata (PEC)* ' + this.getValueFromKeyWithWhitespace('my_PECcci') + ', ' + this.getValueFromKeyWithWhitespace('my_AltraPEC')]);
        rows.push(['sito web: ' + this.getValueFromKeyWithWhitespace('my_SpecificaSitoWebSedeLegale')]);

        return fxTabella(cols, rows);
    }

    private tabellaDatiSedeAmministrativa_Value(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([
            'Indirizzo: ' +
                this.getValueFromKeyWithWhitespace('my_IndirizzoSedeAmministrativa') +
                ',  ' +
                this.getValueFromKeyWithWhitespace('my_DescComuneSedeAmministrativa') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_DescProvinciaSedeAmministrativa') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_CapSedeAmministrativa'),
        ]);
        rows.push([
            'Tel. ' +
                this.getValueFromKeyWithWhitespace('my_SpecificaTelefonoSedeAmministrativa') +
                '  Fax ' +
                this.getValueFromKeyWithWhitespace('my_SpecificaFaxSedeAmministrativa'),
        ]);
        rows.push(['e-mail: ' + this.getValueFromKeyWithWhitespace('my_SpecificaMailSedeAmministrativa')]);
        rows.push(['e-mail certificata (PEC)* ' + this.getValueFromKeyWithWhitespace('my_PECcci') + ', ' + this.getValueFromKeyWithWhitespace('my_AltraPEC')]);
        rows.push(['sito web: ' + this.getValueFromKeyWithWhitespace('my_SpecificaSitoWebSedeAmministrativa')]);

        return fxTabella(cols, rows);
    }

    private tabellaTermineSoc(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '*' });

        rows.push([
            'Data  ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataTermine')),
            'Indeterminato  ' + checkSingoloInterno(this.getValueFromKey('my_CkScadenzaSocieta')),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaAlboCoop(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrev'), '1') + '  Mutualità Prevalente', 'N. Iscrizione Albo ']);
        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrev'), '2') + '  Mutualità non Prevalente', this.getValueFromKeyWithWhitespace('my_PosizAlboEnti')]);
        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrev'), '3') + '  Non iscritta', empty()]);

        return fxTabella(cols, rows);
    }

    private tabellaAderenzaSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '65%' });

        const array = this.getArrayFromKey('my_ElAderentiA');
        for (let c = 0; c < array.length; c++) {
            rows.push(['Aderente a: ', array[c]['my_SezVisibAderenteA']['my_AderenteA']]);
            rows.push(['dal: ', this.formatDate(array[c]['my_SezVisibAderenteA']['my_aderenteDal'])]);
            /* if (c !== array.length - 1) {
        rows.push([lineaVuota(), lineaVuota()]);
      }*/
        }

        return fxTabella(cols, rows);
    }

    private tabellaAderenzaDX(): Content {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '45%' });
        rows.push([empty(), 'N. Matricola ', empty()]);
        rows.push([empty(), this.getValueFromKeyWithWhitespace('my_Matricola'), empty()]);
        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SCHEDA CONTROLLO VERIFICA PREVALENZA --- SCHEDA CONTROLLO VERIFICA PREVALENZA --- SCHEDA CONTROLLO VERIFICA PREVALENZA --- SCHEDA CONTROLLO VERIFICA PREVALENZA ---
    // ***************************************************************************************************************************************************************************

    private fxVerificaPrevalenza(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '100%' });

        rowsTmp.push([format("SCHEDA DI CONTROLLO PER LA VERIFICA DELLA PREVALENZA AI SENSI DELL'art. 2513 C.C.", true, 'left', 10, italics)]);

        rowsTmp.push([this.fxVerificaPrevalenzaInterna()]);

        rowsTmp.push([format('N.B.)', bold, 'left', 10, italics)]);
        rowsTmp.push([format('- a) compilare solo la parte relativa alla tipologia di scambio mutualistico realizzata dalla cooperativa.', bold, 'left', 8, italics)]);
        rowsTmp.push([
            format(
                '- b) solo in caso di cooperative che realizzino contestualmente più tipologie di scambio mutualistico compilare i relativi punti e determinare la media ponderata.',
                bold,
                'left',
                8,
                italics
            ),
        ]);
        rowsTmp.push([
            doubleFormat(
                '- c) nelle cooperative di lavoro, al fine di una corretta misurazione della prevalenza, occorre comprendere sia i costi del lavoro subordinato allocati in B9) nel Conto Economico, che quelli relativi a prestazioni autonome rinvenibili in B7). Vedi delibera CCC 08/07/09 per cui: ',
                false,
                false,
                8,
                '“nelle cooperative di produzione e lavoro ai fini del calcolo della prevalenza vanno computate al nominatore e denominatore (della Voce B7) le sole forme di lavoro dei soci e dei terzi e non gli altri costi in quanto, questi ultimi, non sono previsti dalla legge vigente”.',
                false,
                true,
                8
            ),
        ]);
        rowsTmp.push([format('- d) la prevalenza è verificata se i risultato finale è maggiore del 50%.', bold, 'left', 8, italics)]);
        rowsTmp.push([lineaVuota()]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private fxVerificaPrevalenzaInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '70%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });

        rows.push([
            "Il revisore dovrà effettuare la verifica per l'esercizio relativo all'ultimo bilancio approvato e per quello relativo all'esercizio precedente",
            'Esercizio \n N' + this.getValueFromKeyWithWhitespace('my_BilAnno2'),
            'Esercizio \n N-1' + this.getValueFromKeyWithWhitespace('my_BilAnno1'),
        ]);
        rows.push([Indent(1, format('1) attività svolta prevalentemente in favore dei soci', bold, alignment, fontSize, true)), empty(), empty()]);
        rows.push([
            this.rowTabella('Ricavi delle vendite e delle prestazioni', 'A1 verso soci', 'Totale A1'),
            this.getPercentage('my_A1versoSociPrimoAnno', 'my_TotaleA1PrimoAnno'),
            this.getPercentage('my_A1versoSoci', 'my_TotaleA1'),
        ]);
        rows.push([Indent(1, format('2) attività svolta prevalentemente avvalendosi delle prestazioni lavorative dei soci', bold, alignment, fontSize, true)), empty(), empty()]);
        rows.push([
            this.rowTabella('Costo del lavoro', 'B9 verso soci', 'Totale B9'),
            this.getPercentage('my_B9versoSociPrimoAnno', 'my_TotaleB9PrimoAnno'),
            this.getPercentage('my_B9versoSoci', 'my_TotaleB9'),
        ]);
        rows.push([Indent(1, format('3) attività svolta prevalentemente avvalendosi degli apporti dei soci', bold, alignment, fontSize, true)), empty(), empty()]);
        rows.push([
            this.rowTabella(
                'Costo dei beni conferiti ' + this.getValueFromKey('my_CkValoreQuantita') === 'V' ? '(Valore)' : '(Quantità)',
                this.getValueFromKey('my_CkValoreQuantita') === 'V' ? 'B6 conferiti dai soci' : 'Quantità conf. dai',
                this.getValueFromKey('my_CkValoreQuantita') === 'V' ? 'Totale B6' : 'Totale Quantità'
            ),
            this.getPercentage(
                this.getValueFromKey('my_CkValoreQuantitaPrimoAnno') === 'V' ? 'my_B6confSociPrimoAnno' : 'my_QtaConfSociPrimoAnno',
                this.getValueFromKey('my_CkValoreQuantitaPrimoAnno') === 'V' ? 'my_TotaleB6PrimoAnno' : 'my_TotQtaConfSociPrimoAnno'
            ),
            this.getPercentage(
                this.getValueFromKey('my_CkValoreQuantita') === 'V' ? 'my_B6confSoci' : 'my_QtaConfSoci',
                this.getValueFromKey('my_CkValoreQuantita') === 'V' ? 'my_TotaleB6' : 'my_TotQtaConfSoci'
            ),
        ]);
        rows.push([
            this.rowTabella('Costo della prestazione di servizi ricevuti', 'B7 ricevuti dai soci', 'Totale B7'),
            this.getPercentage('my_B7ricevSociPrimoAnno', 'my_TotaleB7PrimoAnno'),
            this.getPercentage('my_B7ricevSoci', 'my_TotaleB7'),
        ]);
        rows.push([Indent(1, format('4) attività svolta realizzando contestualmente più tipi di scambio mutualistico', bold, alignment, fontSize, true)), empty(), empty()]);
        rows.push([Indent(2, "Determinazione dell'eventuale media ponderata per la condizione di prevalenza:"), empty(), empty()]);
        rows.push([this.tabellaFormulaMediaPonderataPrevalenza(), '= ' + this.getWeightedAverage(true), '= ' + this.getWeightedAverage()]);

        return fxTabella(cols, rows);
    }

    private getWeightedAverage(firstFormula: boolean = false, numeric: boolean = false) {
        let numerator: number;
        const denominatorKeys: string[] = ['my_TotaleA1PrimoAnno', 'my_TotaleB9PrimoAnno', 'my_TotaleB6PrimoAnno', 'my_TotaleB7PrimoAnno'];
        const numeratorArrayKeys: string[] = ['my_A1versoSociPrimoAnno', 'my_B9versoSociPrimoAnno', 'my_B6confSociPrimoAnno', 'my_B7ricevSociPrimoAnno'];
        const denominator: number = this.sumArrayKeys(denominatorKeys);
        if (!firstFormula) {
            numerator = this.sumArrayKeys(numeratorArrayKeys);
        } else {
            numerator = numeratorArrayKeys
                .map((key: string, i: number) => this.getPercentage(key, denominatorKeys[i], true) as number)
                .reduce((prev: number, curr: number) => prev + curr, 0);
        }
        const result: number = (numerator / denominator) * 100;
        if (numeric) {
            return result;
        }
        return result.toFixed(2) + ' %';
    }

    private sumArrayKeys(keys: string[]): number {
        return keys.map((key: string) => parseInt(this.getValueFromKey(key), 10)).reduce((prev: number, curr: number) => prev + curr, 0);
    }

    private getPercentage(numKey, denKey, numeric: boolean = false): string | number {
        const numResult: number = (parseInt(this.getValueFromKey(numKey), 10) / parseInt(this.getValueFromKey(denKey), 10)) * 100;
        if (numeric) {
            return numResult;
        }
        return numResult.toFixed(2) + ' %';
    }

    private tabellaCosti(numeratore: string | number, denominatore: string | number) {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '100%' });
        rows.push([format(numeratore, bold, 'center', fontSize, italics)]);
        rows.push([format('------------------------- = ', bold, 'center', fontSize, italics)]);
        rows.push([format(denominatore, bold, 'center', fontSize, italics)]);
        return fxTabella(cols, rows);
    }

    private rowTabella(intestazione: string, numeratore: string | number, denominatore: string | number) {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '70%' });
        cols.push({ header: '', width: '30%' });
        rows.push([Indent(8, '\n ' + intestazione + ' = '), this.tabellaCosti(numeratore, denominatore)]);
        return fxTabella(cols, rows);
    }

    private tabellaFormulaMediaPonderataPrevalenza(): Content {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '55%' });
        cols.push({ header: '', width: '45%' });
        rows.push([this.tabellaCosti('(A1*X%)+(B9*Y%)+(B6*W%)+(B7*K%)', 'A1 + B9 + B6 + B7'), this.tabellaCosti('(A1 soci+B9 soci+B6 soci+B7 soci)', 'A1 + B9 + B6 +B7')]);
        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // CATEGORIE SOCI E BASE SOCIALE --- CATEGORIE SOCI E BASE SOCIALE --- CATEGORIE SOCI E BASE SOCIALE --- CATEGORIE SOCI E BASE SOCIALE --- CATEGORIE SOCI E BASE SOCIALE ---
    // ***************************************************************************************************************************************************************************

    private tabellaSociPresenti(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '29%' });
        cols.push({ header: '', width: '12%' });
        cols.push({ header: '', width: '13%' });
        cols.push({ header: '', width: '17%' });
        cols.push({ header: '', width: '17%' });
        cols.push({ header: '', width: '12%' });

        rows.push([
            empty(),
            format('n. soci cooperatori persone fisiche', bold, 'center', 9, italics),
            format('n. soci cooperatori persone giuridiche', bold, 'center', 9, italics),
            format('n. soci finanziatori (persone fisiche) e sottoscrittori di titoli di debito', bold, 'center', 9, italics),
            format('n. soci finanziatori (persone giuridiche) e sottoscrittori di titoli di debito', bold, 'center', 9, italics),
            format('n. soci ammessi a categorie speciali', bold, 'center', 9, italics),
        ]);
        rows.push([
            'Alla data della revisione',
            this.getValueFromKeyWithWhitespace('my_SociFisCoopData'),
            this.getValueFromKeyWithWhitespace('my_SociGiuCoopData'),
            this.getValueFromKeyWithWhitespace('my_SociFisFinData'),
            this.getValueFromKeyWithWhitespace('my_SociGiuFinData'),
            this.getValueFromKeyWithWhitespace('my_SociSpecData'),
        ]);
        rows.push([
            'Al termine del primo esercizio precedente ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_Data1EsPrec')),
            this.getValueFromKeyWithWhitespace('my_SociFisCoopPrimo'),
            this.getValueFromKeyWithWhitespace('my_SociGiuCoopPrimo'),
            this.getValueFromKeyWithWhitespace('my_SociFisFinPrimo'),
            this.getValueFromKeyWithWhitespace('my_SociGiuFinPrimo'),
            this.getValueFromKeyWithWhitespace('my_SociSpecPrimo'),
        ]);
        rows.push([
            'Al termine del secondo esercizio precedente ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_Data2EsPrec')),
            this.getValueFromKeyWithWhitespace('my_SociFisCoopSecondo'),
            this.getValueFromKeyWithWhitespace('my_SociGiuCoopSecondo'),
            this.getValueFromKeyWithWhitespace('my_SociFisFinSecondo'),
            this.getValueFromKeyWithWhitespace('my_SociGiuFinSecondo'),
            this.getValueFromKeyWithWhitespace('my_SociSpecSecondo'),
        ]);

        return fxTabella(cols, rows, true);
    }

    private tabellaTreUnitaSociale(): Content {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '23%' });

        cols.push({ header: '', width: '55%' });
        cols.push({ header: '', width: '22%' });

        console.log('my_UnitaSociUtil', this.getValueFromKeyWithWhitespace('my_UnitaSociUtil'));
        console.log('my_UnitaSociUtil', this.getValueFromKeyWithWhitespace('my_UnitaNonSociUtil'));

        rows.push([
            "22 - Unità occupate nell'attività sociale alla data della revisione:",
            'Soci n. ' + this.getValueFromKeyWithWhitespace('my_UnitaSociUtil'),
            'Non Soci n. ' + this.getValueFromKeyWithWhitespace('my_UnitaNonSociUtil'),
        ]);
        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO --- BILANCIO ---
    // ***************************************************************************************************************************************************************************

    private miniTabDoppiaBilancio(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });
        rows.push([
            format(
                'Anno ' + this.getValueFromKeyWithWhitespace('my_AnnoPrimoEs') + ' Importo ' + this.getValueFromKeyWithWhitespace('my_ImportoPatrPrimoEs'),
                bold,
                'center',
                11,
                italics
            ),
            format(
                'Anno ' + this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs') + ' Importo ' + this.getValueFromKeyWithWhitespace('my_ImportoPatrPrimoEs'),
                bold,
                'center',
                11,
                italics
            ),
        ]);
        return fxTabella(cols, rows, true);
    }

    private miniTabQuattroBilancio(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '30%' });

        rows.push(['Capitale sociale sottoscritto', 'versato(gg/mm/aaaa)', 'Capitale sociale sottoscritto', 'versato(gg/mm/aaaa)']);
        rows.push([
            this.getValueFromKeyWithWhitespace('my_CapSocSottoscrPrimoEs'),
            this.getValueFromKeyWithWhitespace('my_CapSocVersatoPrimoEs') + ' (' + this.formatDate(this.getValueFromKeyWithWhitespace('my_AnnoPCapSoc')) + ')',
            this.getValueFromKeyWithWhitespace('my_CapSocSottoscrSecondoEs'),
            this.getValueFromKeyWithWhitespace('my_CapSocVersatoSecondoEs') + ' (' + this.formatDate(this.getValueFromKeyWithWhitespace('my_AnnoSCapSoc')) + ')',
        ]);

        return fxTabella(cols, rows, true);
    }

    private miniTabDoppiaCapSociale(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '50%' });

        rows.push([
            '29 - Il capitale sociale è suddiviso in:',
            checkSingoloInterno(this.getValueFromKey('my_CkSuddivCapitSoc'), '1') + '  quote',
            Indent(3, '- Valore Minimo: € ' + this.getValueFromKeyWithWhitespace('my_ValoreNomSuddivCapit')),
        ]);
        rows.push([empty(), checkSingoloInterno(this.getValueFromKey('my_CkSuddivCapitSoc'), '2') + '  azioni', empty()]);

        return fxTabella(cols, rows);
    }

    private tabellaControlloRistorni(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([
            format(
                'SCHEDA DI CONTROLLO PER LA VERIFICA DEI RISTORNI   (BILANCIO CHIUSO AL ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataChiusura2')) + ')',
                true,
                alignment,
                10,
                italics
            ),
        ]);
        rows.push([
            format(
                "Il revisore dovrà effettuare la verifica per l'esercizio relativo all'ultimo bilancio approvato e per quello relativo all'esercizio precedente.",
                bold,
                alignment,
                8,
                italics
            ),
        ]);

        rows.push([this.tabellaControlloRistorniInterna()]);

        rows.push([format('N.B', true, 'left', 9, italics)]);
        rows.push([format('1) - I valori di E) e di F) e della somma di [ G)+H)+I) ] non possono essere superiori al valore di B)', bold, alignment, 9, italics)]);
        rows.push([format('2) - D) non può essere superiore né a B) né al 30% di C)', bold, alignment, 9, italics)]);
        rows.push([
            format(
                "3) - La percentuale di prevalenza va eventualmente ricalcolata, depurandola dell'eventuale ristorno già imputato a costo e dai valori relativi all'eventuale fruizione di regimi derogatori della prevalenza da parte dell'ente.",
                bold,
                alignment,
                9,
                italics
            ),
        ]);
        rows.push([
            format(
                '4) - Il revisore valuterà eventuali scostamenti qualora la cooperativa produca una dettagliata contabilità analitica per centri di costo, accesi a soci e non soci. ',
                bold,
                alignment,
                9,
                italics
            ),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaControlloRistorniInterna(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '70%' });
        colsTmp.push({ header: '', width: '15%' });
        colsTmp.push({ header: '', width: '15%' });

        rowsTmp.push([empty(), 'Esercizio ' + this.getValueFromKeyWithWhitespace('my_BilAnno2'), 'Esercizio ' + this.getValueFromKeyWithWhitespace('my_BilAnno1')]);
        rowsTmp.push(['VERIFICA AVANZO DI GESTIONE', empty(), empty()]);
        rowsTmp.push([
            'Avanzo di gestione: Rigo 21 del Conto Economico',
            '€ ' + this.getValueFromKeyWithWhitespace('my_Rigo23ContoEc'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_Rigo23ContoEcAnno1'),
        ]);
        rowsTmp.push(['letiazioni in aumento:', empty(), empty()]);
        rowsTmp.push([
            '- eventuali ristorni imputati a conto economico',
            '€ ' + this.getValueFromKeyWithWhitespace('my_RistorniContoEc'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_RistorniContoEcAnno1'),
        ]);
        rowsTmp.push(['letiazioni in riduzione:', empty(), empty()]);
        rowsTmp.push([
            '- eventuale D - Rettifiche di valore di attività finanziarie del C.E. (solo se saldo positivo) ',
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventualeDContoEc'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventualeDContoEcAnno1'),
        ]);
        rowsTmp.push([
            '- eventuali Proventi straordinari del C.E.¹',
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventualeEContoEc'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventualeEContoEcAnno1'),
        ]);
        rowsTmp.push([
            'A) Avanzo di gestione rettificato',
            '€ ' + this.getValueFromKeyWithWhitespace('my_AvanzoDiGestioneRigo23'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_AvanzoDiGestioneRigo23Anno1'),
        ]);
        rowsTmp.push([
            "B) Avanzo di gestione generato dai soci = A) x percentuale di prevalenza relativa all'attività effettivamente svolta con/a favore dei soci. ",
            '€ ' + this.getValueFromKeyWithWhitespace('my_AvanzoDiGestionePrevalenza'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_AvanzoDiGestionePrevalenzaAnno1'),
        ]);
        rowsTmp.push(['VERIFICA IMPORTO DEL RISTORNO', empty(), empty()]);
        rowsTmp.push([format('Cooperative di Lavoro', true, alignment, fontSize, italics), empty(), empty()]);
        rowsTmp.push([
            'C) Trattamenti retributivi complessivi corrisposti ai soci',
            '€ ' + this.getValueFromKeyWithWhitespace('my_CostoPersCoopLav'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_CostoPersCoopLavAnno1'),
        ]);
        rowsTmp.push(['D) Ristorno', '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopLav'), '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopLavAnno1')]);
        rowsTmp.push([format('Cooperative di Utenza', true, alignment, fontSize, italics), empty(), empty()]);
        rowsTmp.push(['E) Ristorno', '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopUt'), '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopUtAnno1')]);
        rowsTmp.push([format('Cooperative di Apporto', true, alignment, fontSize, italics), empty(), empty()]);
        rowsTmp.push(['F) Ristorno', '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopAp'), '€ ' + this.getValueFromKeyWithWhitespace('my_RistornoCoopApAnno1')]);
        rowsTmp.push([empty(), empty(), empty()]);
        rowsTmp.push([
            'G) Liquidazioni salariali o del prezzo o del servizio',
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoLiquidazioniSalariali'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoLiquidazioniSalarialiAnno1'),
        ]);
        rowsTmp.push([
            'H) Aumento gratuito del Capitale Sociale',
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoAumentoGratuito'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoAumentoGratuitoAnno1'),
        ]);
        rowsTmp.push([
            'I) Distribuzione gratuita di titoli (art. 5, Legge n. 59/1992) ',
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoDistribuzioneGratuita'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoDistribuzioneGratuitaAnno1'),
        ]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaVerificaContabilePrestito(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push(['SCHEDA DI CONTROLLO PER VERIFICA CONTABILE DEL PRESTITO DA SOCI']);
        rows.push([lineaVuota()]);
        rows.push([this.tabellaVerificaContabilePrestitoInterna()]);
        rows.push([lineaVuota()]);
        rows.push([
            format(
                "1) Il valore del patrimonio da assumere a riferimento per la raccolta del prestito sociale dovrà essere quello risultante dal bilancio consolidato, qualora la società abbia l'obbligo di redigerlo. Se la società, pur avendo l'obbligo di redigere il bilancio consolidato, rientra in una delle ipotesi di esonero previste dalla legge, dovrà considerare il valore del patrimonio individuale rettificato degli effetti derivanti da operazioni con società controllate che sarebbero state elise se fosse stato redatto il bilancio consolidato. Tali rettifiche devono essere illustrate in un prospetto incluso nella nota integrativa del bilancio. ",
                bold,
                alignment,
                8,
                italics
            ),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaVerificaContabilePrestitoInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '70%' });
        cols.push({ header: '', width: '30%' });

        rows.push(["a) patrimonio netto dell'ultimo bilancio¹", '€ ' + this.getValueFromKeyWithWhitespace('my_PatrNettoUltBil')]);
        rows.push(['b) prestito sociale alla data della revisione', '€ ' + this.getValueFromKeyWithWhitespace('my_PrestitoSociale')]);
        rows.push(['c) rapporto CICR ( b / a )', this.getValueFromKeyWithWhitespace('my_CICR')]);
        rows.push(['numero soci alla data della revisione', this.getValueFromKeyWithWhitespace('my_NumSociDataDich')]);
        rows.push(["Gg/mm/aa di chiusura dell'ultimo esercizio", this.formatDate(this.getValueFromKeyWithWhitespace('my_DataChiusEser'))]);

        return fxTabella(cols, rows);
    }

    private tabellaEntiErogatori(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '33%' });
        cols.push({ header: '', width: '33%' });
        cols.push({ header: '', width: '33%' });

        rows.push([
            format('Tipologia', true, 'center', fontSize, italics),
            format('Ente erogatore', true, 'center', fontSize, italics),
            format('Importo', true, 'center', fontSize, italics),
        ]);

        const array = this.getArrayFromKey('my_ElEnteErogatore');
        for (let c = 0; c < array.length; c++) {
            rows.push([
                this.getValueFromKeyWithWhitespace('my_SpecificaTipologia', array[c]),
                this.getValueFromKeyWithWhitespace('my_Ente', array[c]),
                this.getValueFromKeyWithWhitespace('my_Importo', array[c]),
            ]);
        }

        return fxTabella(cols, rows, true);
    }

    private tabellaSocietaPartecipate(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format('Società Partecipata', true, 'center', fontSize, italics),
            format('Importo di capitale sottoscritto', true, 'center', fontSize, italics),
            format('% se trattasi di Spa o di Srl', true, 'center', fontSize, italics),
        ]);

        const array = this.getArrayFromKey('my_ElSocietaPartecipate');
        for (let c = 0; c < array.length; c++) {
            rows.push([
                this.getValueFromKeyWithWhitespace('my_SocPartec', array[c]),
                this.getValueFromKeyWithWhitespace('my_ImpCapSott', array[c]),
                this.getValueFromKeyWithWhitespace('my_PercSpaSrl', array[c]),
            ]);
        }

        return fxTabella(cols, rows, true);
    }

    // ***************************************************************************************************************************************************************************
    // RAGGIUNGIMENTO DELLO SCOPO SOCIALE --- RAGGIUNGIMENTO DELLO SCOPO SOCIALE --- RAGGIUNGIMENTO DELLO SCOPO SOCIALE --- RAGGIUNGIMENTO DELLO SCOPO SOCIALE ---
    // ***************************************************************************************************************************************************************************

    private tabellaIndicatoriBilancio(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });

        rows.push([empty(), 'Esercizio N ' + this.getValueFromKeyWithWhitespace('my_BilAnno2'), 'Esercizio N-1 ' + this.getValueFromKeyWithWhitespace('my_BilAnno1')]);
        rows.push([this.tabellaIndicatoriBilancioAAASubSX(), this.tabellaIndicatoriBilancioAAASubCE(), this.tabellaIndicatoriBilancioAAASubDX()]);
        rows.push([this.tabellaIndicatoriBilancioBBBSubSX(), this.tabellaIndicatoriBilancioBBBSubCE(), this.tabellaIndicatoriBilancioBBBSubDX()]);
        rows.push([this.tabellaIndicatoriBilancioCCCSubSX(), this.tabellaIndicatoriBilancioCCCSubCE(), this.tabellaIndicatoriBilancioCCCSubDX()]);
        rows.push([this.tabellaIndicatoriBilancioDDDSubSX(), this.tabellaIndicatoriBilancioDDDSubCE(), this.tabellaIndicatoriBilancioDDDSubDX()]);
        rows.push([this.tabellaIndicatoriBilancioEEESubSX(), this.tabellaIndicatoriBilancioEEESubCE(), this.tabellaIndicatoriBilancioEEESubDX()]);
        rows.push([this.tabellaIndicatoriBilancioFFFSubSX(), this.tabellaIndicatoriBilancioFFFSubCE(), this.tabellaIndicatoriBilancioFFFSubDX()]);

        return fxTabella(cols, rows, true);
    }

    private tabellaIndicatoriBilancioAAASubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('a) - ', false, false, 10, 'Attivo corrente netto', true, false, 10)]);
        rows.push(["(Attivo circolante – Debiti entro l'eserc. Succ.):"]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioAAASubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioAAASubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioBBBSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('b) - ', false, false, 10, 'Margine di tesoreria', true, false, 10)]);
        rows.push(["[(Attivo circolante – Rimanenze) - Debiti entro l'eserc. Succ.)]"]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioBBBSubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioBBBSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioCCCSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('c) - ', bold1, italics1, fontSize1, ' Quoziente primario di struttura ', true, italics2, fontSize2, alignment)]);
        rows.push([format('Patrimonio netto (A del Passivo dello SP)', bold, alignment, fontSize, italics)]);
        rows.push([format('___________________________', bold, alignment, fontSize, italics)]);
        rows.push([format("Immobilizzazioni (B dell'Attivo dello SP)", bold, alignment, fontSize, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioCCCSubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioCCCSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioDDDSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('d) - ', bold1, italics1, fontSize1, ' Indice di autonomia finanziaria ', true, italics2, fontSize2, alignment)]);
        rows.push([format('Patrimonio netto (Totale A del Passivo dello SP)', bold, alignment, fontSize, italics)]);
        rows.push([format('___________________________', bold, alignment, fontSize, italics)]);
        rows.push([format('Totale Passivo dello SP', bold, alignment, fontSize, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioDDDSubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioDDDSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioEEESubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('e) - ', bold1, italics1, fontSize1, ' Reddito Operativo ', true, italics2, fontSize2, alignment)]);
        rows.push([format('(Valore della Produzione [A del CE] - Costi della Produzione [B del CE] eventualmente depurati dei Ristorni.', bold, alignment, 10, italics)]);
        rows.push([format("L'indicatore deve essere depurato dagli eventi di carattere eccezionale", bold, alignment, 9, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioEEESubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioEEESubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioFFFSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('f) - ', bold1, italics1, fontSize1, ' Incidenza oneri finanziari su ricavi ', true, italics2, fontSize2, alignment)]);
        rows.push([format('Incidenza oneri finanziari su ricavi', bold, alignment, fontSize, italics)]);
        rows.push([format('Risultato gestione finanziaria [C del CE]', bold, alignment, fontSize, italics)]);
        rows.push([format('Ricavi vendite e prestazioni[A1 del CE]', bold, alignment, fontSize, italics)]);
        rows.push([format("L'indicatore deve essere depurato dagli eventi di carattere eccezionale", bold, alignment, 9, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioFFFSubCE(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIndicatoriBilancioFFFSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SISTEMA DI AMMINISTRAZIONE E CONTROLLO --- SISTEMA DI AMMINISTRAZIONE E CONTROLLO --- SISTEMA DI AMMINISTRAZIONE E CONTROLLO --- SISTEMA DI AMMINISTRAZIONE E CONTROLLO ---
    // ***************************************************************************************************************************************************************************

    private tabellaSistemaAmministrazioneControllo(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '*' });

        rows.push(['Norme sulle S.r.l.', checkSingolo(this.getValueFromKey('my_StampaMinistero_ChkSRL'))]);
        rows.push(['Norme sulle S.p.a. - Sistema ordinario', checkSingolo(this.getValueFromKey('my_StampaMinistero_ChkSPO'))]);
        rows.push(['Norme sulle S.p.a. - Sistema dualistico', checkSingolo(this.getValueFromKey('my_StampaMinistero_ChkSPD'))]);
        rows.push(['Norme sulle S.p.a. - Sistema monistico', checkSingolo(this.getValueFromKey('my_StampaMinistero_ChkSPM'))]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // ***************************************************************************************************************************************************************************
    // SCHEDA AMMINISTRAZIONE E CONTROLLO --- SCHEDA AMMINISTRAZIONE E CONTROLLO --- SCHEDA AMMINISTRAZIONE E CONTROLLO --- SCHEDA AMMINISTRAZIONE E CONTROLLO ---
    // ***************************************************************************************************************************************************************************
    // ***************************************************************************************************************************************************************************

    private creaSchedaAmmControllo(numeroScheda) {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        switch (numeroScheda) {
            case 1:
                // INTESTAZIONE
                rows.push([format('SCHEDA 1 - L’ENTE ADOTTA LE NORME DELLE S.R.L.', true, 'center', fontSize, italics)]);
                rows.push([this.A_intestazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                // ASSEMBLEA DEI SOCI
                rows.push([Indent(1, format('ASSEMBLEA DEI SOCI', true, alignment, fontSize, italics))]);
                rows.push([this.B_assembleaSociScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //CONSIGLIO DI AMMINISTRAZIONE
                rows.push([
                    Indent(
                        1,
                        doubleFormat(
                            'CONSIGLIO DI AMMINISTRAZIONE \n',
                            true,
                            italics1,
                            fontSize1,
                            'Composizione, carica ricoperta e data di elezione',
                            bold2,
                            italics2,
                            9,
                            alignment
                        )
                    ),
                ]);
                rows.push([this.C_consiglioAmministrazioneHeader()]);
                rows.push([this.C_consiglioAmministrazioneDati()]);
                rows.push([this.C_consiglioAmministrazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //AMMINISTRATORE UNICO
                rows.push([
                    Indent(
                        1,
                        doubleFormat('AMMINISTRATORE UNICO ', true, italics1, fontSize, ' (parte da compilare in alternativa al Consiglio di Amministrazione)', bold2, true, 8)
                    ),
                ]);
                rows.push([this.D_amministratoreUnicoHeader()]);
                rows.push([this.D_amministratoreUnicoDati()]);
                rows.push([this.D_amministratoreUnicoScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //LIQUIDATORI
                rows.push([Indent(1, doubleFormat('LIQUIDATORI ', true, italics1, fontSize, ' (per cooperative in liquidazione):', bold2, italics2, 9))]);
                rows.push([this.E_liquidatoriHeader()]);
                rows.push([this.E_liquidatoriDati()]);
                rows.push([lineaVuota()]);
                rows.push([this.E_liquidatoriScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //ORGANI CONTROLLO
                rows.push([Indent(1, format('ORGANO DI CONTROLLO ', true, alignment, fontSize, italics))]);
                rows.push([this.F_organoControlloHeader()]);
                rows.push([this.F_organoControlloDati()]);
                rows.push([lineaVuota()]);
                rows.push([this.F_organoControlloScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //REVISIONE LEGALE
                rows.push([Indent(2, 'REVISIONE LEGALE')]);
                rows.push([this.G_RevisioneLegaleScheda(numeroScheda)]);
                break;

            case 2:
                // INTESTAZIONE
                rows.push([format('SCHEDA 2 - L’ENTE ADOTTA LE NORME DELLE S.P.A. - SISTEMA ORDINARIO', true, 'center', fontSize, italics)]);
                rows.push([this.A_intestazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                // ASSEMBLEA DEI SOCI
                rows.push([Indent(1, format('ASSEMBLEA DEI SOCI', true, alignment, fontSize, italics))]);
                rows.push([this.B_assembleaSociScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //CONSIGLIO DI AMMINISTRAZIONE
                rows.push([
                    Indent(
                        1,
                        doubleFormat(
                            'CONSIGLIO DI AMMINISTRAZIONE \n',
                            true,
                            italics1,
                            fontSize1,
                            'Composizione, carica ricoperta e data di elezione',
                            bold2,
                            italics2,
                            9,
                            alignment
                        )
                    ),
                ]);
                rows.push([this.C_consiglioAmministrazioneHeader()]);
                rows.push([this.C_consiglioAmministrazioneDati()]);
                rows.push([this.C_consiglioAmministrazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //AMMINISTRATORE UNICO
                rows.push([
                    Indent(
                        1,
                        doubleFormat('AMMINISTRATORE UNICO ', true, italics1, fontSize, ' (parte da compilare in alternativa al Consiglio di Amministrazione)', bold2, true, 9)
                    ),
                ]);
                rows.push([this.D_amministratoreUnicoHeader()]);
                rows.push([this.D_amministratoreUnicoDati()]);
                rows.push([this.D_amministratoreUnicoScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //LIQUIDATORI
                rows.push([Indent(1, doubleFormat('LIQUIDATORI ', true, italics1, fontSize, ' (per cooperative in liquidazione):', bold2, italics2, 8))]);
                rows.push([this.E_liquidatoriHeader()]);
                rows.push([this.E_liquidatoriDati()]);
                rows.push([this.E_liquidatoriScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //ORGANI CONTROLLO
                rows.push([Indent(1, format('ORGANO DI CONTROLLO ', true, alignment, fontSize, italics))]);
                rows.push([this.F_organoControlloHeader()]);
                rows.push([this.F_organoControlloDati()]);
                rows.push([this.F_organoControlloScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //REVISIONE LEGALE
                rows.push([Indent(2, 'REVISIONE LEGALE')]);
                rows.push([this.G_RevisioneLegaleScheda(numeroScheda)]);
                break;

            case 3:
                // INTESTAZIONE
                rows.push([format('SCHEDA 3 - L’ENTE ADOTTA LE NORME DELLE S.P.A. - SISTEMA DUALISTICO', true, 'center', fontSize, italics)]);
                rows.push([this.A_intestazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                // ASSEMBLEA DEI SOCI
                rows.push([Indent(1, format('ASSEMBLEA DEI SOCI', true, alignment, fontSize, italics))]);
                rows.push([this.B_assembleaSociScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //CONSIGLIO DI SORVEGLIANZA
                rows.push([Indent(1, format('CONSIGLIO DI SORVEGLIANZA', true, alignment, fontSize, italics))]);
                rows.push([this.H_consiglioSorveglianzaHeader()]);
                rows.push([this.H_consiglioSorveglianzaDati()]);
                rows.push([this.H_consiglioSorveglianzaScheda()]);
                rows.push([lineaVuota()]);
                //CONSIGLIO DI GESTIONE
                rows.push([
                    Indent(
                        1,
                        doubleFormat('CONSIGLIO DI GESTIONE ', true, italics1, fontSize, ' (parte da compilare in alternativa al Consiglio di Amministrazione)', bold2, true, 9)
                    ),
                ]);
                rows.push([this.I_consiglioGestioneHeader()]);
                rows.push([this.I_consiglioGestioneDati()]);
                rows.push([this.I_consiglioGestioneScheda()]);
                rows.push([lineaVuota()]);
                //LIQUIDATORI
                rows.push([Indent(1, doubleFormat('LIQUIDATORI ', true, italics1, fontSize, ' (per cooperative in liquidazione):', bold2, italics2, 8))]);
                rows.push([this.E_liquidatoriHeader()]);
                rows.push([this.E_liquidatoriDati()]);
                rows.push([this.E_liquidatoriScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //REVISIONE LEGALE
                rows.push([Indent(2, 'REVISIONE LEGALE')]);
                rows.push([this.G_RevisioneLegaleScheda(numeroScheda)]);
                break;

            case 4:
                // INTESTAZIONE
                rows.push([format('SCHEDA 4 - L’ENTE ADOTTA LE NORME DELLE S.P.A. - SISTEMA MONISTICO', true, 'center', fontSize, italics)]);
                rows.push([this.A_intestazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                // ASSEMBLEA DEI SOCI
                rows.push([Indent(1, format('ASSEMBLEA DEI SOCI', true, alignment, fontSize, italics))]);
                rows.push([this.B_assembleaSociScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //CONSIGLIO DI AMMINISTRAZIONE
                rows.push([Indent(1, format('CONSIGLIO DI AMMINISTRAZIONE \n', true, alignment, fontSize, italics))]);
                rows.push([this.C_consiglioAmministrazioneHeader()]);
                rows.push([this.C_consiglioAmministrazioneDati()]);
                rows.push([this.C_consiglioAmministrazioneScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //COMITATO CONTROLLO GESTIONE
                rows.push([Indent(1, format('COMITATO DI CONTROLLO SULLA GESTIONE ', true, alignment, fontSize, italics))]);
                rows.push([this.J_comitatoControlloGestioneHeader()]);
                rows.push([this.J_comitatoControlloGestioneDati()]);
                rows.push([this.J_comitatoControlloGestioneScheda()]);
                rows.push([lineaVuota()]);
                //REVISIONE LEGALE
                rows.push([Indent(2, 'REVISIONE LEGALE')]);
                rows.push([this.G_RevisioneLegaleScheda(numeroScheda)]);
                rows.push([lineaVuota()]);
                //LIQUIDATORI
                rows.push([Indent(1, doubleFormat('LIQUIDATORI ', true, italics1, fontSize, ' (per cooperative in liquidazione):', bold2, italics2, 8))]);
                rows.push([this.E_liquidatoriHeader()]);
                rows.push([this.E_liquidatoriDati()]);
                rows.push([this.E_liquidatoriScheda(numeroScheda)]);
                break;
        }

        return fxTabella(cols, rows);
    }

    private A_intestazioneScheda(numeroScheda) {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '4%' });
        colsTmp.push({ header: '', width: '80%' });
        colsTmp.push({ header: '', width: '8%' });
        colsTmp.push({ header: '', width: '8%' });

        switch (numeroScheda) {
            case 1:
                rowsTmp.push([
                    format('1.', bold, 'right', 9, italics),
                    'La cooperativa ha meno di nove soci?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCoop9soci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCoop9soci'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('2.', bold, 'right', 9, italics),
                    'La cooperativa ha meno di venti soci o un attivo patrimoniale inferiore a un milione di euro?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCoop20soci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCoop20soci'), '0') + ' No',
                ]);
                break;

            case 2:
                rowsTmp.push([
                    format('1.', bold, 'right', 9, italics),
                    'La cooperativa ha almeno nove soci?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCoop9soci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCoop9soci'), '0') + ' No',
                ]);
                break;

            case 3:
                rowsTmp.push([
                    format('1.', bold, 'right', 9, italics),
                    'La cooperativa ha almeno nove soci?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdCoop9soci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdCoop9soci'), '0') + ' No',
                ]);
                break;

            case 4:
                rowsTmp.push([
                    format('1.', bold, 'right', 9, italics),
                    'La cooperativa ha almeno nove soci?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmCoop9soci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmCoop9soci'), '0') + ' No',
                ]);
                break;
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private B_assembleaSociScheda(numeroScheda) {
        let colsTmp = [];
        let rowsTmp = [];

        switch (numeroScheda) {
            case 1:
                colsTmp.push({ header: '', width: '4%' });
                colsTmp.push({ header: '', width: '80%' });
                colsTmp.push({ header: '', width: '8%' });
                colsTmp.push({ header: '', width: '8%' });

                rowsTmp.push([
                    format('3.', bold, 'right', 9, italics),
                    'L’Assemblea approva regolarmente il bilancio?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembAppBil'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembAppBil'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('4.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato gli Amministratori/Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('5.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato l’Organo di controllo?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominSind'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominSind'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('6.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato l’Organo cui spetta eventualmente la revisione legale ai sensi dell’art. 2409-bis c.c.?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominOrgano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembNominOrgano'), '0') + ' No',
                ]);
                rowsTmp.push([format('7.', bold, 'right', 9, italics), 'Se lo Statuto non prevede la gratuità delle cariche, l’Assemblea ha determinato', empty(), empty()]);
                rowsTmp.push([
                    empty(),
                    Indent(4, 'il compenso degli amministratori?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembDetCompAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembDetCompAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    empty(),
                    Indent(4, "il compenso dell'organo di controllo?"),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembDetCompSindaci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAssembDetCompSindaci'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('8.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Assemblea o su altre forme di raccolta del consenso dei soci: \n' +
                        this.getValueFromKeyWithWhitespace('my_SrlNoteFunzAssemb'),
                    empty(),
                    empty(),
                ]);
                break;

            case 2:
                colsTmp.push({ header: '', width: '4%' });
                colsTmp.push({ header: '', width: '80%' });
                colsTmp.push({ header: '', width: '8%' });
                colsTmp.push({ header: '', width: '8%' });

                rowsTmp.push([
                    format('2.', bold, 'right', 9, italics),
                    'L’Assemblea ha approvato regolarmente il bilancio?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembAppBil'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembAppBil'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('3.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato regolarmente gli Amministratori/Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('4.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato l’Organo di controllo?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominSind'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominSind'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('5.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato l’Organo cui spetta la revisione legale ai sensi dell’art. 2409-bis c.c.?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominOrgano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembNominOrgano'), '0') + ' No',
                ]);
                rowsTmp.push([format('6.', bold, 'right', 9, italics), 'Se lo Statuto non prevede la gratuità delle cariche, l’Assemblea ha determinato', empty(), empty()]);
                rowsTmp.push([
                    empty(),
                    Indent(4, 'il compenso degli amministratori?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembDetCompAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembDetCompAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    empty(),
                    Indent(4, "il compenso dell'Organo di controllo?"),
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembDetCompSindaci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAssembDetCompSindaci'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('7.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Assemblea: \n' + this.getValueFromKeyWithWhitespace('my_SpoNoteFunzAssemb'),
                    empty(),
                    empty(),
                ]);
                break;

            case 3:
                colsTmp.push({ header: '', width: '4%' });
                colsTmp.push({ header: '', width: '80%' });
                colsTmp.push({ header: '', width: '8%' });
                colsTmp.push({ header: '', width: '8%' });

                rowsTmp.push([
                    format('2.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato i consiglieri di sorveglianza/Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembNominSorv'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembNominSorv'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('3.', bold, 'right', 9, italics),
                    'L’Assemblea determina il compenso spettante ai consiglieri di sorveglianza, se non è stato stabilito dallo Statuto?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembDetCompSorv'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembDetCompSorv'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('4.', bold, 'right', 9, italics),
                    'L’Assemblea delibera sulla distribuzione degli utili?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembDelibDistribUtil'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpdAssembDelibDistribUtil'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('5.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Assemblea: \n' + this.getValueFromKeyWithWhitespace('my_SpdNoteComposAssemb'),
                    empty(),
                    empty(),
                ]);
                break;

            case 4:
                colsTmp.push({ header: '', width: '4%' });
                colsTmp.push({ header: '', width: '80%' });
                colsTmp.push({ header: '', width: '8%' });
                colsTmp.push({ header: '', width: '8%' });

                rowsTmp.push([
                    format('2.', bold, 'right', 9, italics),
                    'L’Assemblea approva il bilancio? ',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembAppBil'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembAppBil'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('3.', bold, 'right', 9, italics),
                    'L’Assemblea ha nominato regolarmente gli Amministratori/Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembNominAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembNominAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('4.', bold, 'right', 9, italics),
                    'l’Assemblea ha nominato l’organo cui spetta la revisione legale ai sensi dell’art. 2409-bis c.c.?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembNominOrgano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembNominOrgano'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('5.', bold, 'right', 9, italics),
                    'Se lo Statuto non prevede la gratuità delle cariche, l’Assemblea ha determinato il compenso degli amministratori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembDetCompAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAssembDetCompAmmin'), '0') + ' No',
                ]);
                rowsTmp.push([
                    format('6.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Assemblea: \n' + this.getValueFromKeyWithWhitespace('my_SpmNoteFunzAssemb'),
                    empty(),
                    empty(),
                ]);
                break;
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private C_consiglioAmministrazioneHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '18%' });
        colsTmp.push({ header: '', width: '10%' });

        rowsTmp.push(['Carica', 'Cognome', 'Nome', 'Data Elezione', 'Socio']);

        return fxTabella(colsTmp, rowsTmp);
    }

    private C_consiglioAmministrazioneDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '24%' });
        colsTmp.push({ header: '', width: '18%' });
        colsTmp.push({ header: '', width: '10%' });

        const array = this.getArrayFromKey('my_AllElemConsiglioAmministrazione');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                this.getValueFromKeyWithWhitespace('my_AllConsAmmCaricaDesc', array[c]),
                this.getValueFromKeyWithWhitespace('my_AllConsAmmCognome', array[c]),
                this.getValueFromKeyWithWhitespace('my_AllConsAmmNome', array[c]),
                this.formatDate(this.getValueFromKeyWithWhitespace('my_AllConsAmmDataElezione', array[c])),
                checkSingoloInterno(this.getValueFromKey('my_AllConsAmmCkSocio'), '1') +
                    ' Si' +
                    ' ' +
                    checkSingoloInterno(this.getValueFromKey('my_AllConsAmmCkSocio'), '0') +
                    ' No',
            ]);
        }

        return fxTabella(colsTmp, rowsTmp);
    }

    private C_consiglioAmministrazioneScheda(numeroScheda) {
        let colsTmpp = [];
        let rowsTmpp = [];

        switch (numeroScheda) {
            case 1:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('9.', bold, 'right', 9, italics),
                    'La maggioranza degli amministratori è composta da soci cooperatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlMaggAmminSoci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlMaggAmminSoci'), '0') + ' No',
                ]);
                rowsTmpp.push([format('10.', bold, 'right', 9, italics), 'Lo statuto prevede forme di \n', empty(), empty()]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(1, 'Amministrazione congiuntiva?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlStatutoFormeAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlStatutoFormeAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(1, 'Amministrazione disgiuntiva?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlStatutoFormeAmminDisg'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlStatutoFormeAmminDisg'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('11.', bold, 'right', fontSize, italics),
                    'In caso di risposta affermativa al punto precedente descriverne le modalità:' + this.getValueFromKeyWithWhitespace('my_SrlModalitaFormeAmmin'),
                    empty(),
                    empty(),
                ]);
                rowsTmpp.push([
                    format('12.', bold, 'right', 9, italics),
                    'Gli Amministratori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAmminCarattMutual'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlAmminCarattMutual'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('13.', bold, 'right', 9, italics),
                    'L’Organo di controllo partecipa alle riunioni del Consiglio di Amministrazione?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciConsAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciConsAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('14.', bold, 'right', 9, italics),
                    'Vi sono componenti del Consiglio di Amministrazione indicati dallo Stato o da enti pubblici?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompStatoInConsAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompStatoInConsAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(4, ' - Vi sono componenti del Consiglio di Amministrazione eletti dai possessori degli strumenti finanziari emessi dall’ente?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompElettiStrFinan'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompElettiStrFinan'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(4, ' - Se sì, viene rispettato il limite di un terzo?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompElettiLimite'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompElettiLimite'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('15.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento del Consiglio di Amministrazione:' +
                        this.getValueFromKeyWithWhitespace('my_SrlNoteComposConsAmmin'),
                    empty(),
                    empty(),
                ]);
                break;

            case 2:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('8.', bold, 'right', 9, italics),
                    'La maggioranza degli amministratori è composta da soci cooperatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoMaggAmminSoci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoMaggAmminSoci'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('9.', bold, 'right', 9, italics),
                    'E’ stato nominato un Comitato Esecutivo?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoNominComitEsec'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoNominComitEsec'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('10.', bold, 'right', 9, italics),
                    'Indicare le attribuzioni che gli sono state delegate: \n' + this.getValueFromKeyWithWhitespace('my_SpoAttribComitEsec'),
                    empty(),
                    empty(),
                ]);
                rowsTmpp.push([
                    format('11.', bold, 'right', fontSize, italics),
                    'Gli Amministratori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAmminRelazionano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoAmminRelazionano'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('12.', bold, 'right', 9, italics),
                    'L’Organo di controllo partecipa alle riunioni del Consiglio di Amministrazione?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciConsAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciConsAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('13.', bold, 'right', 9, italics),
                    'Vi sono componenti del Consiglio di Amministrazione indicati dallo Stato o da enti pubblici?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompStatoInConsAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompStatoInConsAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(4, ' - Vi sono componenti del Consiglio di Amministrazione eletti dai possessori degli strumenti finanziari emessi dall’ente?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompElettiStrFinan'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompElettiStrFinan'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format(empty(), bold, 'right', 9, italics),
                    Indent(4, ' - Se sì, viene rispettato il limite di un terzo?'),
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompElettiLimite'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompElettiLimite'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('14.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento del Consiglio di Amministrazione:' +
                        this.getValueFromKeyWithWhitespace('my_SpoNoteComposConsAmmin'),
                    empty(),
                    empty(),
                ]);
                break;

            case 3:
                break;

            case 4:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('7.', bold, 'right', fontSize, italics),
                    'La maggioranza degli amministratori è composta da soci cooperatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmMaggAmminSoci'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmMaggAmminSoci'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('8.', bold, 'right', fontSize, italics),
                    "Almeno un terzo degli amministratori è in possesso dei requisiti di indipendenza stabiliti dall'art. 2399 c.c.?",
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmMetaAmminIndip'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmMetaAmminIndip'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('9.', bold, 'right', fontSize, italics),
                    'Gli Amministratori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAmminCarattMutual'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmAmminCarattMutual'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('10.', bold, 'right', fontSize, italics),
                    'Vi sono componenti del Consiglio di Amministrazione indicati dallo Stato o da enti pubblici?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmCompStatoInConsAmmin'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpmCompStatoInConsAmmin'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('11.', bold, 'right', fontSize, italics),
                    'Note ed osservazioni sulla composizione e sul funzionamento del Consiglio di Amministrazione:' +
                        this.getValueFromKeyWithWhitespace('my_SpmNoteComposConsAmmin'),
                    empty(),
                    empty(),
                ]);
                break;
        }

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    private D_amministratoreUnicoHeader(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });

        rows.push([
            format('Cognome', bold, 'center', 9, true),
            format('Nome', bold, 'center', 9, true),
            format('Data Elezione', bold, 'center', 9, true),
            format('Socio (deve esserlo sempre)', bold, 'center', 9, true),
        ]);

        return fxTabella(cols, rows);
    }

    private D_amministratoreUnicoDati(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });

        rows.push([empty(), empty(), empty(), empty()]);

        return fxTabella(cols, rows);

        // let bindarray = "my_AllElemConsiglioAmministrazione";

        // $.each(evalThis(bindarray), private (indxEle, valore){
        // rowsTmp.push([   this.getValueFromKeyWithWhitespace("my_AllConsAmmCarica", indxEle, bindarray),  this.getValueFromKeyWithWhitespace("my_AllConsAmmCognome", indxEle,
        // bindarray),  this.getValueFromKeyWithWhitespace("my_AllConsAmmNome", indxEle, bindarray), this.formatDate(
        // this.getValueFromKeyWithWhitespace("my_AllConsAmmDataElezione", indxEle, bindarray)) ,  this.getValueFromKeyWithWhitespace("my_AllConsAmmCkSocio", indxEle, bindarray),
        // ]) })
    }

    private D_amministratoreUnicoScheda(numeroScheda) {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '4%' });
        cols.push({ header: '', width: '80%' });
        cols.push({ header: '', width: '8%' });
        cols.push({ header: '', width: '8%' });

        switch (numeroScheda) {
            case 1:
                rows.push([
                    format('16.', bold, 'right', fontSize, italics),
                    'Note e osservazioni sull’attività dell’Amministratore Unico: \n' + this.getValueFromKeyWithWhitespace('my_SrlNoteAmminUnico'),
                    empty(),
                    empty(),
                ]);
                rows.push([
                    format('17.', bold, 'right', fontSize, italics),
                    "L'Amministratore Unico relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)?",
                    checkSingoloInterno(this.getValueFromKey('my_CKSRLAmmUniRelCarMutua'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CKSRLAmmUniRelCarMutua'), '0') + ' No',
                ]);
                break;
            case 2:
                rows.push([
                    format('15.', bold, 'right', fontSize, italics),
                    'Note e osservazioni sull’attività dell’Amministratore Unico: \n' + this.getValueFromKeyWithWhitespace('my_SpoNoteAmminUnico'),
                    empty(),
                    empty(),
                ]);
                rows.push([
                    format('16.', bold, 'right', fontSize, italics),
                    "L'Amministratore Unico relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)?",
                    checkSingoloInterno(this.getValueFromKey('my_CKSPOAmmUniRelCarMutua'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CKSPOAmmUniRelCarMutua'), '0') + ' No',
                ]);
                break;
        }

        return fxTabella(cols, rows, true);
    }

    private E_liquidatoriHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '10%' });

        rowsTmp.push([
            format('Cognome', bold, 'center', 9, italics),
            format('Nome', bold, 'center', 9, italics),
            format('Data Nomina', bold, 'center', 9, italics),
            format('Socio', bold, 'center', 9, italics),
        ]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private E_liquidatoriDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '10%' });

        rowsTmp.push([empty(), empty(), empty(), empty()]);

        const array = this.getArrayFromKey('my_AllElemLiquidatori');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                format(this.getValueFromKeyWithWhitespace('my_AllLiquidatoriCognome', array[c]), bold, 'center', fontSize, italics),
                format(this.getValueFromKeyWithWhitespace('my_AllLiquidatoriNome', array[c]), bold, 'center', fontSize, italics),
                format(this.formatDate(this.getValueFromKeyWithWhitespace('my_AllLiquidatoriDataElezione', array[c])), bold, 'center', fontSize, italics),
                format(this.getValueFromKeyWithWhitespace('my_AllLiquidatoriCkSocio', array[c]), bold, 'center', fontSize, italics),
            ]);
        }

        return fxTabella(colsTmp, rowsTmp);
    }

    private E_liquidatoriScheda(numeroScheda) {
        let colsTmpp = [];
        let rowsTmpp = [];

        switch (numeroScheda) {
            case 1:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('18.', bold, 'right', 9, italics),
                    'I Liquidatori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)? \n Accertare e indicare tali criteri: \n' +
                        this.getValueFromKeyWithWhitespace('my_CriteriRelazioneLiquidatori'),
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('19.', bold, 'right', 9, italics),
                    'L’Organo di Controllo partecipa alle riunioni dei Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSindaciRiunioniLiquidatori'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSindaciRiunioniLiquidatori'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('20.', bold, 'right', 9, italics),
                    'Note e osservazioni sull’attività dei Liquidatori:\n' + this.getValueFromKeyWithWhitespace('my_NoteAttivitaLiquidatori'),
                    empty(),
                    empty(),
                ]);
                break;

            case 2:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('17.', bold, 'right', 9, italics),
                    'I Liquidatori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('18.', bold, 'right', 9, italics),
                    'L’Organo di Controllo partecipa alle riunioni dei Liquidatori?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSindaciRiunioniLiquidatori'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSindaciRiunioniLiquidatori'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('19.', bold, 'right', 9, italics),
                    'È stato istituito il registro delle deliberazioni?',
                    checkSingoloInterno(this.getValueFromKey('my_CkRegistroDelibLiquidatori'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkRegistroDelibLiquidatori'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('20.', bold, 'right', 9, italics),
                    'Note e osservazioni sull’attività dei Liquidatori:\n' + this.getValueFromKeyWithWhitespace('my_NoteAttivitaLiquidatori'),
                    empty(),
                    empty(),
                ]);
                break;

            case 3:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('16.', bold, 'right', 9, italics),
                    'I Liquidatori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)? \n Accertare e indicare tali criteri:' +
                        this.getValueFromKeyWithWhitespace('my_CriteriRelazioneLiquidatori'),
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('17.', bold, 'right', 9, italics),
                    'Note e osservazioni sull’attività dei Liquidatori:\n' + this.getValueFromKeyWithWhitespace('my_NoteAttivitaLiquidatori'),
                    empty(),
                    empty(),
                ]);
                break;

            case 4:
                colsTmpp.push({ header: '', width: '4%' });
                colsTmpp.push({ header: '', width: '80%' });
                colsTmpp.push({ header: '', width: '8%' });
                colsTmpp.push({ header: '', width: '8%' });

                rowsTmpp.push([
                    format('18.', bold, 'right', 9, italics),
                    'I Liquidatori relazionano sul carattere mutualistico della cooperativa (art. 2545 c.c.)? \n Accertare e indicare tali criteri:' +
                        this.getValueFromKeyWithWhitespace('my_CriteriRelazioneLiquidatori'),
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '1') + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkLiquidatoriRelazionano'), '0') + ' No',
                ]);
                rowsTmpp.push([
                    format('19.', bold, 'right', 9, italics),
                    'Note e osservazioni sull’attività dei Liquidatori:\n' + this.getValueFromKeyWithWhitespace('my_NoteAttivitaLiquidatori'),
                    empty(),
                    empty(),
                ]);
                break;
        }

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    private F_organoControlloHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '22%' });
        colsTmp.push({ header: '', width: '26%' });
        colsTmp.push({ header: '', width: '26%' });
        colsTmp.push({ header: '', width: '26%' });

        rowsTmp.push(['Carica', 'Cognome', 'Nome', 'Data Elezione']);

        return fxTabella(colsTmp, rowsTmp);
    }

    private F_organoControlloDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '22%' });
        colsTmp.push({ header: '', width: '26%' });
        colsTmp.push({ header: '', width: '26%' });
        colsTmp.push({ header: '', width: '26%' });

        const array = this.getArrayFromKey('my_AllElemCollegioSindacale');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                this.getValueFromKeyWithWhitespace('my_AllCollSindCarica', array[c]),
                this.getValueFromKeyWithWhitespace('my_AllCollSindCognome', array[c]),
                this.getValueFromKeyWithWhitespace('my_AllCollSindNome', array[c]),
                this.formatDate(this.getValueFromKeyWithWhitespace('my_AllCollSindDataElezione', array[c])),
            ]);
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private F_organoControlloScheda(numeroScheda) {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '4%' });
        colsTmpp.push({ header: '', width: '80%' });
        colsTmpp.push({ header: '', width: '8%' });
        colsTmpp.push({ header: '', width: '8%' });

        switch (numeroScheda) {
            case 1:
                rowsTmpp.push([
                    format('21.', bold, 'right', 9, italics),
                    'Vi sono componenti dell’Organo di controllo indicati dallo Stato o da enti pubblici?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompStatoInOrgContr')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompStatoInOrgContr')) + ' No',
                ]);
                rowsTmpp.push([
                    format('22.', bold, 'right', 9, italics),
                    'Vi sono componenti dell’Organo di controllo eletti dai possessori degli strumenti finanziari emessi dall’ente? ',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompOrgContrEletti')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlCompOrgContrEletti')) + ' No',
                ]);
                rowsTmpp.push([
                    format('23.', bold, 'right', 9, italics),
                    'Se sì viene rispettato il limite di un terzo?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlRispettoLimite')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlRispettoLimite')) + ' No',
                ]);
                rowsTmpp.push([
                    format('24.', bold, 'right', 9, italics),
                    'I componenti dell’Organo di Controllo sono revisori legali?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciRevisori')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciRevisori')) + ' No',
                ]);
                rowsTmpp.push([
                    format('25.', bold, 'right', 9, italics),
                    'In caso di risposta negativa, sono scelti in coerenza con l’art. 2397 c.c.? ',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciSceltiCoerenza')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciSceltiCoerenza')) + ' No',
                ]);
                rowsTmpp.push([
                    format('26.', bold, 'right', 9, italics),
                    'L’organo di controllo relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciRelazionano')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSrlSindaciRelazionano')) + ' No',
                ]);
                rowsTmpp.push([
                    format('27.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione, sul funzionamento dell’Organo di Controllo' + this.getValueFromKeyWithWhitespace('my_SrlNoteComposCollSind'),
                    empty(),
                    empty(),
                ]);
                break;

            case 2:
                rowsTmpp.push([
                    format('21.', bold, 'right', 9, italics),
                    'Vi sono componenti dell’Organo di controllo indicati dallo Stato o da enti pubblici?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompStatoInOrgContr')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompStatoInOrgContr')) + ' No',
                ]);
                rowsTmpp.push([
                    format('22.', bold, 'right', 9, italics),
                    'Vi sono componenti dell’Organo di controllo eletti dai possessori degli strumenti finanziari emessi dall’ente? ',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompOrgContrEletti')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoCompOrgContrEletti')) + ' No',
                ]);
                rowsTmpp.push([
                    format('23.', bold, 'right', 9, italics),
                    'Se sì viene rispettato il limite di un terzo?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoRispettoLimite')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoRispettoLimite')) + ' No',
                ]);
                rowsTmpp.push([
                    format('24.', bold, 'right', 9, italics),
                    'I componenti dell’Organo di Controllo sono revisori legali?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciRevisori')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciRevisori')) + ' No',
                ]);
                rowsTmpp.push([
                    format('25.', bold, 'right', 9, italics),
                    'In caso di risposta negativa, sono scelti in coerenza con l’art. 2397 c.c.? ',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciSceltiCoerenza')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciSceltiCoerenza')) + ' No',
                ]);
                rowsTmpp.push([
                    format('26.', bold, 'right', 9, italics),
                    'L’organo di controllo relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciRelazionano')) + ' Si',
                    checkSingoloInterno(this.getValueFromKey('my_CkSpoSindaciRelazionano')) + ' No',
                ]);
                rowsTmpp.push([
                    format('27.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione, sul funzionamento dell’Organo di Controllo' + this.getValueFromKeyWithWhitespace('my_SpoNoteComposCollSind'),
                    empty(),
                    empty(),
                ]);
                break;
        }

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    private G_RevisioneLegaleScheda(numeroScheda) {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '4%' });
        colsTmpp.push({ header: '', width: '80%' });
        colsTmpp.push({ header: '', width: '8%' });
        colsTmpp.push({ header: '', width: '8%' });

        switch (numeroScheda) {
            case 1:
                rowsTmpp.push([
                    format('28.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Organo che esercita la Revisione Legale:' +
                        this.getValueFromKeyWithWhitespace('my_SrlNoteComposContrContab'),
                    empty(),
                    empty(),
                ]);
                break;

            case 2:
                rowsTmpp.push([
                    format('28.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Organo che esercita la Revisione Legale:' +
                        this.getValueFromKeyWithWhitespace('my_SpoNoteComposContrContab'),
                    empty(),
                    empty(),
                ]);
                break;

            case 3:
                rowsTmpp.push([
                    format('18.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Organo che esercita la Revisione Legale:' +
                        this.getValueFromKeyWithWhitespace('my_SpdNoteComposContrContab'),
                    empty(),
                    empty(),
                ]);
                break;

            case 4:
                rowsTmpp.push([
                    format('17.', bold, 'right', 9, italics),
                    'Note e osservazioni sulla composizione e sul funzionamento dell’Organo che esercita la Revisione Legale:' +
                        this.getValueFromKeyWithWhitespace('my_SpmNoteComposOrgContr'),
                    empty(),
                    empty(),
                ]);
                break;
        }

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    private H_consiglioSorveglianzaHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '23%' });
        colsTmp.push({ header: '', width: '10%' });
        colsTmp.push({ header: '', width: '17%' });

        rowsTmp.push(['Cognome', 'Nome', 'Data Elezione', 'Socio', 'Revisore Legale']);

        return fxTabella(colsTmp, rowsTmp);
    }

    private H_consiglioSorveglianzaDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '23%' });
        colsTmp.push({ header: '', width: '10%' });
        colsTmp.push({ header: '', width: '17%' });

        const array = this.getArrayFromKey('my_SpdElemConsiglioSorveglianza');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                format(this.getValueFromKeyWithWhitespace('my_SpdConsSorvCognome', array[c]), bold, 'center', fontSize, italics),
                format(this.getValueFromKeyWithWhitespace('my_SpdConsSorvNome', array[c]), bold, 'center', fontSize, italics),
                format(this.formatDate(this.getValueFromKeyWithWhitespace('my_SpdConsSorvDataElezione', array[c])), bold, 'center', fontSize, italics),
                format(checkSingoloInterno(this.getValueFromKey('my_SpdConsSorvCkSocio', array[c])), bold, 'center', fontSize, italics),
                format(this.getValueFromKeyWithWhitespace('my_SpdConsSorvNome', array[c]), bold, 'center', fontSize, italics),
            ]);
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private H_consiglioSorveglianzaScheda(): Content {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '4%' });
        colsTmpp.push({ header: '', width: '80%' });
        colsTmpp.push({ header: '', width: '8%' });
        colsTmpp.push({ header: '', width: '8%' });

        rowsTmpp.push([
            format('6.', bold, 'right', fontSize, italics),
            'La maggioranza dei consiglieri di sorveglianza è composta da soci cooperatori?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdMaggSorvSoci'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdMaggSorvSoci'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('7.', bold, 'right', fontSize, italics),
            'Vi sono componenti del Consiglio di Sorveglianza eletti dai possessori degli strumenti finanziari emessi dall’ente?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdCompElettiStrFinan'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdCompElettiStrFinan'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format(empty(), bold, 'right', fontSize, italics),
            'Se sì, viene rispettato il limite di un terzo?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdCompElettiLimite'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdCompElettiLimite'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('8.', bold, 'right', fontSize, italics),
            'Ha nominato i consiglieri di gestione?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdNominaConsGest'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdNominaConsGest'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('9.', bold, 'right', fontSize, italics),
            'Approva il bilancio di esercizio?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdApprovaBilEserc'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdApprovaBilEserc'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('10.', bold, 'right', fontSize, italics),
            'Relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdRelazCaratMut'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdRelazCaratMut'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('11.', bold, 'right', fontSize, italics),
            'Vigila ai sensi dell’art 2403 c.c. 1° comma?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdVigila'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdVigila'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('12.', bold, 'right', fontSize, italics),
            'Riferisce per iscritto almeno una volta all’anno all’Assemblea?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdRiferisceAssemb'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpdRiferisceAssemb'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('13.', bold, 'right', fontSize, italics),
            'Note e osservazioni sulla composizione e sul funzionamento del Consiglio di Sorveglianza \n' + this.getValueFromKeyWithWhitespace('my_SpdNoteComposConsSorv'),
            empty(),
            empty(),
        ]);
        rowsTmpp.push([
            format('14.', bold, 'right', fontSize, italics),
            "Almeno un membro effettivo è revisore legale in coerenza con l'art. 2409 duodecies c.c.?",
            checkSingoloInterno(this.getValueFromKey('my_CkSPDMembroRevisore'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSPDMembroRevisore'), '0') + ' No',
        ]);

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    private I_consiglioGestioneHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '10%' });

        rowsTmp.push([
            format('Cognome', bold, 'center', 9, italics),
            format('Nome', bold, 'center', 9, italics),
            format('Data Elezione', bold, 'center', 9, italics),
            format('Socio', bold, 'center', 9, italics),
        ]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private I_consiglioGestioneDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '30%' });
        colsTmp.push({ header: '', width: '10%' });

        const array = this.getArrayFromKey('my_SpdElemConsiglioGestione');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                format(this.getValueFromKeyWithWhitespace('my_SpdConsGestCognome', array[c]), bold, 'center', fontSize, italics),
                format(this.getValueFromKeyWithWhitespace('my_SpdConsGestNome', array[c]), bold, 'center', fontSize, italics),
                format(this.formatDate(this.getValueFromKeyWithWhitespace('my_SpdConsGestDataElezione', array[c])), bold, 'center', fontSize, italics),
                format(checkSingoloInterno(this.getValueFromKey('my_SpdConsGestCkSocio', array[c])), bold, 'center', fontSize, italics),
            ]);
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private I_consiglioGestioneScheda(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '4%' });
        colsTmp.push({ header: '', width: '80%' });
        colsTmp.push({ header: '', width: '8%' });
        colsTmp.push({ header: '', width: '8%' });

        rowsTmp.push([
            format('15.', bold, 'right', 9, italics),
            'Note e osservazioni sulla composizione e sul funzionamento del Consiglio di Gestione: \n' + this.getValueFromKeyWithWhitespace('my_SpdNoteComposConsGest'),
            empty(),
            empty(),
        ]);

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private J_comitatoControlloGestioneHeader(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });

        rowsTmp.push(['Carica', 'Cognome', 'Nome', 'Data Elezione']);

        return fxTabella(colsTmp, rowsTmp);
    }

    private J_comitatoControlloGestioneDati(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });
        colsTmp.push({ header: '', width: '25%' });

        const array = this.getArrayFromKey('my_SpmElemComitatoGestione');
        for (let c = 0; c < array.length; c++) {
            rowsTmp.push([
                this.getValueFromKeyWithWhitespace('my_SpmComitGestCarica', array[c]),
                this.getValueFromKeyWithWhitespace('my_SpmComitGestCognome', array[c]),
                this.getValueFromKeyWithWhitespace('my_SpmComitGestNome', array[c]),
                this.formatDate(this.getValueFromKeyWithWhitespace('my_SpmComitGestDataElezione', array[c])),
            ]);
        }

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private J_comitatoControlloGestioneScheda(): Content {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '4%' });
        colsTmpp.push({ header: '', width: '80%' });
        colsTmpp.push({ header: '', width: '8%' });
        colsTmpp.push({ header: '', width: '8%' });

        rowsTmpp.push([
            format('12.', bold, 'right', 9, italics),
            'Salvo diversa previsione statutaria, il numero e la nomina dei membri del Comitato di Controllo sono stati determinati dal Consiglio di amministrazione?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmComitGestConsAmmin'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmComitGestConsAmmin'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('13.', bold, 'right', 9, italics),
            'Vi sono componenti del Comitato di controllo indicati dallo Stato o da enti pubblici?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompStatoInOrgContr'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompStatoInOrgContr'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('14.', bold, 'right', 9, italics),
            'Almeno uno dei componenti è scelto tra i revisori legali?',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompRevisori'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompRevisori'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('15.', bold, 'right', 9, italics),
            'Il Comitato relaziona sul carattere mutualistico della cooperativa (art. 2545 c.c.)? ',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompRelazionano'), '1') + ' Si',
            checkSingoloInterno(this.getValueFromKey('my_CkSpmCompRelazionano'), '0') + ' No',
        ]);
        rowsTmpp.push([
            format('16.', bold, 'right', 9, italics),
            'Note ed osservazioni sulla composizione e sul funzionamento del Comitato di Controllo sulla gestione: \n' +
                this.getValueFromKeyWithWhitespace('my_SpmNoteComposComitGest'),
            empty(),
            empty(),
        ]);

        return fxTabella(colsTmpp, rowsTmpp, true);
    }

    // ***************************************************************************************************************************************************************************
    // CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE --- CONTRIBUTO BIENNALE ---
    // ***************************************************************************************************************************************************************************

    private tabellaRiferimentoContributoBiennale(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '45%' });
        colsTmp.push({ header: '', width: '55%' });

        rowsTmp.push(['Tipologia ', 'Anno di Riferimento (2° anno del biennio concluso) ' + this.getValueFromKeyWithWhitespace('my_Anno2')]);
        rowsTmp.push([this.tabellaRiferimentoContributoBiennaleTipologia(), this.tabellaRiferimentoContributoBiennaleLiquidazione()]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaRiferimentoContributoBiennaleTipologia(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '35%' });
        colsTmp.push({ header: '', width: '*' });

        rowsTmp.push([Indent(10, 'Sezione:'), this.getValueFromKeyWithWhitespace('my_DenominazEnte')]);
        rowsTmp.push([Indent(10, 'Revisione:'), this.getValueFromKeyWithWhitespace('my_Anno1') + '/' + this.getValueFromKeyWithWhitespace('my_Anno2')]);
        rowsTmp.push([Indent(10, 'Data costituzione:'), this.formatDate(this.getValueFromKeyWithWhitespace('my_DataCostituz'))]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaRiferimentoContributoBiennaleLiquidazione(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '35%' });
        colsTmp.push({ header: '', width: '*' });

        rowsTmp.push([Indent(90, empty()), Indent(5, 'In liquidazione dal¹ ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataAssembleaLiquidazVolontaria')))]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaRiferimentoContributoBiennaleImporti(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '70%' });
        colsTmp.push({ header: '', width: '30%' });

        rowsTmp.push([this.tabellaRiferimentoContributoBiennaleImportiInterna()]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaRiferimentoContributoBiennaleImportiInterna(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '60%' });
        colsTmp.push({ header: '', width: '40%' });

        rowsTmp.push(['- Soci ', 'n. ' + this.getValueFromKeyWithWhitespace('my_SociInseriti')]);
        rowsTmp.push(['- Capitale Sociale ', '€ ' + this.getValueFromKeyWithWhitespace('my_CapitaleSociale')]);
        rowsTmp.push(['- Valore della Produzione² ', '€ ' + this.getValueFromKeyWithWhitespace('my_RicaviVendPrest')]);
        rowsTmp.push(['- Aumento per revisione annuale ', '€ ' + this.getValueFromKeyWithWhitespace('my_AumentoIspez')]);
        rowsTmp.push(['- Contributo dovuto ', '€ ' + this.getValueFromKeyWithWhitespace('my_ContributoDovutoModif')]);
        rowsTmp.push(['- Contributo versato ', '€ ' + this.getValueFromKeyWithWhitespace('my_ContribVersato')]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaDevoluzioneContributi(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push(['Scheda controllo per la devoluzione ai Fondi Mutualistici ex-art. 11 legge 59/92 relativa agli ultimi due esercizi']);
        rows.push([this.tabellaDevoluzioneContributiInterna()]);
        rows.push([
            format(
                '¹) Eventualmente rettificato dei componenti positivi e negativi contabilizzati a conto economico che hanno già concorso a determinare il risultato degli esercizi precedenti. ²) Al fine della detrazione il revisore verificherà la presenza della delibera assembleare che dispone il ripianamento delle perdite.  \n ³)In luogo della “x” andrà inserito il valore percentuale vigente al momento della verifica.',
                bold,
                alignment,
                8,
                italics
            ),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaDevoluzioneContributiInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '25%' });
        cols.push({ header: '', width: '25%' });

        rows.push([empty(), 'Esercizio ' + this.getValueFromKeyWithWhitespace('my_BilAnno2'), 'Esercizio ' + this.getValueFromKeyWithWhitespace('my_BilAnno1')]);
        rows.push([
            "a) risultato dell'esercizio¹",
            '€ ' + this.getValueFromKeyWithWhitespace('my_RisultEsercizio'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_RisultEsercizioAnno1'),
        ]);
        rows.push([
            'b) eventuali preaccantonamenti a riserva indivisibile',
            '€ ' + this.getValueFromKeyWithWhitespace('my_Preaccantonamenti'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_PreaccantonamentiAnno1'),
        ]);
        rows.push([
            'c) eventuale accantonamento art. 11 L. 59 ',
            '€ ' + this.getValueFromKeyWithWhitespace('my_Accantonamento'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_AccantonamentoAnno1'),
        ]);
        rows.push([
            'd) eventuali perdite di esercizio al netto delle riserve²',
            '€ ' + this.getValueFromKeyWithWhitespace('my_PerditeEserc'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_PerditeEsercAnno1'),
        ]);
        rows.push([
            'e) eventuali ristorni (deliberati dall’assemblea di bilancio)',
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventRistorni'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_EventRistorniAnno1'),
        ]);
        rows.push([
            'f) BASE DI CALCOLO: A + B + C - D - E',
            '€ ' + this.getValueFromKeyWithWhitespace('my_BaseCalcolo'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_BaseCalcoloAnno1'),
        ]);
        rows.push([
            'g) Importo complessivo art. 11 L. 59 (f*x/100)³',
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoComplessivo'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoComplessivoAnno1'),
        ]);
        rows.push(['Importo/i versato/i', '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoVersato'), '€ ' + this.getValueFromKeyWithWhitespace('my_ImportoVersatoAnno1')]);
        rows.push([
            'data/e di versamento',
            '€ ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataVersam')),
            '€ ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataVersamAnno1')),
        ]);
        rows.push([
            'fondo/i beneficiario/i',
            '€ ' + this.getValueFromKeyWithWhitespace('my_FondoBeneficiario'),
            '€ ' + this.getValueFromKeyWithWhitespace('my_FondoBeneficiarioAnno1'),
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // OSSERVAZIONI E CONTRODEDUZIONI --- OSSERVAZIONI E CONTRODEDUZIONI --- OSSERVAZIONI E CONTRODEDUZIONI --- OSSERVAZIONI E CONTRODEDUZIONI --- OSSERVAZIONI E CONTRODEDUZIONI
    // ***************************************************************************************************************************************************************************

    private tabellaOsservazioniControdeduzioni(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '3%' });
        cols.push({ header: '', width: '*' });

        rows.push([checkSingolo(this.getValueFromKey('my_CkProposte'), '1'), 'propone il rilascio del certificato/attestazione di revisione']);
        rows.push([
            checkSingolo(this.getValueFromKey('my_CkProposte'), '3'),
            'irroga la diffida ad eliminare le irregolarità indicate al punto 63 entro il termine di gg. ' +
                this.getValueFromKeyWithWhitespace('my_ggDiffida') +
                ' come da notifica allegata.',
        ]);
        rows.push([checkSingolo(this.getValueFromKey('my_CkProposte'), '2'), 'propone l’adozione del/i provvedimento/i di:']);
        rows.push([empty(), this.tabellaOsservazioniControdeduzioniSub()]);

        return fxTabella(cols, rows);
    }

    private tabellaOsservazioniControdeduzioniSub(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '3%' });
        colsTmp.push({ header: '', width: '*' });

        rowsTmp.push([checkSingolo(this.getValueFromKey('my_CkProvvedimendoDi'), '2'), 'gestione commissariale ai sensi dell’art. 2545 sexiesdecies c.c. ']);
        rowsTmp.push([checkSingolo(this.getValueFromKey('my_CkProvvedimendoDi'), '3'), 'scioglimento per atto dell’autorità ai sensi dell’art. 2545 septiesdecies c.c. ']);
        rowsTmp.push([empty(), this.tabellaOsservazioniControdeduzioniSubIntendata()]);
        rowsTmp.push([checkSingolo(this.getValueFromKey('my_CkProvvedimendoDi'), '4'), 'sostituzione dei liquidatori ai sensi dell’art. 2545 octiesdecies c.c.']);
        rowsTmp.push([
            checkSingolo(this.getValueFromKey('my_CkProvvedimendoDi'), '9'),
            'cancellazione dal Registro Imprese ai sensi del secondo comma dell’art. 2545 octiesdecies c.c.',
        ]);
        rowsTmp.push([checkSingolo(this.getValueFromKey('my_CkProvvedimendoDi'), '5'), 'liquidazione coatta amministrativa ai sensi dell’art. 2545 terdecies c.c.']);
        rowsTmp.push([empty(), empty()]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaOsservazioniControdeduzioniSubIntendata(): Content {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '3%' });
        colsTmpp.push({ header: '', width: '*' });

        rowsTmpp.push([checkSingolo(this.getValueFromKey('my_CkNominaLiq'), '1'), ' con nomina del liquidatore ']);
        rowsTmpp.push([checkSingolo(this.getValueFromKey('my_CkNominaLiq'), '0'), ' senza nomina del liquidatore ']);

        return fxTabella(colsTmpp, rowsTmpp);
    }

    private tabellaOsservazioniControdeduzioniFirme(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([format("Il legale rappresentante dell'ente revisionato", bold, 'center', 10, italics), format('Il revisore', bold, 'center', 10, italics)]);
        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_FirmaLegaleRappresentante'), bold, 'center', 10, italics),
            format(this.getValueFromKeyWithWhitespace('my_Revisore'), bold, 'center', 10, italics),
        ]);

        return fxTabella(cols, rows);
    }

    // *******************************************************************************************************************************
    // *******************************************************************************************************************************
    // SEZIONE II ACCCERTAMENTO --- SEZIONE II ACCERTAMENTO --- SEZIONE II ACCERTAMENTO --- SEZIONE II ACCERTAMENTO ---
    // *******************************************************************************************************************************
    // *******************************************************************************************************************************

    // ***************************************************************************************************************************************************************************
    // DATI IDENTIFICATIVI ACCERTAMENTO --- DATI IDENTIFICATIVI ACCERTAMENTO --- DATI IDENTIFICATIVI ACCERTAMENTO ---DATI IDENTIFICATIVI ACCERTAMENTO ---
    // ***************************************************************************************************************************************************************************

    private fxDatiIdentificativiAccertamento(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '40%' });
        colsTmp.push({ header: '', width: '60%' });

        rowsTmp.push(["Denominazione dell'ente", this.tabellaDenominazioneEnte_ValueAccertamento()]);
        rowsTmp.push([format('Sede legale (indirizzo, telefono, e-mail, sito WEB) ', false, alignment, 10, italics), this.tabellaDatiUtente_ValueAccertamento()]);
        rowsTmp.push(['Codice Fiscale', this.getValueFromKeyWithWhitespace('my_CodFiscII')]);
        rowsTmp.push(['Posizione Albo Società Cooperative', this.tabellaAlboCoopAccertamento()]);
        rowsTmp.push(["Categoria a cui appartiene l'ente cooperativo", this.getValueFromKeyWithWhitespace('my_DescSezEnteII')]);
        rowsTmp.push([this.tabellaAderenzaSXAccertamento(), this.tabellaAderenzaDXAccertamento()]);

        return fxTabella(colsTmp, rowsTmp, true);
    }

    private tabellaDenominazioneEnte_ValueAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: 20 });
        cols.push({ header: '', width: 100 });
        cols.push({ header: '', width: '*' });

        rows.push([empty(), empty(), format(this.getValueFromKeyWithWhitespace('my_DenominazEnteII'), bold, 'right', fontSize, italics)]);
        rows.push([empty(), 'In liquidazione dal', this.formatDate(this.getValueFromKeyWithWhitespace('my_DataAssembleaLiquidazVolontaria'))]);

        return fxTabella(cols, rows);
    }

    private tabellaDatiUtente_ValueAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([
            'Indirizzo: ' +
                this.getValueFromKeyWithWhitespace('my_IndirizzoSedeLegaleII') +
                ', \n ' +
                this.getValueFromKeyWithWhitespace('my_CapSedeLegaleII') +
                ' ' +
                this.getValueFromKeyWithWhitespace('my_DescComuneSedeLegaleII') +
                ', ' +
                this.getValueFromKeyWithWhitespace('my_DescProvinciaSedeLegaleII'),
        ]);
        rows.push([
            'Tel. ' + this.getValueFromKeyWithWhitespace('my_SpecificaTelefonoSedeLegaleII') + '  Fax ' + this.getValueFromKeyWithWhitespace('my_SpecificaFaxSedeLegaleII'),
        ]);
        rows.push(['e-mail: ' + this.getValueFromKeyWithWhitespace('my_SpecificaMailSedeLegaleII')]);
        rows.push(['e-mail certificata (PEC)* ' + this.getValueFromKeyWithWhitespace('my_PECcciII') + ', ' + this.getValueFromKeyWithWhitespace('my_AltraPECII')]);
        rows.push(['sito web: ' + this.getValueFromKeyWithWhitespace('my_SpecificaSitoWebSedeLegaleII')]);

        return fxTabella(cols, rows);
    }

    private tabellaAlboCoopAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrevII'), '1') + '  Mutualità Prevalente', 'N. Iscrizione Albo ']);
        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrevII'), '2') + '  Mutualità non Prevalente', this.getValueFromKeyWithWhitespace('my_PosizAlboEntiII')]);
        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkMutualPrevII'), '3') + '  Non iscritta', empty()]);

        return fxTabella(cols, rows);
    }

    private tabellaAderenzaSXAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '65%' });
        cols.push({ header: '', width: '35%' });

        const array = this.getArrayFromKey('my_ElAderentiAII');
        for (let c = 0; c < array.length; c++) {
            rows.push(['Aderente a: ', this.getValueFromKeyWithWhitespace('my_AderenteAII', array[c]?.['my_SezVisibAderenteAII'])]);
            rows.push(['dal: ', this.formatDate(this.getValueFromKeyWithWhitespace('my_aderenteDalII', array[c]?.['my_SezVisibAderenteAII']))]);
        }

        return fxTabella(cols, rows);
    }

    private tabellaAderenzaDXAccertamento(): Content {
        let cols = [];
        let rows = [];
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '45%' });
        rows.push([empty(), 'N. Matricola ', empty()]);
        rows.push([empty(), this.getValueFromKeyWithWhitespace('my_MatricolaII'), empty()]);
        return fxTabella(cols, rows);
    }

    private tabellaOsservazioniControdeduzioniAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '3%' });
        cols.push({ header: '', width: '*' });

        rows.push([checkSingolo(this.getValueFromKey('my_CkProposteII'), '1'), 'propone il rilascio del certificato/attestazione di revisione']);
        rows.push([checkSingolo(this.getValueFromKey('my_CkProposteII'), '2'), 'propone l’adozione del/i provvedimento/i di:']);
        rows.push([empty(), this.tabellaOsservazioniControdeduzioniSubAccertamento()]);

        return fxTabella(cols, rows);
    }

    private tabellaOsservazioniControdeduzioniSubAccertamento(): Content {
        let colsTmp = [];
        let rowsTmp = [];

        colsTmp.push({ header: '', width: '3%' });
        colsTmp.push({ header: '', width: '*' });

        rowsTmp.push([checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), '2'), 'gestione commissariale ai sensi dell’art. 2545 sexiesdecies c.c. ']);
        rowsTmp.push([checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), '3'), 'scioglimento per atto dell’autorità ai sensi dell’art. 2545 septiesdecies c.c. ']);
        rowsTmp.push([empty(), this.tabellaOsservazioniControdeduzioniSubIntendataAccertamento()]);
        rowsTmp.push([checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), '4'), 'sostituzione dei liquidatori ai sensi dell’art. 2545 octiesdecies c.c.']);
        rowsTmp.push([
            checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), '5'),
            'cancellazione dal Registro Imprese ai sensi del secondo comma dell’art. 2545 octiesdecies c.c.',
        ]);
        rowsTmp.push([checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), '9'), 'liquidazione coatta amministrativa ai sensi dell’art. 2545 terdecies c.c.']);
        rowsTmp.push([
            checkSingoloInterno(this.getValueFromKey('my_CkProvvedimendoDiII'), ''),
            "unitamente alla irrogazione della sanzione amministrativa di cui ai commi 5 bis e 5 ter dell'art. 12 del D.Lgs. n. 220/2002 e successive modificazioni ed integrazioni.",
        ]);
        rowsTmp.push([empty(), empty()]);

        return fxTabella(colsTmp, rowsTmp);
    }

    private tabellaOsservazioniControdeduzioniSubIntendataAccertamento(): Content {
        let colsTmpp = [];
        let rowsTmpp = [];

        colsTmpp.push({ header: '', width: '3%' });
        colsTmpp.push({ header: '', width: '*' });

        rowsTmpp.push([checkSingoloInterno(this.getValueFromKey('my_CkNominaLiqII'), '1'), ' con nomina del liquidatore ']);
        rowsTmpp.push([checkSingoloInterno(this.getValueFromKey('my_CkNominaLiqII'), '0'), ' senza nomina del liquidatore ']);

        return fxTabella(colsTmpp, rowsTmpp);
    }

    private tabellaOsservazioniControdeduzioniFirmeAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([format("Il legale rappresentante dell'ente revisionato", bold, 'center', 10, italics), format('Il revisore', bold, 'center', 10, italics)]);
        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_FirmaLegaleRappresentanteII'), bold, 'center', 10, italics),
            format(this.getValueFromKeyWithWhitespace('my_RevisoreII'), bold, 'center', 10, italics),
        ]);

        return fxTabella(cols, rows);
    }

    // ************************************************************************************************************************************
    // ************************************************************************************************************************************
    // SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE --- SCHEDE DI SETTORE ---
    // ************************************************************************************************************************************
    // ************************************************************************************************************************************

    // ***************************************************************************************************************************************************************************
    // SS. COOPERATIVE SOCIALI --- SS. COOPERATIVE SOCIALI --- SS. COOPERATIVE SOCIALI --- SS. COOPERATIVE SOCIALI --- SS. COOPERATIVE SOCIALI --- SS. COOPERATIVE SOCIALI ---
    // ***************************************************************************************************************************************************************************

    private tabellaExArt1(punto13: boolean = false, punto14: boolean = false, coopServizio: boolean = false): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([format('Cooperative ex art. 1 Legge 3 aprile 2001 n. 142', true, 'center', 9, italics)]);
        rows.push([lineaVuota()]);
        rows.push([tabellaCheck('1 - E’ stato redatto il Regolamento previsto dall’art. 6 della Legge n.142/01?', this.getValueFromKeyWithWhitespace('my_CkRedazRegolam'))]);
        if (coopServizio) {
            rows.push([
                tabellaCheck(
                    'Per le cooperative di servizio compilare la scheda seguente:   1 - E’ stato redatto il Regolamento previsto dall’art. 6 della Legge n.142/01 ?',
                    this.getValueFromKeyWithWhitespace('my_ModoDiAssociazione')
                ),
            ]);
        }
        rows.push(['2 - Data di approvazione da parte dell’Assemblea ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataApprovAssemb'))]);
        rows.push(['3 - Data deposito presso l’Ufficio pubblico competente ' + this.formatDate(this.getValueFromKeyWithWhitespace('my_DataDeposUfficio'))]);
        rows.push([tabellaCheck('4 - Il Regolamento è stato sottoposto a certificazione?', this.getValueFromKeyWithWhitespace('my_ChkRegolamentoCertificato'))]);
        rows.push([
            tabellaCheck("5 - Il Regolamento è conforme alle previsioni dell'art. 6 della Legge n. 142/01?", this.getValueFromKeyWithWhitespace('my_CkRegolamentoConforme')),
        ]);
        rows.push(['- In caso di risposta negativa precisarne i motivi: ' + this.getValueFromKeyWithWhitespace('my_NoteRegNonConforme')]);
        rows.push([this.tabellaSociExArt1()]);
        rows.push([
            tabellaCheck('8 - La tipologia dei rapporti di lavoro posti in essere con i soci è corretta?', this.getValueFromKeyWithWhitespace('my_CkTipiRappLavConSociCorretti')),
        ]);
        rows.push([tabellaCheck("9 - E' conforme alle previsioni del Regolamento?", this.getValueFromKeyWithWhitespace('my_CkTipiRappLavCorretti'))]);
        rows.push([Indent(4, '- In caso di risposta negativa precisarne i motivi: ' + this.getValueFromKeyWithWhitespace('my_MotTipiRappLavCorretti'))]);
        rows.push([
            tabellaCheck(
                '10 - In caso di lavoro subordinato è specificato il tipo di contratto collettivo applicato?',
                this.getValueFromKeyWithWhitespace('my_CkContrattoCollLavSub')
            ),
        ]);
        rows.push([Indent(4, 'Descrivere: ')]);

        const array = this.getArrayFromKey('my_ElContrattiUsatiSociLavoratori');
        for (let c = 0; c < array.length; c++) {
            rows.push([this.getValueFromKeyWithWhitespace('my_ContrattiUsatiSociLavoratoriDescNome', array[c])]);
        }

        rows.push([
            Indent(
                3,
                tabellaCheck(
                    '- Il contratto applicato è conforme ai requisiti di cui al comma 4 dell’art. 7 del D.L. n. 248/07, convertito con modificazioni dalla legge n. 31/08',
                    this.getValueFromKeyWithWhitespace('my_CkContrApplicConforme')
                )
            ),
        ]);
        rows.push([Indent(4, ' In caso di risposta negativa specificare: ' + this.getValueFromKeyWithWhitespace('my_DescContrSocLavNoConf'))]);
        rows.push([
            tabellaCheck(
                '11 - Esistono soggetti iscritti nel libro soci non impiegati nelle attività sociali?',
                this.getValueFromKeyWithWhitespace('my_ckSoggLibrSociNoAttSociali')
            ),
        ]);
        rows.push(['In caso di risposta affermativa , qual è il loro ruolo?' + this.getValueFromKeyWithWhitespace('my_DescRuoloSocNoAttSoc')]);
        rows.push(['12 - Eventuali osservazioni sulle letiazioni della base sociale negli ultimi due anni: \n' + this.getValueFromKeyWithWhitespace('my_DescletBaseSoc')]);
        if (punto13) {
            rows.push([tabellaCheck("13 - E’ iscritta all'albo delle imprese artigiane?", this.getValueFromKeyWithWhitespace('my_CkAlboImpreseArtigiane'))]);
            if (punto14) {
                rows.push([
                    tabellaCheck('14 - La cooperativa esercita in maniera esclusiva l’attività di facchinaggio?', this.getValueFromKeyWithWhitespace('my_ckAttivFacchinaggio')),
                ]);
            }
        }

        return fxTabella(cols, rows);
    }

    private listaConvenzioniStipulateCoopSocialiHeader(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '30%' });

        rows.push(['Ente', 'Oggetto convenzione', 'Importo ']);

        return fxTabella(cols, rows);
    }

    private listaConvenzioniStipulateCoopSocialiDati(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '30%' });

        const array = this.getArrayFromKey('my_ElEntiConv');
        for (let c = 0; c < array.length; c++) {
            rows.push([
                this.getValueFromKeyWithWhitespace('my_ConvEnte', array[c]),
                this.getValueFromKeyWithWhitespace('my_ConvOggetto', array[c]),
                this.getValueFromKeyWithWhitespace('my_ImportoConvenzione', array[c]),
            ]);
        }

        return fxTabella(cols, rows, true);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP AGRICOLA LAVORO AGRICOLO --- SS. COOP AGRICOLA LAVORO AGRICOLO --- SS. COOP AGRICOLA LAVORO AGRICOLO --- SS. COOP AGRICOLA LAVORO AGRICOLO ---
    // ***************************************************************************************************************************************************************************

    private tabellaTerreniLavorati(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([
            '1) Terreni di proprietà ' + this.getValueFromKeyWithWhitespace('my_TerreniProprieta') + ' Ha',
            '1) Terreni a conduzione unita ' + this.getValueFromKeyWithWhitespace('my_TerreniConduzioneUnica') + ' Ha',
        ]);
        rows.push([
            '2) Terreni in fitto ' + this.getValueFromKeyWithWhitespace('my_TerreniInFitto') + ' Ha',
            '2) Terreni a conduzione divisa ' + this.getValueFromKeyWithWhitespace('my_TerreniConduzioneDivisa') + ' Ha',
        ]);
        rows.push(['3) Terreni ad altro titolo ' + this.getValueFromKeyWithWhitespace('my_TerreniAdAltroTitolo') + ' Ha', empty()]);
        const sommaTerreni: number = this.sommaTerreni(['my_TerreniProprieta', 'my_TerreniInFitto', 'my_TerreniAdAltroTitolo']);
        const sommaTerreniCond: number = this.sommaTerreni(['my_TerreniConduzioneUnica', 'my_TerreniConduzioneDivisa']);
        rows.push([format('TOTALE: ' + sommaTerreni + ' Ha', bold, 'center', fontSize, italics), format('TOTALE: ' + sommaTerreniCond + ' Ha', bold, 'center', fontSize, italics)]);

        return fxTabella(cols, rows);
    }

    private sommaTerreni(fieldNames: string[]): number {
        return fieldNames
            .map((name: string) => parseInt(this.getValueFromKey(name), 10))
            .filter((res: number) => !isNaN(res))
            .reduce((prev: number, curr: number) => prev + curr, 0);
    }

    private tabellaSociExArt1(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '53%' });
        cols.push({ header: '', width: '13%' });
        cols.push({ header: '', width: '3%' });
        cols.push({ header: '', width: '31%' });

        rows.push([
            '6 - Tipo di rapporto lavorativo instaurato con i soci lavoratori: ',
            'n. soci ' + this.getValueFromKeyWithWhitespace('my_NumSocLavSub'),
            checkSingolo(this.getValueFromKey('my_CkSocLavSub')),
            'subordinato a tempo indeterminato',
        ]);
        rows.push([
            empty(),
            'n. soci ' + this.getValueFromKeyWithWhitespace('my_NumSocLavSubTempoDeterminato'),
            checkSingolo(this.getValueFromKey('my_CkSocLavSubTempoDeterminato')),
            'subordinato a tempo determinato',
        ]);
        rows.push([empty(), 'n. soci ' + this.getValueFromKeyWithWhitespace('my_NumSocLavParaSub'), checkSingolo(this.getValueFromKey('my_CkSocLavParaSub')), 'parasubordinato']);
        rows.push([empty(), 'n. soci ' + this.getValueFromKeyWithWhitespace('my_NumSocLavAut'), checkSingolo(this.getValueFromKey('my_CkSocLavAut')), 'autonomo']);
        rows.push([empty(), 'n. soci ' + this.getValueFromKeyWithWhitespace('my_NumSocLavAltro'), checkSingolo(this.getValueFromKey('my_CkSocLavAltro')), 'altro']);
        rows.push([
            '7 - Di cui soci tecnici ed amministrativi (ex art. 14, l. 59/92) presenti:',
            'n. soci ' + this.getValueFromKeyWithWhitespace('my_SociTechAmminiLav'),
            empty(),
            empty(),
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP AGRICOLE CONFERIMENTO ALLEVAMENTO --- SS. COOP AGRICOLE CONFERIMENTO ALLEVAMENTO --- SS. COOP AGRICOLE CONFERIMENTO ALLEVAMENTO --- SS. COOP AGRICOLE CONFERIMENTO
    // ***************************************************************************************************************************************************************************

    private tabellaCoopAgricAllevamentoProprietà(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '70%' });
        cols.push({ header: '', width: '10%' });
        cols.push({ header: '', width: '10%' });
        cols.push({ header: '', width: '10%' });

        rows.push([
            '- In caso di risposta affermativa, lo stabilimento è di proprietà della cooperativa, in affitto oppure ad altro titolo? ',
            checkSingoloInterno(this.getValueFromKey('my_CkStabilimentoPropr')) + ' Proprietà',
            checkSingoloInterno(this.getValueFromKey('my_CkStabilimentoAffit')) + ' Affitto',
            checkSingoloInterno(this.getValueFromKey('my_CkStabilimentoAltroTit')) + ' Altro',
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP TRASPORTO --- SS. COOP TRASPORTO --- SS. COOP TRASPORTO --- SS. COOP TRASPORTO --- --- SS. COOP TRASPORTO --- SS. COOP TRASPORTO --- SS. COOP TRASPORTO ---
    // ***************************************************************************************************************************************************************************
    private tabellaCoopTrasportoInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '65%' });
        cols.push({ header: '', width: '35%' });

        rows.push(['1 - I soci sono associati in cooperativa di servizio od in cooperativa di lavoro:', this.getValueFromKeyWithWhitespace('my_ModoDiAssociazione')]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP PESCA --- SS. COOP PESCA --- SS. COOP PESCA --- SS. COOP PESCA --- --- SS. COOP PESCA --- SS. COOP PESCA --- SS. COOP PESCA --- SS. COOP PESCA --- SS. COOP PESCA
    // ***************************************************************************************************************************************************************************

    private tabellaCoopPescaInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '65%' });
        cols.push({ header: '', width: '35%' });

        rows.push(['1 - I soci sono associati in cooperativa di servizio od in cooperativa di lavoro:', this.getValueFromKeyWithWhitespace('my_ModoDiAssociazioneSchPesca')]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. ALTRE COOP --- SS. ALTRE COOP --- SS. ALTRE COOP --- SS. ALTRE COOP --- --- SS. ALTRE COOP --- SS. ALTRE COOP --- SS. ALTRE COOP --- SS. ALTRE COOP --- SS. ALTRE COOP
    // ***************************************************************************************************************************************************************************

    private tabellaAltreCoopCategoriaScambiMutualistici(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaGaranziaFidi')) + ' Garanzia Fidi',
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaPesca')) + ' Pesca',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaTrasporto')) + ' Trasporto (servizi ai soci)',
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaConsumo')) + ' Consumo',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaAgricoleConferimento')) + ' Agricola di conferimento',
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaAgricoleConsorzi')) + ' Consorzio agrario ',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaSociali')) + ' Social',
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaAgricole')) + ' Lavoro agricolo',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaPDL')) + ' Produzione e lavoro (valida anche per il trasporto)',
            checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaEdilizie')) + ' Abitazione e loro consorzi',
        ]);
        rows.push([checkSingoloInterno(this.getValueFromKey('my_CkAltreCoopAllegaSchedaDettaglianti')) + ' Dettaglianti', empty()]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP CONSUMO --- SS. COOP CONSUMO --- SS. COOP CONSUMO --- SS. COOP CONSUMO --- --- SS. COOP CONSUMO --- SS. COOP CONSUMO --- SS. COOP CONSUMO --- SS. COOP CONSUMO ---
    // ***************************************************************************************************************************************************************************

    private tabellaCoopConsumoInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            '3 - Quali generi vende?',
            checkSingoloInterno(this.getValueFromKey('my_coopConsGenAlimVend')) + ' Alimentari',
            checkSingoloInterno(this.getValueFromKey('my_coopConsAltriGenVend')) + ' altri generi',
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP ABITAZIONE E CONSORZI --- SS. COOP ABITAZIONE E CONSORZI --- SS. COOP ABITAZIONE E CONSORZI --- SS. COOP ABITAZIONE E CONSORZI --- SS. COOP ABITAZIONE E CONSORZI
    // ***************************************************************************************************************************************************************************

    private tabellaCoopAbitazioneConsorziInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });

        rows.push([
            'non iscritta   ' + checkSingoloInterno(this.getValueFromKey('my_CkIscrizAlbo'), '1'),
            ' iscritta   ' + checkSingoloInterno(this.getValueFromKey('my_CkIscrizAlbo'), '2'),
            ' sospesa   ' + checkSingoloInterno(this.getValueFromKey('my_CkIscrizAlbo'), '3'),
            ' cancellata   ' + checkSingoloInterno(this.getValueFromKey('my_CkIscrizAlbo'), '4'),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopAbitazioneSociPrenotatari(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '10%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '10%' });

        rows.push([
            empty(),
            checkSingoloInterno(this.getValueFromKey('my_CkCdASociPrenOAss'), '1'),
            checkSingoloInterno(this.getValueFromKey('my_CkCdASociPrenOAss'), '0'),
            checkSingoloInterno(this.getValueFromKey('my_CkCdASociPrenOAss'), '2'),
            checkSingoloInterno(this.getValueFromKey('my_CkCdASociPrenOAss'), '3'),
            empty(),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopAreeInterventiFontiFinanziamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '10%' });
        cols.push({ header: '', width: '10%' });
        cols.push({ header: '', width: '5%' });

        rows.push([
            empty(),
            checkSingoloInterno(this.getValueFromKey('my_FonFinContiScop')) + ' Banche conti correnti / scoperti',
            checkSingoloInterno(this.getValueFromKey('my_FonFinMutuiOrd')) + ' Mutui Ordinari',
            checkSingoloInterno(this.getValueFromKey('my_FonFinMutuiAgev')) + ' Mutui agevolati',
            checkSingoloInterno(this.getValueFromKey('my_FonFinSoci')) + ' Soci',
            checkSingoloInterno(this.getValueFromKey('my_FonFinAltro')) + ' Altro',
            empty(),
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP CONSORZI GARANZIA FIDI --- SS. COOP CONSORZI GARANZIA FIDI --- SS. COOP CONSORZI GARANZIA FIDI --- SS. COOP CONSORZI GARANZIA FIDI ---
    // ***************************************************************************************************************************************************************************

    private tabellaCoopConsorzioFidi(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });
        cols.push({ header: '', width: '20%' });

        rows.push([
            '- Competenza operativa territoriale:',
            'provinciale ' + checkSingoloInterno(this.getValueFromKey('my_CkFidiCompetenzaPr')),
            'regionale ' + checkSingoloInterno(this.getValueFromKey('my_CkFidiCompetenzaRg')),
            'nazionale ' + checkSingoloInterno(this.getValueFromKey('my_CkFidiCompetenzaNz')),
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. COOP PROPOSTA SCIOGLIMENTO --- SS. COOP PROPOSTA SCIOGLIMENTO --- SS. COOP PROPOSTA SCIOGLIMENTO --- SS. COOP PROPOSTA SCIOGLIMENTO --- SS. COOP PROPOSTA SCIOGLIMENTO
    // ***************************************************************************************************************************************************************************

    private tabellaCoopPropostaScioglimento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_ckNonPersegScMutua')) + ' Non perseguimento dello scopo mutualistico',
            checkSingoloInterno(this.getValueFromKey('my_ckImpossRaggScopo')) + '  Impossibilità di raggiungere lo scopo per cui è stata costituita',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_ckNoDepBil2Anni')) + ' Mancato deposito del bilancio per due anni consecutivi',
            checkSingoloInterno(this.getValueFromKey('my_ckNoAttiGest2Anni')) + '  Assenza di atti di gestione per due anni consecutivi',
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopPropostaScioglimentoAccertamento(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_ckNonPersegScMutuaII')) + ' Non perseguimento dello scopo mutualistico',
            checkSingoloInterno(this.getValueFromKey('my_ckImpossRaggScopoII')) + '  Impossibilità di raggiungere lo scopo per cui è stata costituita',
        ]);
        rows.push([
            checkSingoloInterno(this.getValueFromKey('my_ckNoDepBil2AnniII')) + ' Mancato deposito del bilancio per due anni consecutivi',
            checkSingoloInterno(this.getValueFromKey('my_ckNoAttiGest2AnniII')) + '  Assenza di atti di gestione per due anni consecutivi',
        ]);

        return fxTabella(cols, rows);
    }

    // ***************************************************************************************************************************************************************************
    // SS. LIQUIDAZIONE COATTA AMMINISTRATIVA --- SS. LIQUIDAZIONE COATTA AMMINISTRATIVA --- SS. LIQUIDAZIONE COATTA AMMINISTRATIVA --- SS. LIQUIDAZIONE COATTA AMMINISTRATIVA ---
    // ***************************************************************************************************************************************************************************

    private tabellaCoopLiquidazioneCoattaInterna(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '40%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '15%' });

        rows.push([
            '2 – Risultano avviati od in essere: ',
            checkSingoloInterno(this.getValueFromKey('my_ckAvvIstanzaFall')) + '  Istanze di fallimento?',
            checkSingoloInterno(this.getValueFromKey('my_ckAvvDecrIngiunt')) + '  Decreti Ingiuntivi?',
            checkSingoloInterno(this.getValueFromKey('my_ckAvvAzEsec')) + ' Azioni esecutive?',
            checkSingoloInterno(this.getValueFromKey('my_ckAvvProtesti')) + '  Protesti?',
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopLiquidazioneCoattaBilancioPatrimoniale(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '15%' });
        cols.push({ header: '', width: '35%' });
        cols.push({ header: '', width: '15%' });

        rows.push([
            format('Bilancio d’esercizio al ' + this.getValueFromKeyWithWhitespace('my_AnnoSecondoEs'), true, 'center', fontSize, italics),
            empty(),
            format('Situazione Patrimoniale al ' + this.getValueFromKeyWithWhitespace('my_dtaSitPatrimAggiorn'), true, 'center', fontSize, italics),
            empty(),
        ]);
        rows.push([
            'TOTALE CREDITI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_StPaAtCIItot2'),
            'TOTALE CREDITI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_sitPatrAggTotCrediti'),
        ]);
        rows.push([
            'IMMOBILIZZAZIONI MATERIALI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_StPaAtBIItot2'),
            'IMMOBILIZZAZIONI MATERIALI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_sitPatrAggImmMat'),
        ]);
        rows.push(['RIMANENZE', '€ ' + this.getValueFromKeyWithWhitespace('my_StPaAtCItot2'), 'RIMANENZE', '€ ' + this.getValueFromKeyWithWhitespace('my_sitPatrAggRiman')]);
        rows.push([
            'PATRIMONIO NETTO',
            '€ ' + this.getValueFromKeyWithWhitespace('my_StPaPsAtot2'),
            'PATRIMONIO NETTO',
            '€ ' + this.getValueFromKeyWithWhitespace('my_sitPatrAggPatrNetto'),
        ]);
        rows.push([
            'TOTALE DEBITI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_StPaPsDtot2'),
            'TOTALE DEBITI',
            '€ ' + this.getValueFromKeyWithWhitespace('my_sitPatrAggTotDeb'),
        ]);

        return fxTabella(cols, rows, true);
    }

    // ***************************************************************************************************************************************************************************
    // SS. INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIO. --- SS. INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIO. --- SS. INDICATORI BILANCIO SITUAZIONE PATRIMONIALE AGGIO.
    // ***************************************************************************************************************************************************************************

    private tabellaCoopBilancioSituazionePatrimoniale(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '55%' });
        cols.push({ header: '', width: '45%' });

        rows.push([empty(), format('al ' + this.getValueFromKeyWithWhitespace('my_dtaSitPatrimAggiorn'), bold, 'right', fontSize, italics)]);
        rows.push([this.tabellaCoopBilancioSituazionePatrimonialeAAASubSX(), this.tabellaCoopBilancioSituazionePatrimonialeAAASubDX()]);
        rows.push([this.tabellaCoopBilancioSituazionePatrimonialeBBBSubSX(), this.tabellaCoopBilancioSituazionePatrimonialeBBBSubDX()]);
        rows.push([this.tabellaCoopBilancioSituazionePatrimonialeCCCSubSX(), this.tabellaCoopBilancioSituazionePatrimonialeCCCSubDX()]);
        rows.push([this.tabellaCoopBilancioSituazionePatrimonialeDDDSubSX(), this.tabellaCoopBilancioSituazionePatrimonialeDDDSubDX()]);

        return fxTabella(cols, rows, true);
    }

    private tabellaCoopBilancioSituazionePatrimonialeAAASubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('a) - ', bold1, italics1, fontSize1, ' Attivo corrente netto¹ ', true, italics2, fontSize2, alignment)]);
        rows.push(["(Attivo circolante – Debiti entro l'eserc. Succ.):"]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeAAASubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_sitPatrAggAttCirc'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_sitPatrAggDebEntrEsSucc'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeBBBSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('b) - ', bold1, italics1, fontSize1, ' Margine di Tesoreria¹ ', true, italics2, fontSize2, alignment)]);
        rows.push(["[(Attivo circolante – Rimanenze) - Debiti entro l'eserc. Succ.)]"]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeBBBSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '30%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' - ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            ' = ',
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeCCCSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('c) - ', bold1, italics1, fontSize1, ' Quoziente primario di struttura ', true, italics2, fontSize2, alignment)]);
        rows.push([format('Patrimonio netto (A del Passivo dello SP)', bold, alignment, fontSize, italics)]);
        rows.push([format('___________________________', bold, alignment, fontSize, italics)]);
        rows.push([format("Immobilizzazioni (B dell'Attivo dello SP)", bold, alignment, fontSize, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeCCCSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeDDDSubSX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        rows.push([doubleFormat('d) - ', bold1, italics1, fontSize1, ' Indice di autonomia finanziaria ', true, italics2, fontSize2, alignment)]);
        rows.push([format('Patrimonio netto (Totale A del Passivo dello SP)', bold, alignment, fontSize, italics)]);
        rows.push([format('___________________________', bold, alignment, fontSize, italics)]);
        rows.push([format('Totale Passivo dello SP', bold, alignment, fontSize, italics)]);

        return fxTabella(cols, rows);
    }

    private tabellaCoopBilancioSituazionePatrimonialeDDDSubDX(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '47.5%' });
        cols.push({ header: '', width: '5%' });
        cols.push({ header: '', width: '47.5%' });

        rows.push([
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
            format(' = ', bold, alignment, fontSize, italics),
            format(this.getValueFromKeyWithWhitespace('my_IdAssegnazione'), bold, alignment, fontSize, italics),
        ]);

        return fxTabella(cols, rows);
    }

    private tabellaIrrSanabili(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '100%' });

        const array = this.getArrayFromKey('my_ElIrregSanabili');
        for (let c = 0; c < array.length; c++) {
            rows.push([this.getValueFromKeyWithWhitespace('my_DescIrregSanabili', array[c])]);
        }

        return fxTabella(cols, rows);
    }

    private tabellaRisultanzeAccertamentiEff(): Content {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '50%' });
        cols.push({ header: '', width: '50%' });

        rows.push([format('Irregolarità sanabile', bold, 'center', fontSize, italics), format('Risultanze', bold, 'center', fontSize, italics)]);

        const array = this.getArrayFromKey('my_ElRisultanzeAccert');
        for (let c = 0; c < array.length; c++) {
            rows.push([this.getValueFromKeyWithWhitespace('my_DescRisultAccert', array[c]), this.getValueFromKeyWithWhitespace('my_CommRisultAccert', array[c])]);
        }

        return fxTabella(cols, rows, true);
    }

    /**
     * INTESTAZIONE PER SCHEDE DI SETTORE
     * @param {any} ragioneSociale
     * @param {any} posAlbo
     */
    private intestazioneSchedaSettore(ragioneSociale, posAlbo) {
        let cols = [];
        let rows = [];

        cols.push({ header: '', width: '37%' });
        cols.push({ header: '', width: '23%' });
        cols.push({ header: '', width: '40%' });

        rows.push([intestazioneLogoConf(), empty(), empty()]);
        rows.push([empty(), empty(), empty()]);

        return fxTabella(cols, rows);
    }
}
